import React, { useState, useEffect } from "react";
import { Menu, Layout, Skeleton } from "antd";
import { NavLink, useLocation } from "react-router-dom";
import CustomScrollbars from "util/CustomScrollbars";
import SidebarLogo from "./SidebarLogo";
import { THEME_TYPE_LITE } from "../../constants/ThemeSetting";
import IntlMessages from "../../util/IntlMessages";
import { dismissOpenOverlays } from "../../util/dismissOpenOverlays";
import { useSelector } from "react-redux";
import SubMenu from "antd/lib/menu/SubMenu";
import { FaAward, FaUserTie } from "react-icons/fa6";
import { RiAdminFill, RiMedalFill } from "react-icons/ri";

import {
  FaHouseUser,
  FaUserCheck,
  FaUserCog,
  FaRegFileArchive,
  FaFileAlt,
  FaFileExport,
  FaFileInvoice,
  FaUserFriends,
  FaChevronDown,
  FaRegSun,
  FaDraftingCompass,
  FaPaperPlane,
  FaBalanceScale,
  FaUserClock,
  FaTrophy,
  FaUserShield,
  FaUsersCog,
  FaBuilding,
  FaIdBadge,
  FaClock,
  FaSitemap,
  FaClipboardCheck,
  FaChartLine,
  FaClipboardList,
  FaSignInAlt,
  FaUserTimes,
} from "react-icons/fa";
import {
  BsFillFileEarmarkPersonFill,
  BsFillTicketDetailedFill,
  BsPersonWorkspace,
  BsFillFileEarmarkMedicalFill,
  BsClipboard2CheckFill,
  BsFillBagCheckFill,
  BsFillAwardFill,
} from "react-icons/bs";
import { HiDocumentReport } from "react-icons/hi";
import { GiOnTarget } from "react-icons/gi";
import { ImTicket } from "react-icons/im";
import { AiOutlineDashboard } from "react-icons/ai";
import { MdOutlinePendingActions } from "react-icons/md";

import InOuticon from "./sidebarIcon/inOutIcon";
import LeaveIcon from "./sidebarIcon/LeaveIcon";
import AttendanceIcon from "./sidebarIcon/attendanceIcon";
import EmployeesmasterIcon from "./sidebarIcon/employeesmasterIcon";
import ExpenseIcon from "./sidebarIcon/ExpenseIcon";
import AssetsIcon from "./sidebarIcon/AssetsIcon";
import SeparationIcon from "./sidebarIcon/separationIcon";
import LeaveManagement from "./sidebarIcon/LeaveManagement";
import AuditmasterIcon from "./sidebarIcon/AuditMaster";
import NewemployIcon from "./sidebarIcon/NewemployIcon";
import JobopeningIcon from "./sidebarIcon/JobopeningIcon";
import RankingIcon from "./sidebarIcon/RankingIcon";
import OnboardingIcon from "./sidebarIcon/OnboardingIcon";
import AchivementIcon from "./sidebarIcon/AchivementIcon";
import TeamInfoIcon from "./sidebarIcon/TeamInfoIcon";
import AuditIcon from "./sidebarIcon/AuditIcon";
import PermissionmanagerIcon from "./sidebarIcon/PermissionmanagerIcon";
import DepartmentIcon from "./sidebarIcon/DepartmentIcon";
import InventoryIcon from "./sidebarIcon/inventoryIcon";
import ReportsIcon from "./sidebarIcon/ReportsIcon";
import AnalyticsIcon from "./sidebarIcon/AnalyticsIcon";

import { getRoles } from "../../util/getRoles";
import { isHiddenForSubAdmin } from "../../util/subAdminAccess";
import config from "../../permission/config.json";
import { getResource, getResource as getResourceRaw } from "../../util/getResourcePermission";
import { AuditOutlined, FileAddOutlined, FileTextOutlined, FundOutlined, SettingOutlined, TeamOutlined, HistoryOutlined } from "@ant-design/icons";
import { BiChat } from "react-icons/bi";
const { Sider } = Layout;

const SidebarContent = ({
  sidebarCollapsed,
  setSidebarCollapsed,
  ...props
}) => {

  let { themeType } = useSelector(({ settings }) => settings);
  let { pathname } = useSelector(({ common }) => common);
  let location = useLocation();
  let { authUser } = useSelector(({ auth }) => auth);

  const companySlug = localStorage.getItem("companyDomain")
  const selectedKeys = pathname.substr(1);
  const [openKeys, setOpenKeys] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);

  let submenuTimeout = null;

  // Sidebar Skeleton Loader
  const SidebarMenuSkeleton = () => (
    <div className="gx-sidebar-content sidebar-menu">
      <CustomScrollbars className="gx-layout-sider-scrollbar">
        <div style={{ padding: '8px' }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '12px 8px', marginBottom: '4px' }}>
              <Skeleton.Avatar active size={20} shape="square" style={{ marginRight: '12px' }} />
              <Skeleton.Input active size="small" style={{ flex: 1, height: 16 }} />
            </div>
          ))}
        </div>
      </CustomScrollbars>
    </div>
  );

  const handleSubmenuEnter = (key) => {
    if (submenuTimeout) clearTimeout(submenuTimeout);
    // Close any open Select/DatePicker/Dropdown dropdown elsewhere on the page
    // first - otherwise this flyout submenu and that dropdown render on top of
    // each other (both use the same @zindex-dropdown), a confusing overlap.
    dismissOpenOverlays();
    setOpenKeys([key]);
  };

  const handleSubmenuLeave = () => {
    submenuTimeout = setTimeout(() => {
      setOpenKeys([]);
    }, 300);
  };

  // Helper function to check if pathname matches submenu path (avoiding overlap between /resignation and /resignation-employee-list)
  const matchesSubmenuPath = (pathname, subPath) => {
    if (!pathname || !subPath) return false;
    const lowerPathname = pathname.toLowerCase();
    const lowerSubPath = subPath.toLowerCase();

    // Specific matches should return true immediately
    if (lowerPathname === lowerSubPath) return true;

    // Special handling for resignation paths to avoid overlap
    // If checking for /resignation-employee-list, match it specifically
    if (lowerSubPath.includes('/resignation-employee-list')) {
      return lowerPathname.includes('/resignation-employee-list');
    }
    // If checking for /resignation, ensure it doesn't match /resignation-employee-list
    if (lowerSubPath.includes('/resignation') && !lowerSubPath.includes('/resignation-employee-list')) {
      return lowerPathname.includes('/resignation') && !lowerPathname.includes('/resignation-employee-list');
    }

    // Special handling for announcement paths to avoid overlap between
    // /engage/announcements, /announcement and /announcement-category.
    // If checking for the Engage module path, match it specifically
    if (lowerSubPath.includes('/engage/announcements')) {
      return lowerPathname.includes('/engage/announcements');
    }
    // If checking for /announcement-category, match it specifically
    if (lowerSubPath.includes('/announcement-category')) {
      return lowerPathname.includes('/announcement-category');
    }
    // If checking for /announcement, ensure it doesn't match /announcement-category or /engage/announcements
    if (lowerSubPath.includes('/announcement') && !lowerSubPath.includes('/announcement-category')) {
      return lowerPathname.includes('/announcement') &&
        !lowerPathname.includes('/announcement-category') &&
        !lowerPathname.includes('/engage/announcements');
    }

    // /skills is a substring of its own sibling paths (/skills-matrix,
    // /skill-inventory, etc.) - without this, the generic `includes` check
    // below would highlight "Skill Master" as active on every Skills page.
    if (lowerSubPath.endsWith('/skills')) {
      return lowerPathname.endsWith('/skills');
    }

    // Handle performance module overlaps (dashboard, goals, etc.)
    const isPerformancePathname = lowerPathname.includes('/performance/') || lowerPathname.endsWith('/performance');
    const isPerformanceSubPath = lowerSubPath.includes('/performance/') || lowerSubPath.endsWith('/performance');

    if (isPerformancePathname !== isPerformanceSubPath) {
      return false;
    }

    return lowerPathname.includes(lowerSubPath);
  };

  const ALWAYS_ALLOWED_PATH_SUFFIXES = [
    "/my-profile",
    // Face Identity Vault is always available where it is listed (currently the HR
    // "More" menu only) and is also linked from the Settings dropdown. It has no RBAC
    // resource and no plan-catalogue module, so without this it resolves to no module
    // slug and hasSidebarItemAccess hides it. newRoutes.js lists it the same way.
    "/frs-management",
    // Expense Management is now plan-gated (mapped to expense_* slugs in
    // pathToSidebarModuleSlug), so it is NOT listed here.
  ];
  const isAlwaysAllowedPath = (path = "") =>
    ALWAYS_ALLOWED_PATH_SUFFIXES.some((suffix) => String(path).endsWith(suffix));
  const sidebarModuleAccess = (() => {
    try {
      const raw = JSON.parse(localStorage.getItem("sidebarModuleAccess") || "[]");
      return new Set(Array.isArray(raw) ? raw : []);
    } catch (error) {
      return new Set();
    }
  })();
  const normalizeResourceKey = (value = "") =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  // ── Skills module: SuperAdmin plan gate temporarily disabled ────────────────
  // The Skills modules exist in the SuperAdmin plan catalogue but are not yet
  // enabled on any plan, so gating on them would hide the whole module. Until
  // that integration is done, Skills visibility is decided by RBAC alone.
  // To re-enable the plan gate: delete these two sets and the three call sites
  // that reference them (getResource, hasPlanModuleAccess below).
  // "Career Path" is its own resource (spec §2 makes it its own module) and is
  // likewise absent from the plan catalogue, so it takes the same exemption.
  const PLAN_EXEMPT_RESOURCE_KEYS = new Set(["skills", "career_path"]);
  // Role Skill Requirements and Skill Gap Analysis are sibling screens of the
  // same Skills module - mirror the /skills exemption above for them too.
  const PLAN_EXEMPT_PATH_SUFFIXES = ["/skills", "/skills-matrix", "/skill-inventory", "/role-skill-requirements", "/skill-gap-analysis",
    "/career-paths", "/employee-career-paths", "/course-skill-mapping",
    "/employee-career-view", "/career-readiness", "/competencies", "/employee-competencies",
    "/talent-development"];
  const isPlanExemptPath = (path = "") =>
    PLAN_EXEMPT_PATH_SUFFIXES.some((suffix) => String(path || "").endsWith(suffix));

  const RESOURCE_TO_SIDEBAR_SLUGS = {
    employee_in_out: ["my_in_out", "employee_in_out"],
    leave: ["leave_application", "user_leave_management", "admin_leave_management"],
    attendance_regularization: ["attendance_regularization", "admin_regularization", "manage_team_regularization"],
    separation: ["resignation"],
    separation_management: ["resignation_employee_list", "last_working_day_list"],
    ticket: ["tickets"],
    employee_ticket: ["employee_tickets"],
    my_assets: ["user_assets"],
    attendance_report: ["user_reports", "admin_reports"],
    employee_report: ["admin_reports"],
    ranking: ["individual_rank"],
    // "My Goals" → normalized "my_goals"   (was wrong: "my_goal" - never matched)
    my_goals: ["goals", "my_goals"],
    // "Team Goals" → normalized "team_goals" (was wrong: "team_goal" - never matched)
    team_goals: ["goals", "team_goals"],
    // "Bill Reimbursement" → normalized "bill_reimbursement" (was: "billreimbursement")
    bill_reimbursement: ["bill_reimbursement"],
    // "Bill Reimbursement Approval" → normalized "bill_reimbursement_approval" (was: "billreimbursement_approval" - BROKEN)
    bill_reimbursement_approval: ["admin_bill_reimbursement"],
    employee_master: ["employee_master"],
    admin_leave_management: ["admin_leave_management"],
    leave_management: ["user_leave_management"],
    pending_task: ["pending_task"],
    admin_regularization: ["admin_regularization"],
    team_regularization: ["manage_team_regularization"],
    last_working_day_list: ["last_working_day_list"],
    // "Onboarding Initiation" → normalized "onboarding_initiation" (was: "on_boarding_initiation" - lost multi-slug)
    onboarding_initiation: ["doc_master", "onboarding_master", "onboarding_initiation"],
    candidate_approval: ["candidate_approval", "candidate_list"],
    // "Onboarding Documents Approval List" → normalized "onboarding_documents_approval_list" (was: "on_boarding_doc_approval_list" - BROKEN)
    onboarding_documents_approval_list: ["doc_approve_list"],
    new_joinees: ["new_joinees_list"],
    job_opening: ["job_opening"],
    inventory: ["inventory"],
    employee_details: ["employee_details"],
    holidays: ["mood_analytics"],
    survey: ["360_survey", "questionnaire", "360_reviews"],
    // "360 Degree Questionnaire" → normalized "360_degree_questionnaire"
    "360_degree_questionnaire": ["questionnaire"],
    // "360 Degree Review" → normalized "360_degree_review"
    "360_degree_review": ["360_reviews"],
    // LMS resources → specific catalog slugs.
    // Sidebar items that share the same RBAC resource are grouped together:
    // LMS_COURSE_MANAGEMENT  → lms_courses + lms_cert_template
    // LMS_ASSESSMENT_MANAGEMENT → lms_assessments + lms_grading
    // LMS_REPORTS → lms_reports + lms_data_retention
    lms_course_management: ["lms_courses", "lms_cert_template"],
    lms_assessment_management: ["lms_assessments", "lms_grading"],
    lms_learning_path: ["lms_learning_paths"],
    lms_enrollment: ["lms_enrollments"],
    lms_auto_assignment: ["lms_auto_assignment"],
    lms_my_learning: ["lms_my_learning"],
    lms_team_learning: ["lms_team_learning"],
    lms_reports: ["lms_reports", "lms_data_retention"],
    lms_certificates: ["lms_certificates"],
    // Performance module resources → specific catalog slugs (one-to-one)
    performance_dashboard: ["performance_dashboard"],
    performance_goals: ["performance_goals"],
    performance_kpi_kra: ["performance_kpi_kra"],
    performance_appraisal: ["performance_appraisal"],
    performance_reports: ["performance_reports"],
    performance_cycles: ["performance_cycles"],
    performance_templates: ["performance_templates"],
    performance_development_plan: ["performance_development_plan"],
    performance_settings: ["performance_settings"],
    expense_dashboard: ["expense_dashboard"],
    expense_advance: ["expense_advance"],
    expense_submission: ["expense_submission"],
    expense_approval: ["expense_approval"],
    expense_reports: ["expense_reports"],
    expense_configuration: ["expense_configuration"],
    // Skills module → single catalog slug; categories and proficiency levels
    // are managed inside the Skill Master screen, not as separate modules.
    // Skills and Career Growth Path each have one RBAC resource covering every
    // screen in the module, so each maps to a single plan slug.
    skills: ["skill_master"],
    career_path: ["career_path"],
  };
  const getResource = (name, types = []) => {
    const key = normalizeResourceKey(name);
    // Step 1: SuperAdmin plan check - only applied when a plan is configured,
    // and skipped for resources in PLAN_EXEMPT_RESOURCE_KEYS (see comment above).
    if (sidebarModuleAccess.size > 0 && !PLAN_EXEMPT_RESOURCE_KEYS.has(key)) {
      const mappedKeys = RESOURCE_TO_SIDEBAR_SLUGS[key] || [key];
      const hasPlanAccess = mappedKeys.some((mappedKey) => sidebarModuleAccess.has(mappedKey));
      if (!hasPlanAccess) return false;
    }
    // Step 2: Internal RBAC check - user's role must have the requested permission.
    return getResourceRaw(name, types);
  };

  const performanceMenu = {
    icon: <FaChartLine />,
    className: "main-submenu",
    key: "sub_performance",
    title: "Performance Review",
    submenu: true,
    subMenuInfo: [
      ...(getResource(config.RESOURCES.PERFORMANCE_DASHBOARD, ["read"]) ? [{
        key: "performance-dashboard",
        path: `/${companySlug}/performance/dashboard`,
        title: "sidebar.performance.dashboard",
        permission: [config.RESOURCES.PERFORMANCE_DASHBOARD],
      }] : []),
      ...(getResource(config.RESOURCES.PERFORMANCE_GOALS, ["read"]) ? [{
        key: "performance-goals",
        path: `/${companySlug}/performance/goals`,
        title: "sidebar.performance.goals",
        permission: [config.RESOURCES.PERFORMANCE_GOALS],
      }] : []),
      ...(getResource(config.RESOURCES.PERFORMANCE_KPI_KRA, ["read"]) ? [{
        key: "performance-kpi-kra",
        path: `/${companySlug}/performance/kpi-kra`,
        title: "sidebar.performance.kpi_kra",
        permission: [config.RESOURCES.PERFORMANCE_KPI_KRA],
      }] : []),
      ...(getResource(config.RESOURCES.PERFORMANCE_APPRAISAL, ["read"]) ? [{
        key: "performance-appraisals",
        path: `/${companySlug}/performance/appraisal`,
        title: "sidebar.performance.appraisals",
        permission: [config.RESOURCES.PERFORMANCE_APPRAISAL],
      }] : []),
      ...(getResource(config.RESOURCES.PERFORMANCE_REPORTS, ["read"]) ? [{
        key: "performance-reports",
        path: `/${companySlug}/performance/reports`,
        title: "sidebar.performance.reports",
        permission: [config.RESOURCES.PERFORMANCE_REPORTS],
      }] : []),
      ...(getResource(config.RESOURCES.PERFORMANCE_CYCLES, ["read"]) ? [{
        key: "performance-cycles",
        path: `/${companySlug}/performance/cycles`,
        title: "sidebar.performance.cycles",
        permission: [config.RESOURCES.PERFORMANCE_CYCLES],
      }] : []),
      ...(getResource(config.RESOURCES.PERFORMANCE_TEMPLATES, ["read"]) ? [{
        key: "performance-templates",
        path: `/${companySlug}/performance/templates`,
        title: "sidebar.performance.templates",
        permission: [config.RESOURCES.PERFORMANCE_TEMPLATES],
      }] : []),
    ].filter((item) => !isHiddenForSubAdmin(item.path)), // settings never shown to Sub-Admin
  };

  // Enhanced Expense Management submenu - shared across every role menu, gated per
  // item by RBAC via getResource (same pattern as performanceMenu).
  const expenseMenu = {
    icon: <ExpenseIcon />,
    className: "main-submenu",
    key: "sub_expense",
    title: "Expense Management",
    submenu: true,
    subMenuInfo: [
      ...(getResourceRaw(config.RESOURCES.EXPENSE_DASHBOARD, ["read"]) ? [{
        key: "expense-dashboard",
        path: `/${companySlug}/expense/dashboard`,
        title: "Expense Dashboard",
        submenu: false,
        permission: [config.RESOURCES.EXPENSE_DASHBOARD],
      }] : []),
      ...(getResourceRaw(config.RESOURCES.EXPENSE_ADVANCE, ["read"]) ? [{
        key: "expense-advances",
        path: `/${companySlug}/expense/advances`,
        title: "Advance Requests",
        submenu: false,
        permission: [config.RESOURCES.EXPENSE_ADVANCE],
      }] : []),
      ...(getResourceRaw(config.RESOURCES.EXPENSE_SUBMISSION, ["read"]) ? [{
        key: "expense-submit",
        path: `/${companySlug}/expense/submit`,
        title: "Submit Expense",
        submenu: false,
        permission: [config.RESOURCES.EXPENSE_SUBMISSION],
      }] : []),
      ...(getResourceRaw(config.RESOURCES.EXPENSE_APPROVAL, ["read"]) ? [{
        key: "expense-approvals",
        path: `/${companySlug}/expense/approvals`,
        title: "Approval Queue",
        submenu: false,
        permission: [config.RESOURCES.EXPENSE_APPROVAL],
      }] : []),
      ...(getResourceRaw(config.RESOURCES.EXPENSE_REPORTS, ["read"]) ? [{
        key: "expense-reports",
        path: `/${companySlug}/expense/reports`,
        title: "Expense Reports",
        submenu: false,
        permission: [config.RESOURCES.EXPENSE_REPORTS],
      }] : []),
      ...(getResourceRaw(config.RESOURCES.EXPENSE_CONFIG, ["read"]) ? [{
        key: "expense-config",
        path: `/${companySlug}/expense/config`,
        title: "Expense Configuration",
        submenu: false,
        permission: [config.RESOURCES.EXPENSE_CONFIG],
      }] : []),
    ],
  };

  const pathToSidebarModuleSlug = (path = "") => {
    const normalizedPath = String(path || "").toLowerCase();
    // Performance paths are matched FIRST. They are nested (/performance/goals,
    // /performance/dashboard), so the generic endsWith checks below would claim
    // them: "/performance/goals" ends with "/goals" and "/performance/dashboard"
    // ends with "/dashboard". That mis-slugged Performance > Goals as "goals",
    // whose plan lookup then failed and hid the item from the sidebar even though
    // the plan enabled it - while the header, which resolves slugs from the
    // resource name instead of the path, kept showing it.
    if (normalizedPath.includes("/performance/dashboard")) return "performance_dashboard";
    if (normalizedPath.includes("/performance/goals")) return "performance_goals";
    if (normalizedPath.includes("/performance/kpi-kra")) return "performance_kpi_kra";
    if (normalizedPath.includes("/performance/appraisal")) return "performance_appraisal";
    if (normalizedPath.includes("/performance/reports")) return "performance_reports";
    if (normalizedPath.includes("/performance/cycles")) return "performance_cycles";
    if (normalizedPath.includes("/performance/templates")) return "performance_templates";
    if (normalizedPath.includes("/performance/development-plan")) return "performance_development_plan";
    // Expense Management paths → plan-catalog slugs. Matched before the generic
    // "/dashboard" check below, which "/expense/dashboard" would otherwise claim.
    if (normalizedPath.includes("/expense/dashboard")) return "expense_dashboard";
    if (normalizedPath.includes("/expense/advances")) return "expense_advance";
    if (normalizedPath.includes("/expense/submit")) return "expense_submission";
    if (normalizedPath.includes("/expense/approvals")) return "expense_approval";
    if (normalizedPath.includes("/expense/reports")) return "expense_reports";
    if (normalizedPath.includes("/expense/config")) return "expense_configuration";
    if (normalizedPath.endsWith("/dashboard")) return "dashboard";
    if (normalizedPath.endsWith("/my-in-out")) return "my_in_out";
    if (normalizedPath.endsWith("/leave-application")) return "leave_application";
    if (normalizedPath.endsWith("/employee-attendence-regularization")) return "attendance_regularization";
    if (normalizedPath.endsWith("/resignation")) return "resignation";
    if (normalizedPath.endsWith("/tickets")) return "tickets";
    if (normalizedPath.endsWith("/user-assets")) return "user_assets";
    if (normalizedPath.endsWith("/user-reports")) return "user_reports";
    if (normalizedPath.endsWith("/individual-rank")) return "individual_rank";
    if (normalizedPath.endsWith("/my-goals") || normalizedPath.endsWith("/goals")) return "goals";
    if (normalizedPath.endsWith("/bill-reimbursement")) return "bill_reimbursement";
    if (normalizedPath.endsWith("/employee-master")) return "employee_master";
    if (normalizedPath.endsWith("/employee-in-out")) return "employee_in_out";
    if (normalizedPath.endsWith("/resignation-employee-list")) return "resignation_employee_list";
    if (normalizedPath.endsWith("/last-working-day-emps")) return "last_working_day_list";
    if (normalizedPath.endsWith("/admin-reports")) return "admin_reports";
    if (normalizedPath.endsWith("/employee-tickets")) return "employee_tickets";
    if (normalizedPath.endsWith("/admin-leave-approval")) return "admin_leave_management";
    if (normalizedPath.endsWith("/user-leave-approve")) return "user_leave_management";
    // Checked before /penalty-revoke - endsWith would match the user path too.
    if (normalizedPath.endsWith("/user-penalty-revoke")) return "user_leave_management";
    if (normalizedPath.endsWith("/penalty-revoke")) return "admin_leave_management";
    if (normalizedPath.endsWith("/pending-task-list")) return "pending_task";
    if (normalizedPath.endsWith("/admin-regularizatrion")) return "admin_regularization";
    if (normalizedPath.endsWith("/manage-team-regularization")) return "manage_team_regularization";
    if (normalizedPath.endsWith("/company-doc-master")) return "doc_master";
    if (normalizedPath.endsWith("/onboarding-master")) return "onboarding_master";
    if (normalizedPath.endsWith("/candidate-approval-master")) return "candidate_approval_master";
    if (normalizedPath.endsWith("/onboarding-initiation-master")) return "onboarding_initiation_master";
    if (normalizedPath.endsWith("/employee-master-form-builder")) return "employee_master_form_builder";
    if (normalizedPath.endsWith("/rejoin-employee-form-builder")) return "rejoin_employee_form_builder";
    if (normalizedPath.endsWith("/candidate-approval")) return "candidate_approval";
    if (normalizedPath.endsWith("/candidate-list")) return "candidate_list";
    if (normalizedPath.endsWith("/onboarding-initiation")) return "onboarding_initiation";
    if (normalizedPath.endsWith("/doc-approve-list")) return "doc_approve_list";
    if (normalizedPath.endsWith("/new-joinees-list")) return "new_joinees_list";
    if (normalizedPath.endsWith("/mood-list")) return "mood_analytics";
    if (normalizedPath.includes("/jobs/")) return "job_opening";
    if (normalizedPath.endsWith("/team-goals")) return "goals";
    if (normalizedPath.endsWith("/inventory")) return "inventory";
    if (normalizedPath.endsWith("/viewemployeedetails")) return "employee_details";
    if (normalizedPath.endsWith("/organization-policy")) return "organization_policy";
    if (normalizedPath.includes("/admin-bill-reimbursement")) return "admin_bill_reimbursement";
    if (normalizedPath.includes("/engage/announcements")) return "announcement";
    if (normalizedPath.includes("/engage/celebrations")) return "celebration";
    if (
      normalizedPath.includes("/engage/wall") ||
      normalizedPath.includes("/engage/awards") ||
      normalizedPath.includes("/engage/badges") ||
      normalizedPath.includes("/engage/moderation") ||
      normalizedPath.includes("/engage/settings")
    ) return "wall";
    if (normalizedPath.endsWith("/announcement") || normalizedPath.endsWith("/announcement-category")) return "announcement";
    if (normalizedPath.endsWith("/360-survey") || normalizedPath.includes("/survey-form")) return "360_survey";
    if (normalizedPath.endsWith("/360-questionnaire") || normalizedPath.includes("/add/new-questionnaire")) return "questionnaire";
    if (normalizedPath.endsWith("/360-reviews") || normalizedPath.includes("/add/new-review") || normalizedPath.includes("/360_reports")) return "360_reviews";
    if (normalizedPath.endsWith("/admin/zoho-payroll-integration")) return "zoho_payroll_integration";
    if (normalizedPath.endsWith("/analytics-studio")) return "analytics";
    if (normalizedPath.endsWith("/organization-view")) return "organization_tree";
    if (normalizedPath.endsWith("/mira")) return "mira_ai";
    // LMS paths → specific catalog slugs (check most specific first)
    if (normalizedPath.includes('/lms/settings/data-retention')) return "lms_data_retention";
    if (normalizedPath.includes('/lms/settings/certificate-template')) return "lms_cert_template";
    if (normalizedPath.includes('/lms/courses')) return "lms_courses";
    if (normalizedPath.includes('/lms/assessments')) return "lms_assessments";
    if (normalizedPath.includes('/lms/enrollments')) return "lms_enrollments";
    if (normalizedPath.includes('/lms/my-learning')) return "lms_my_learning";
    if (normalizedPath.includes('/lms/certificates')) return "lms_certificates";
    if (normalizedPath.includes('/lms/learning-paths')) return "lms_learning_paths";
    if (normalizedPath.includes('/lms/auto-assignment')) return "lms_auto_assignment";
    if (normalizedPath.includes('/lms/team-learning')) return "lms_team_learning";
    if (normalizedPath.includes('/lms/reports')) return "lms_reports";
    if (normalizedPath.includes('/lms/grading')) return "lms_grading";
    if (normalizedPath.includes('/lms/')) return "learning"; // fallback for unrecognised LMS paths
    // Specific performance slugs are resolved at the top of this function, before
    // the generic endsWith checks can claim them. Only the fallback remains here.
    // Skills module — every screen rides the single skill_master plan slug.
    if (normalizedPath.endsWith("/skills") || normalizedPath.endsWith("/skills-matrix") || normalizedPath.endsWith("/skill-inventory")
      || normalizedPath.includes("/view-skill-inventory") || normalizedPath.endsWith("/role-skill-requirements")
      || normalizedPath.endsWith("/skill-gap-analysis")) return "skill_master";
    // Career Growth Path module — every screen rides the single career_path slug.
    if (normalizedPath.endsWith("/career-paths") || normalizedPath.includes("/career-path-builder")
      || normalizedPath.endsWith("/employee-career-paths") || normalizedPath.endsWith("/career-role-requirements")
      || normalizedPath.endsWith("/course-skill-mapping") || normalizedPath.endsWith("/employee-career-view")
      || normalizedPath.endsWith("/career-readiness") || normalizedPath.endsWith("/competencies")
      || normalizedPath.endsWith("/employee-competencies")) return "career_path";
    if (normalizedPath.includes('/performance/')) return "performance"; // fallback
    return "";
  };
  const getTopLevelSidebarModuleSlug = (item) => {
    const title = String(item?.title || "").trim().toLowerCase();
    if (title === "me") return "me";
    if (title === "team") return "team";
    if (title === "onboarding") return "onboarding";
    if (title === "more") return "more";
    if (title === "survey") return "survey";
    return pathToSidebarModuleSlug(item?.path);
  };
  const hasEnabledModuleSlug = (slug = "") => {
    if (!slug || sidebarModuleAccess.size === 0) return false;
    const moduleAliases = {
      admin_regularization: ["admin_regularization", "attendance_regularization"],
      manage_team_regularization: ["manage_team_regularization", "attendance_regularization"],
      user_leave_management: ["user_leave_management", "leave_application"],
      goals: ["goals", "my_goals", "team_goals"],
    };
    const candidates = moduleAliases[slug] || [slug];
    return candidates.some((candidate) => sidebarModuleAccess.has(candidate));
  };
  const hasPlanModuleAccess = (item) => {
    // Skills module is not plan-gated yet - see PLAN_EXEMPT_* above.
    if (isPlanExemptPath(item?.path)) return true;
    const slug = getTopLevelSidebarModuleSlug(item);
    if (!slug) return isAlwaysAllowedPath(item?.path);
    if (slug === "dashboard") return true; // Dashboard is always visible regardless of plan
    return hasEnabledModuleSlug(slug);
  };
  const hasSidebarItemAccess = (item) => {
    // Settings/master screens are never shown to Sub-Admin, whatever it is granted.
    if (isHiddenForSubAdmin(item?.path)) return false;
    // Step 1: SuperAdmin plan check - block if plan is configured but excludes this module.
    if (sidebarModuleAccess.size > 0 && !hasPlanModuleAccess(item)) return false;
    // Step 2: Internal RBAC check - user's role must have read access.
    // ANY listed resource grants the item, matching hasReadAccess in routes/newRoutes.js.
    // Previously only permission[0] was consulted, so an entry reachable via more than
    // one resource (Admin Reports, which renders a card per report family the caller may
    // read) was gated on its first resource alone.
    if (item?.permission?.length > 0) {
      return item.permission.some((resourceName) => getResourceRaw(resourceName, ["read"]));
    }
    return isAlwaysAllowedPath(item?.path) || Boolean(pathToSidebarModuleSlug(item?.path));
  };
  // Sub-Admin gets the Admin sidebar, but unlike Admin it only holds the
  // permissions granted to it - so the plan-only submenu headers (Me, Team,
  // Onboarding, More) and Analytics must also need at least one granted item.
  const isSubAdmin = Boolean(getRoles(["Sub-Admin"]));
  const subAdminSeesAny = (items) => !isSubAdmin || items.some((item) => hasSidebarItemAccess(item));
  const hasVisibleSubmenuItems = (item) =>
    Array.isArray(item?.subMenuInfo) && item.subMenuInfo.some((subItem) => hasSidebarItemAccess(subItem));

  // const User = [
  //   {
  //     key: "User Dashboard",
  //     path: `/${companySlug}/dashboard`,
  //     icon: <AiOutlineDashboard />,
  //     title: "sidebar.User_Dashboard",
  //     submenu: false,
  //     permission: [],
  //   },
  //   ...(getResource(config.RESOURCES.LEAVE, ["read"])) ? [
  //     {
  //       key: "manage-leave-application",
  //       path: `/${companySlug}/leave-application`,
  //       icon: <LeaveIcon />,
  //       title: "sidebar.leave-application",
  //       submenu: false,
  //       permission: [config.RESOURCES.LEAVE],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.SEPARATION, ["read"])) ? [
  //     {
  //       key: "resignation",
  //       path: `/${companySlug}/resignation`,
  //       icon: <SeparationIcon />,
  //       title: "sidebar.resignation",
  //       submenu: false,
  //       permission: [config.RESOURCES.SEPARATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])) ? [
  //     {
  //       key: "last-working-day-emps",
  //       path: `/${companySlug}/last-working-day-emps`,
  //       icon: <SeparationIcon />,
  //       title: "Last Working Day List",
  //       submenu: false,
  //       permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
  //     {
  //       key: "Attendance Regularization",
  //       path: `/${companySlug}/employee-attendence-regularization`,
  //       icon: <AttendanceIcon />,
  //       title: "sidebar.Attendence_Regularization",
  //       submenu: false,
  //       permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.TICKET, ["read"])) ? [
  //     {
  //       key: "tickets",
  //       path: `/${companySlug}/tickets`,
  //       icon: <ImTicket />,
  //       title: "sidebar.tickets",
  //       submenu: false,
  //       permission: [config.RESOURCES.TICKET],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])) ? [
  //     {
  //       key: "assets",
  //       path: `/${companySlug}/user-assets`,
  //       icon: <AssetsIcon />,
  //       title: "sidebar.user-assets",
  //       submenu: false,
  //       permission: [config.RESOURCES.MY_ASSETS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [
  //     {
  //       key: "user-reports",
  //       path: `/${companySlug}/user-reports`,
  //       icon: <ReportsIcon />,
  //       title: "sidebar.user-reports",
  //       submenu: false,
  //       permission: [config.RESOURCES.ATTENDANCE_REPORT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"])) ? [
  //     {
  //       key: "admin-reports",
  //       path: `/${companySlug}/admin-reports`,
  //       icon: <ReportsIcon />,
  //       title: "sidebar.admin-reports",
  //       submenu: false,
  //       permission: [config.RESOURCES.EMPLOYEE_REPORT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.RANKING, ["read"])) ? [
  //     {
  //       key: "individual-ranking",
  //       path: `/${companySlug}/individual-rank`,
  //       icon: <RankingIcon />,
  //       title: "sidebar.individual-rank",
  //       submenu: false,
  //       permission: [config.RESOURCES.RANKING],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.MY_GOAL, ["read"])) ? [
  //     {
  //       key: "goals",
  //       path: `/${companySlug}/goals`,
  //       icon: <GiOnTarget />,
  //       title: "sidebar.create-goals",
  //       submenu: false,
  //       permission: [config.RESOURCES.MY_GOAL],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [
  //     {
  //       key: "Job Opening",
  //       path: `/${companySlug}/jobs/openings`,
  //       icon: <JobopeningIcon />,
  //       title: "sidebar.admin-jobopening",
  //       submenu: false,
  //       permission: [config.RESOURCES.JOB_OPENING],
  //     }] : [],
  //   {
  //     key: "organization-policy",
  //     path: `/${companySlug}/organization-policy`,
  //     icon: <BsClipboard2CheckFill />,
  //     title: "sidebar.organization-policies",
  //     submenu: false,
  //     permission: [],
  //   },
  //   ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [
  //     {
  //       key: "inventory",
  //       path: `/${companySlug}/inventory`,
  //       icon: <InventoryIcon />,
  //       title: "sidebar.user-assetstabpane",
  //       submenu: false,
  //       permission: [config.RESOURCES.INVENTORY],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.MANAGE_TICKET, ["read"])) ? [
  //     {
  //       key: "managetickets",
  //       path: `/${companySlug}/manage-tickets`,
  //       icon: <BsFillTicketDetailedFill />,
  //       title: "sidebar.manage-tickets",
  //       submenu: false,
  //       permission: [config.RESOURCES.MANAGE_TICKET],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
  //     {
  //       key: "Audit Master",
  //       path: `/${companySlug}/auditmaster`,
  //       icon: <AuditmasterIcon />,
  //       title: "Audit Master",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDIT_MASTER],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.AUDITS, ["read"])) ? [
  //     {
  //       key: "Audits",
  //       path: `/${companySlug}/my-audit-history`,
  //       icon: <FaFileInvoice />,
  //       title: "Audits",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDITS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [
  //     {
  //       key: "auditperformance",
  //       path: `/${companySlug}/auditperformance`,
  //       icon: <AuditIcon />,
  //       title: "Audit Performance",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDITS_PERFORMANCE],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [
  //     {
  //       key: "auditHead",
  //       path: `/${companySlug}/auditHead`,
  //       icon: <AuditmasterIcon />,
  //       title: "Audit Head",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDIT_HEAD],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [
  //     {
  //       key: "employee-master",
  //       path: `/${companySlug}/employee-master`,
  //       icon: <EmployeesmasterIcon />,
  //       title: "sidebar.employee-master",
  //       submenu: false,
  //       permission: [config.RESOURCES.EMPLOYEE_MASTER],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
  //     {
  //       key: "admin-leave-approval",
  //       path: `/${companySlug}/admin-leave-approval`,
  //       icon: <LeaveManagement />,
  //       title: "sidebar.admin-leave-management",
  //       submenu: false,
  //       permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.PENDING_TASK, ["read"])) ? [
  //     {
  //       key: "pendingtask",
  //       path: `/${companySlug}/pending-task-list`,
  //       icon: <MdOutlinePendingActions />,
  //       title: "sidebar.pending-task",
  //       submenu: false,
  //       permission: [config.RESOURCES.PENDING_TASK],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [
  //     {
  //       key: "admin-regularizatrion",
  //       path: `/${companySlug}/admin-regularizatrion`,
  //       icon: <FaUserCog />,
  //       title: "sidebar.admin-regularizatrion",
  //       submenu: false,
  //       permission: [config.RESOURCES.ADMIN_REGULARIZATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ACHIEVEMENTS, ["read"])) ? [
  //     {
  //       key: "achivements",
  //       path: `/${companySlug}/achivements`,
  //       icon: <AchivementIcon />,
  //       title: "sidebar.achivements",
  //       submenu: false,
  //       permission: [config.RESOURCES.ACHIEVEMENTS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.SETTINGS, ["read"])) ? [
  //     {
  //       key: "permission",
  //       path: `/${companySlug}/permission-manager`,
  //       icon: <PermissionmanagerIcon />,
  //       title: "sidebar.permission",
  //       submenu: false,
  //       permission: [config.RESOURCES.SETTINGS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.DEPARTMENTS, ["read"])) ? [
  //     {
  //       key: "admin-department",
  //       path: `/${companySlug}/admin-department`,
  //       icon: <DepartmentIcon />,
  //       title: "sidebar.Department",
  //       submenu: false,
  //       permission: [config.RESOURCES.DEPARTMENTS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.DESIGNATION, ["read"])) ? [
  //     {
  //       key: "admin-designation",
  //       path: `/${companySlug}/admin-designation`,
  //       icon: <FaUserCog />,
  //       title: "sidebar.designation",
  //       submenu: false,
  //       permission: [config.RESOURCES.DESIGNATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.SHIFT, ["read"])) ? [
  //     {
  //       key: "admin-shift",
  //       path: `/${companySlug}/admin-shift`,
  //       icon: <FaClock />,
  //       title: "sidebar.shift",
  //       submenu: false,
  //       permission: [config.RESOURCES.SHIFT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.BUSINESS_UNIT, ["read"])) ? [
  //     {
  //       key: "admin-businessunit",
  //       path: `/${companySlug}/admin-businessunit`,
  //       icon: <FaSitemap />,
  //       title: "sidebar.businessunit",
  //       submenu: false,
  //       permission: [config.RESOURCES.BUSINESS_UNIT]
  //     }] : [],
  //   ...(getResource(config.RESOURCES.SUBDEPARTMENT, ["read"])) ? [
  //     {
  //       key: "admin-subdepartment",
  //       path: `/${companySlug}/admin-subdepartment`,
  //       icon: <FaSitemap />,
  //       title: "sidebar.subdepartment",
  //       submenu: false,
  //       permission: [config.RESOURCES.SUBDEPARTMENT],
  //     }] : [],
  //      ...(getResource(config.RESOURCES.EMPLOYEMENT_TYPE, ["read"])) ? [
  //       {
  //         key: "admin-employment-type",
  //         path: `/${companySlug}/admin-employment-type`,
  //         icon: <FaSitemap />,
  //         title: "Employment Type",
  //         submenu: false,
  //         permission: [config.RESOURCES.EMPLOYEMENT_TYPE],
  //       }] : [],
  //   ...(getResource(config.RESOURCES.LEAVE_MASTER, ["read"])) ? [
  //     {
  //       key: "leave-master",
  //       path: `/${companySlug}/admin-leave-master`,
  //       icon: <FaUserCog />,
  //       title: "sidebar.admin-leave-master",
  //       submenu: false,
  //       permission: [config.RESOURCES.LEAVE_MASTER],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.MANAGE_LEAVE_BALANCE, ["read"])) ? [
  //     {
  //       key: "employee-leave-balance",
  //       path: `/${companySlug}/employee-leave-balance`,
  //       icon: <FaHouseUser />,
  //       title: "sidebar.employee-leave-balance",
  //       submenu: false,
  //       permission: [config.RESOURCES.MANAGE_LEAVE_BALANCE],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
  //     {
  //       key: "candidate-approvel",
  //       path: `/${companySlug}/candidate-approval`,
  //       icon: <FaUserTie />,
  //       title: "sidebar.candidate-approvel",
  //       submenu: false,
  //       permission: [config.RESOURCES.ON_BOARDING_INITIATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
  //     {
  //       key: "candidate-list",
  //       path: `/${companySlug}/candidate-list`,
  //       icon: <FaUserCheck />,
  //       title: "sidebar.candidate-approvel-list",
  //       submenu: false,
  //       permission: [config.RESOURCES.CANDIDATE_APPROVAL],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
  //     {
  //       key: "initiation",
  //       path: `/${companySlug}/onboarding-initiation`,
  //       icon: <OnboardingIcon />,
  //       title: "sidebar.onboarding-initiation",
  //       submenu: false,
  //       permission: [config.RESOURCES.ON_BOARDING_INITIATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [
  //     {
  //       key: "doc-approve-list",
  //       path: `/${companySlug}/doc-approve-list`,
  //       icon: <FaRegFileArchive />,
  //       title: "sidebar.doc-approve-list",
  //       submenu: false,
  //       permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [
  //     {
  //       key: "new-joinees-list",
  //       path: `/${companySlug}/new-joinees-list`,
  //       icon: <NewemployIcon />,
  //       title: "sidebar.new-joinees-list",
  //       submenu: false,
  //       permission: [config.RESOURCES.NEW_JOINEES],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.HOLIDAYS, ["read"])) ? [
  //     {
  //       key: "holidays",
  //       path: `/${companySlug}/holidays`,
  //       icon: <FaRegFileArchive />,
  //       title: "sidebar.holidays",
  //       submenu: false,
  //       permission: [config.RESOURCES.HOLIDAYS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.POLICIES, ["read"])) ? [
  //     {
  //       key: "policies",
  //       path: `/${companySlug}/policies`,
  //       icon: <BsFillFileEarmarkMedicalFill />,
  //       title: "sidebar.policies",
  //       submenu: false,
  //       permission: [config.RESOURCES.POLICIES],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
  //     {
  //       key: "Audits",
  //       path: `/${companySlug}/audits`,
  //       icon: <AuditIcon />,
  //       title: "Audits",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDIT_MASTER],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [
  //     {
  //       key: "employee-in-out",
  //       path: `/${companySlug}/employee-in-out`,
  //       icon: <InOuticon />,
  //       title: "sidebar.Employee-In-Out",
  //       submenu: false,
  //       permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [
  //     {
  //       key: "resignation-employee-list",
  //       path: `/${companySlug}/resignation-employee-list`,
  //       icon: <SeparationIcon />,
  //       title: "sidebar.resignation-employee-list",
  //       submenu: false,
  //       permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [

  //     {
  //       key: "ManageTeamRegularization",
  //       path: `/${companySlug}/manage-team-regularization`,
  //       icon: <FaUserFriends />,
  //       title: "sidebar.ManageTeamRegularization",
  //       submenu: false,
  //       permission: [config.RESOURCES.TEAM_REGULARIZATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [
  //     {
  //       key: "user-leave-approval",
  //       path: `/${companySlug}/user-leave-approve`,
  //       icon: <LeaveManagement />,
  //       title: "sidebar.user-leave-management",
  //       submenu: false,
  //       permission: [config.RESOURCES.LEAVE_MANAGEMENT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [
  //     {
  //       key: "view-employee-details",
  //       path: `/${companySlug}/viewEmployeeDetails`,
  //       icon: <FaHouseUser />,
  //       title: "sidebar.employee-details",
  //       submenu: false,
  //       permission: [config.RESOURCES.EMPLOYEE_DETAILS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
  //     {
  //       key: "Bill Reimbursement",
  //       path: `/${companySlug}/bill-reimbursement`,
  //       icon: <HiDocumentReport />,
  //       title: "sidebar.bill-reimbursement",
  //       submenu: false,
  //       permission: [config.RESOURCES.BILLREIMBURSEMENT],
  //     }] : [],

  //   ...(getResource(config.RESOURCES.SHIFT, ["read"])) ? [
  //     {
  //       key: "admin-roster",
  //       path: `/${companySlug}/admin-roster`,
  //       icon: <FaClock />,
  //       title: "sidebar.roster",
  //       submenu: false,
  //       permission: [config.RESOURCES.SHIFT],
  //     }
  //   ]
  //     : [],
  // ...(getResource(config.RESOURCES["SURVEY"], ["read"])) ? [
  //   {
  //     key: "admin-roster",
  //     path: `/${companySlug}/360-survey`,
  //     icon: <AuditOutlined />,
  //     title: "sidebar.360_survey",
  //     submenu: false,
  //     permission: [config.RESOURCES["SURVEY"]],
  //   }] : [],

  //   {
  //     key: "organisationtree",
  //     path: `/${companySlug}/organization-view`,
  //     icon: <ImTree />,
  //     title: "sidebar.organisation-tree",
  //     submenu: false,
  //     permission: [],
  //   },

  // ];


  const User = [
    {
      key: "User Dashboard",
      path: `/${companySlug}/dashboard`,
      icon: <AiOutlineDashboard />,
      title: "sidebar.User_Dashboard",
      submenu: false,
      permission: [], // always visible
    },

    // className: " main-submenu",
    // key: "sub15",
    // title: "Me",
    // submenu: true,

    // ✅ "Me" submenu
    ...(getResource(config.RESOURCES.LEAVE, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION, ["read"]) ||
      getResource(config.RESOURCES.TICKET, ["read"]) ||
      getResource(config.RESOURCES.MY_ASSETS, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.RANKING, ["read"]) ||
      getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
      // getResource(config.RESOURCES.AUDITS, ["read"]) ||
      getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])
      ? [
        {
          key: "Me",
          title: "Me",
          className: " main-submenu",
          icon: <i className="fas fa-user " />,
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
              {
                key: "My-In-Out",
                path: `/${companySlug}/my-in-out`,
                title: "sidebar.My-In-Out",
                submenu: false,
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE, ["read"])
              ? [{
                key: "manage-leave-application",
                path: `/${companySlug}/leave-application`,
                // icon: <LeaveIcon />,
                title: "sidebar.leave-application",
                permission: [config.RESOURCES.LEAVE],
              }]
              : []),

            ...(getResource(config.RESOURCES.SHIFT_SWAP_APPLICATION, ["read"])
              ? [{
                key: "shift-change-requests",
                path: `/${companySlug}/shift-change-requests`,
                title: "sidebar.shift-change-requests",
                permission: [config.RESOURCES.SHIFT_SWAP_APPLICATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])
              ? [{
                key: "attendance-regularization",
                path: `/${companySlug}/employee-attendence-regularization`,
                // icon: <AttendanceIcon />,
                title: "sidebar.Attendence_Regularization",
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.SEPARATION, ["read"])
              ? [{
                key: "resignation",
                path: `/${companySlug}/resignation`,
                // icon: <SeparationIcon />,
                title: "sidebar.resignation",
                permission: [config.RESOURCES.SEPARATION],
              }]
              : []),
            // ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
            //   {
            //     key: "Attendance Regularization",
            //     path: `/${companySlug}/employee-attendence-regularization`,
            //     // icon: <AttendanceIcon />,
            //     title: "sidebar.Attendence_Regularization",
            //     submenu: false,
            //     permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
            //   }] : [],

            ...(getResource(config.RESOURCES.TICKET, ["read"])
              ? [{
                key: "tickets",
                path: `/${companySlug}/tickets`,
                // icon: <ImTicket />,
                title: "sidebar.tickets",
                permission: [config.RESOURCES.TICKET],
              }]
              : []),

            ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])
              ? [{
                key: "assets",
                path: `/${companySlug}/user-assets`,
                // icon: <AssetsIcon />,
                title: "sidebar.user-assets",
                permission: [config.RESOURCES.MY_ASSETS],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])
              ? [{
                key: "user-reports",
                path: `/${companySlug}/user-reports`,
                // icon: <ReportsIcon />,
                title: "sidebar.user-reports",
                permission: [config.RESOURCES.ATTENDANCE_REPORT],
              }]
              : []),


            ...(getResource(config.RESOURCES.RANKING, ["read"])
              ? [{
                key: "individual-ranking",
                path: `/${companySlug}/individual-rank`,
                // icon: <RankingIcon />,
                title: "sidebar.individual-rank",
                permission: [config.RESOURCES.RANKING],
              }]
              : []),

            // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])
            //   ? [{
            //     key: "goals",
            //     path: `/${companySlug}/my-goals`,
            //     // icon: <GiOnTarget />,
            //     title: "sidebar.create-goals",
            //     permission: [config.RESOURCES.MY_GOAL],
            //   }]
            //   : []),

            // ...(getResource(config.RESOURCES.AUDITS, ["read"])
            //   ? [{
            //     key: "audits",
            //     path: `/${companySlug}/my-audit-history`,
            //     // icon: <FaFileInvoice />,
            //     title: "Audits",
            //     permission: [config.RESOURCES.AUDITS],
            //   }]
            //   : []),

            // {
            //   key: "proejct-overview",
            //   path: `/${companySlug}/project-overview`,
            //   // icon: <GiOnTarget />,
            //   title: "sidebar.project-overview",
            //   permission: [],
            // },
            ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
              {
                key: "Bill Reimbursement",
                path: `/${companySlug}/bill-reimbursement`,
                title: "sidebar.bill-reimbursement",
                submenu: false,
                permission: [config.RESOURCES.BILLREIMBURSEMENT],
              }] : [],
          ],
        },] : []),

    // ✅ "Team" submenu - Same structure as Admin
    ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
      ? [
        {
          icon: <TeamInfoIcon />,
          className: "main-submenu",
          key: "sub16",
          title: "Team",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [
              {
                key: "employee-master",
                path: `/${companySlug}/employee-master`,
                title: "sidebar.employee-master",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_MASTER],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [
              {
                key: "employee-in-out",
                path: `/${companySlug}/employee-in-out`,
                title: "sidebar.Employee-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
            ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [
              {
                key: "resignation-employee-list",
                path: `/${companySlug}/resignation-employee-list`,
                title: "sidebar.resignation-employee-list",
                submenu: false,
                permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
              ? [{
                key: "last-working-day-emps",
                path: `/${companySlug}/last-working-day-emps`,
                // icon: <SeparationIcon />,
                title: "Last Working Day List",
                permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
              }]
              : []),
            // Milestone review forms - the HR/Admin tracker.
            //
            // Gated on the ROLE as well as the permission, because the server
            // gates this screen on role (`requireAdminOrHr`) and not on the
            // resource. Keying the menu off `create` alone would put a dead
            // link in front of anyone who was granted `create` without being an
            // Admin or HR - which is easy to do from the permission manager.
            ...((getRoles(["Admin"]) || getRoles(["HR"])) &&
              getResource(config.RESOURCES.REVIEW_FORMS, ["create"])
              ? [{
                key: "review-forms",
                path: `/${companySlug}/review-forms`,
                title: "Review Forms",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            // The approvals queue. Gated on `update`, which Reporting Managers
            // and ARMs hold as well - they are the people who actually decide.
            // The screen shows an explicit empty state to anyone with no
            // reports, so a stray viewer sees nothing rather than an error.
            ...(getResource(config.RESOURCES.REVIEW_FORMS, ["update"])
              ? [{
                key: "review-form-approvals",
                path: `/${companySlug}/review-form-approvals`,
                title: "Review Form Approvals",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [
              {
                key: "admin-reports",
                path: `/${companySlug}/admin-reports`,
                title: "sidebar.admin-reports",
                submenu: false,
                // Reports.js already renders one card per report family the caller may
                // read, so reaching the hub must not hinge on EMPLOYEE_REPORT alone -
                // a role granted only LEAVE_REPORT (e.g. an employee who may see their
                // own Leave Balance History) could otherwise never navigate here.
                permission: [
                  config.RESOURCES.EMPLOYEE_REPORT,
                  config.RESOURCES.LEAVE_REPORT,
                  config.RESOURCES.ATTENDANCE_REPORT,
                ],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [
              {
                key: "tickets",
                path: `/${companySlug}/employee-tickets`,
                title: "sidebar.tickets",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_TICKET],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "admin-leave-approval",
                path: `/${companySlug}/admin-leave-approval`,
                title: "sidebar.admin-leave-management",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "user-leave-approval",
                path: `/${companySlug}/user-leave-approve`,
                title: "sidebar.user-leave-management",
                submenu: false,
                permission: [config.RESOURCES.LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [
              {
                key: "admin-regularizatrion",
                path: `/${companySlug}/admin-regularizatrion`,
                title: "sidebar.admin-regularizatrion",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [
              {
                key: "ManageTeamRegularization",
                path: `/${companySlug}/manage-team-regularization`,
                title: "sidebar.ManageTeamRegularization",
                submenu: false,
                permission: [config.RESOURCES.TEAM_REGULARIZATION],
              }] : [],
            // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [
            //   {
            //     key: "Kpis",
            //     path: `/${companySlug}/Kpis`,
            //     title: "sidebar.manage-kpis",
            //     submenu: false,
            //     permission: [config.RESOURCES.KPI],
            //   }] : [],

          ],
        },
      ]
      : []),

    ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),
    ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),

    ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
      getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"]) ||
      getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"]) ||
      getResource(config.RESOURCES.NEW_JOINEES, ["read"])
      ? [
        {
          icon: <OnboardingIcon />,
          className: "main-submenu",
          key: "sub19",
          title: "Onboarding",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "company-doc-master",
                path: `/${companySlug}/company-doc-master`,
                title: "sidebar.docMaster",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-approvel",
                path: `/${companySlug}/candidate-approval`,
                title: "sidebar.candidate-approvel",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-list",
                path: `/${companySlug}/candidate-list`,
                title: "sidebar.candidate-approvel-list",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "initiation",
                path: `/${companySlug}/onboarding-initiation`,
                title: "sidebar.onboarding-initiation",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [
              {
                key: "doc-approve-list",
                path: `/${companySlug}/doc-approve-list`,
                title: "sidebar.doc-approve-list",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
              }] : [],
            ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [
              {
                key: "new-joinees-list",
                path: `/${companySlug}/new-joinees-list`,
                title: "sidebar.new-joinees-list",
                submenu: false,
                permission: [config.RESOURCES.NEW_JOINEES],
              }] : [],
          ],
        },
      ]
      : []),
    // ✅ "Audit" submenu - Same structure as Admin
    // ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"]) ||
    //   getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"]) ||
    //   getResource(config.RESOURCES.AUDIT_HEAD, ["read"])
    //   ? [
    //     {
    //       icon: <AuditIcon />,
    //       className: "main-submenu",
    //       key: "sub20",
    //       title: "Audit",
    //       submenu: true,
    //       subMenuInfo: [
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audit Master",
    //             path: `/${companySlug}/auditmaster`,
    //             title: "Audits Master",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audits",
    //             path: `/${companySlug}/audits`,
    //             title: "Audits",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [
    //           {
    //             key: "auditperformance",
    //             path: `/${companySlug}/auditperformance`,
    //             title: "Audit Performance",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDITS_PERFORMANCE],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [
    //           {
    //             key: "auditHead",
    //             path: `/${companySlug}/auditHead`,
    //             title: "Audit Head",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_HEAD],
    //           }] : [],
    //       ],
    //     },
    //   ]
    //   : []),


    // ...(getResource(config.RESOURCES.MANAGE_TICKET, ["read"])) ? [
    //   {
    //     key: "managetickets",
    //     path: `/${companySlug}/manage-tickets`,
    //     icon: <BsFillTicketDetailedFill />,
    //     title: "sidebar.manage-tickets",
    //     submenu: false,
    //     permission: [config.RESOURCES.MANAGE_TICKET],
    //   }] : [],


    ...(getResource(config.RESOURCES["KPI"], ["read"])) ?
      [{
        key: "Kpis",
        path: `/${companySlug}/user-kpi`,
        icon: <FundOutlined />,
        title: "sidebar.kpis",
        submenu: false,
        permission: [],
      }] : [],

    // ✅ "Survey" submenu - Same structure as Admin
    ...((getResource(config.RESOURCES["SURVEY"], ["read"]) ||
      getResource(config.RESOURCES["360QUE"], ["read"]) ||
      getResource(config.RESOURCES["360REVW"], ["read"]))
      ? [
        {
          icon: <i className="fas fa-poll" />,
          className: "main-submenu",
          key: "sub22",
          title: "Survey",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES["SURVEY"], ["read"])
              ? [
                {
                  key: "360_survey",
                  path: `/${companySlug}/360-survey`,
                  title: "sidebar.360_survey",
                  submenu: false,
                  permission: [config.RESOURCES["SURVEY"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360QUE"], ["read"])
              ? [
                {
                  key: "360_questionnaire",
                  path: `/${companySlug}/360-questionnaire`,
                  title: "sidebar.360_questionnaire",
                  submenu: false,
                  permission: [config.RESOURCES["360QUE"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360REVW"], ["read"])
              ? [
                {
                  key: "360_reviews",
                  path: `/${companySlug}/360-reviews`,
                  title: "sidebar.360_reviews",
                  submenu: false,
                  permission: [config.RESOURCES["360REVW"]],
                },
              ]
              : []),
          ],
        },
      ]
      : []),


    // ─── LMS (Learning Management) sidebar ────────────────────────────
    ...((
      getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ||
      getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ||
      getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_REPORTS"], ["read"])
    ) ? [
      {
        icon: <i className="fas fa-graduation-cap" />,
        className: "main-submenu",
        key: "lms_module_sidebar",
        title: "Learning",
        submenu: true,
        subMenuInfo: [
          ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ? [{
            key: "lms_courses_sidebar",
            path: `/${companySlug}/lms/courses`,
            title: "Course Management",
            submenu: false,
            permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{
            key: "lms_assessments_sidebar",
            path: `/${companySlug}/lms/assessments`,
            title: "Assessments",
            submenu: false,
            permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ? [{
            key: "lms_enrollments_sidebar",
            path: `/${companySlug}/lms/enrollments`,
            title: "Enrollments",
            submenu: false,
            permission: [config.RESOURCES["LMS_ENROLLMENT"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ? [{
            key: "lms_my_learning_sidebar",
            path: `/${companySlug}/lms/my-learning`,
            title: "My Learning",
            submenu: false,
            permission: [config.RESOURCES["LMS_MY_LEARNING"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ? [{
            key: "lms_certificates_sidebar",
            path: `/${companySlug}/lms/certificates`,
            title: "Certificates",
            submenu: false,
            permission: [config.RESOURCES["LMS_CERTIFICATES"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ? [{
            key: "lms_learning_paths_sidebar",
            path: `/${companySlug}/lms/learning-paths`,
            title: "Learning Paths",
            submenu: false,
            permission: [config.RESOURCES["LMS_LEARNING_PATH"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ? [{
            key: "lms_auto_assignment_sidebar",
            path: `/${companySlug}/lms/auto-assignment`,
            title: "Auto-Assignment",
            submenu: false,
            permission: [config.RESOURCES["LMS_AUTO_ASSIGNMENT"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ? [{
            key: "lms_team_learning_sidebar",
            path: `/${companySlug}/lms/team-learning`,
            title: "Team Learning",
            submenu: false,
            permission: [config.RESOURCES["LMS_TEAM_LEARNING"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{
            key: "lms_reports_sidebar",
            path: `/${companySlug}/lms/reports`,
            title: "LMS Reports",
            submenu: false,
            permission: [config.RESOURCES["LMS_REPORTS"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{
            key: "lms_grading_sidebar",
            path: `/${companySlug}/lms/grading`,
            title: "Manual Grading",
            submenu: false,
            permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{
            key: "lms_data_retention_sidebar",
            path: `/${companySlug}/lms/settings/data-retention`,
            title: "Data Retention",
            submenu: false,
            permission: [config.RESOURCES["LMS_REPORTS"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["update"]) ? [{
            key: "lms_cert_template_sidebar",
            path: `/${companySlug}/lms/settings/certificate-template`,
            title: "Certificate Template",
            submenu: false,
            permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]],
          }] : []),
        ],
      },
    ] : []),
    ...(getResource(config.RESOURCES.SKILL, ["read"]) ||
      getResource(config.RESOURCES.SKILLS_MATRIX, ["read"]) ||
      getResource(config.RESOURCES.SKILL_INVENTORY, ["read"]) ||
      getResource(config.RESOURCES.CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_VIEW, ["read"]) ||
      getResource(config.RESOURCES.CAREER_READINESS, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_COMPETENCY, ["read"]) ||
      getResource(config.RESOURCES.COURSE_SKILL_MAPPING, ["read"]) ? [{
      icon: <i className="fas fa-user-graduate" />,
      key: "talent_development_sidebar",
      title: "Talent Development",
      path: `/${companySlug}/talent-development`,
      submenu: false,
      permission: [config.RESOURCES.SKILL, config.RESOURCES.CAREER_PATH],
    }] : []),


    {
      icon: <i className="fas fa-ellipsis-h" />,
      className: "main-submenu",
      key: "sub18",
      title: "More",
      submenu: true,
      subMenuInfo: [
        // ...(getResource(config.RESOURCES.SUBSCRIPTION, ["read"])) ? [
        //   {
        //     key: "subscription",
        //     path: `/${companySlug}/subscription`,
        //     title: "sidebar.subscription",
        //     submenu: false,
        //     permission: [config.RESOURCES.SUBSCRIPTION],
        //   }] : [],
        ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [
          {
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],

        // ...(getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"])) ? [
        //   {
        //     key: "employee-goals",
        //     path: `/${companySlug}/employee-goals`,
        //     title: "sidebar.employee-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.EMPLOYEE_GOAL],
        //   }] : [],
        // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [
        //   {
        //     key: "team-goals",
        //     path: `/${companySlug}/team-goals`,
        //     title: "sidebar.team-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.TEAM_GOAL],
        //   }] : [],
        ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [
          {
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
        ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [
          {
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
        {
          key: "organization-policy",
          path: `/${companySlug}/organization-policy`,
          title: "sidebar.organization-policies",
          submenu: false,
          permission: [], // Always shown
        },
        ...(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"])) ? [
          {
            key: "Bill Reimbursement Approval",
            path: `/${companySlug}/admin-bill-reimbursement/submit`,
            title: "sidebar.admin-bill-reimbursement",
            submenu: false,
            permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL],
          },] : [],
      ],
    },


  ];


  const Account = [
    {
      key: "User Dashboard",
      path: `/${companySlug}/dashboard`,
      icon: <AiOutlineDashboard />,
      title: "sidebar.User_Dashboard",
      submenu: false,
      permission: [],
    },

    // ✅ "Me" submenu
    ...(getResource(config.RESOURCES.LEAVE, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION, ["read"]) ||
      getResource(config.RESOURCES.TICKET, ["read"]) ||
      getResource(config.RESOURCES.MY_ASSETS, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.RANKING, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"]) ||
      // getResource(config.RESOURCES.AUDITS, ["read"]) ||
      getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])
      ? [
        {
          key: "Me",
          title: "Me",
          className: " main-submenu",
          icon: <i className="fas fa-user " />,
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
              {
                key: "My-In-Out",
                path: `/${companySlug}/my-in-out`,
                title: "sidebar.My-In-Out",
                submenu: false,
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE, ["read"])
              ? [{
                key: "manage-leave-application",
                path: `/${companySlug}/leave-application`,
                // icon: <LeaveIcon />,
                title: "sidebar.leave-application",
                permission: [config.RESOURCES.LEAVE],
              }]
              : []),

            ...(getResource(config.RESOURCES.SHIFT_SWAP_APPLICATION, ["read"])
              ? [{
                key: "shift-change-requests",
                path: `/${companySlug}/shift-change-requests`,
                title: "sidebar.shift-change-requests",
                permission: [config.RESOURCES.SHIFT_SWAP_APPLICATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])
              ? [{
                key: "attendance-regularization",
                path: `/${companySlug}/employee-attendence-regularization`,
                // icon: <AttendanceIcon />,
                title: "sidebar.Attendence_Regularization",
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.SEPARATION, ["read"])
              ? [{
                key: "resignation",
                path: `/${companySlug}/resignation`,
                // icon: <SeparationIcon />,
                title: "sidebar.resignation",
                permission: [config.RESOURCES.SEPARATION],
              }]
              : []),


            // ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
            //   {
            //     key: "Attendance Regularization",
            //     path: `/${companySlug}/employee-attendence-regularization`,
            //     // icon: <AttendanceIcon />,
            //     title: "sidebar.Attendence_Regularization",
            //     submenu: false,
            //     permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
            //   }] : [],

            ...(getResource(config.RESOURCES.TICKET, ["read"])
              ? [{
                key: "tickets",
                path: `/${companySlug}/tickets`,
                // icon: <ImTicket />,
                title: "sidebar.tickets",
                permission: [config.RESOURCES.TICKET],
              }]
              : []),

            ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])
              ? [{
                key: "assets",
                path: `/${companySlug}/user-assets`,
                // icon: <AssetsIcon />,
                title: "sidebar.user-assets",
                permission: [config.RESOURCES.MY_ASSETS],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])
              ? [{
                key: "user-reports",
                path: `/${companySlug}/user-reports`,
                // icon: <ReportsIcon />,
                title: "sidebar.user-reports",
                permission: [config.RESOURCES.ATTENDANCE_REPORT],
              }]
              : []),


            ...(getResource(config.RESOURCES.RANKING, ["read"])
              ? [{
                key: "individual-ranking",
                path: `/${companySlug}/individual-rank`,
                // icon: <RankingIcon />,
                title: "sidebar.individual-rank",
                permission: [config.RESOURCES.RANKING],
              }]
              : []),

            // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])
            //   ? [{
            //     key: "goals",
            //     path: `/${companySlug}/my-goals`,
            //     // icon: <GiOnTarget />,
            //     title: "sidebar.create-goals",
            //     permission: [config.RESOURCES.MY_GOAL],
            //   }]
            //   : []),

            // ...(getResource(config.RESOURCES.AUDITS, ["read"])
            //   ? [{
            //     key: "audits",
            //     path: `/${companySlug}/my-audit-history`,
            //     // icon: <FaFileInvoice />,
            //     title: "Audits",
            //     permission: [config.RESOURCES.AUDITS],
            //   }]
            //   : []),

            ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
              {
                key: "Bill Reimbursement",
                path: `/${companySlug}/bill-reimbursement`,
                title: "sidebar.bill-reimbursement",
                submenu: false,
                permission: [config.RESOURCES.BILLREIMBURSEMENT],
              }] : [],


          ],
        },] : []),
    // ✅ "Team" submenu - Same structure as Admin

    ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
      ? [
        {
          icon: <TeamInfoIcon />,
          className: "main-submenu",
          key: "sub16",
          title: "Team",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [
              {
                key: "employee-master",
                path: `/${companySlug}/employee-master`,
                title: "sidebar.employee-master",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_MASTER],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [
              {
                key: "employee-in-out",
                path: `/${companySlug}/employee-in-out`,
                title: "sidebar.Employee-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
            ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [
              {
                key: "resignation-employee-list",
                path: `/${companySlug}/resignation-employee-list`,
                title: "sidebar.resignation-employee-list",
                submenu: false,
                permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
              ? [{
                key: "last-working-day-emps",
                path: `/${companySlug}/last-working-day-emps`,
                // icon: <SeparationIcon />,
                title: "Last Working Day List",
                permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
              }]
              : []),
            // Milestone review forms - the HR/Admin tracker.
            //
            // Gated on the ROLE as well as the permission, because the server
            // gates this screen on role (`requireAdminOrHr`) and not on the
            // resource. Keying the menu off `create` alone would put a dead
            // link in front of anyone who was granted `create` without being an
            // Admin or HR - which is easy to do from the permission manager.
            ...((getRoles(["Admin"]) || getRoles(["HR"])) &&
              getResource(config.RESOURCES.REVIEW_FORMS, ["create"])
              ? [{
                key: "review-forms",
                path: `/${companySlug}/review-forms`,
                title: "Review Forms",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            // The approvals queue. Gated on `update`, which Reporting Managers
            // and ARMs hold as well - they are the people who actually decide.
            // The screen shows an explicit empty state to anyone with no
            // reports, so a stray viewer sees nothing rather than an error.
            ...(getResource(config.RESOURCES.REVIEW_FORMS, ["update"])
              ? [{
                key: "review-form-approvals",
                path: `/${companySlug}/review-form-approvals`,
                title: "Review Form Approvals",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [
              {
                key: "admin-reports",
                path: `/${companySlug}/admin-reports`,
                title: "sidebar.admin-reports",
                submenu: false,
                // Reports.js already renders one card per report family the caller may
                // read, so reaching the hub must not hinge on EMPLOYEE_REPORT alone -
                // a role granted only LEAVE_REPORT (e.g. an employee who may see their
                // own Leave Balance History) could otherwise never navigate here.
                permission: [
                  config.RESOURCES.EMPLOYEE_REPORT,
                  config.RESOURCES.LEAVE_REPORT,
                  config.RESOURCES.ATTENDANCE_REPORT,
                ],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [
              {
                key: "tickets",
                path: `/${companySlug}/employee-tickets`,
                title: "sidebar.tickets",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_TICKET],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "admin-leave-approval",
                path: `/${companySlug}/admin-leave-approval`,
                title: "sidebar.admin-leave-management",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "user-leave-approval",
                path: `/${companySlug}/user-leave-approve`,
                title: "sidebar.user-leave-management",
                submenu: false,
                permission: [config.RESOURCES.LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [
              {
                key: "admin-regularizatrion",
                path: `/${companySlug}/admin-regularizatrion`,
                title: "sidebar.admin-regularizatrion",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [
              {
                key: "ManageTeamRegularization",
                path: `/${companySlug}/manage-team-regularization`,
                title: "sidebar.ManageTeamRegularization",
                submenu: false,
                permission: [config.RESOURCES.TEAM_REGULARIZATION],
              }] : [],
            // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [
            //   {
            //     key: "Kpis",
            //     path: `/${companySlug}/Kpis`,
            //     title: "sidebar.manage-kpis",
            //     submenu: false,
            //     permission: [config.RESOURCES.KPI],
            //   }] : [],

          ],
        },
      ]
      : []),

    // ✅ "Onboarding" submenu - Same structure as Admin
    ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),
    ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),

    ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
      getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"]) ||
      getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"]) ||
      getResource(config.RESOURCES.NEW_JOINEES, ["read"])
      ? [
        {
          icon: <OnboardingIcon />,
          className: "main-submenu",
          key: "sub19",
          title: "Onboarding",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "company-doc-master",
                path: `/${companySlug}/company-doc-master`,
                title: "sidebar.docMaster",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-approvel",
                path: `/${companySlug}/candidate-approval`,
                title: "sidebar.candidate-approvel",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-list",
                path: `/${companySlug}/candidate-list`,
                title: "sidebar.candidate-approvel-list",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "initiation",
                path: `/${companySlug}/onboarding-initiation`,
                title: "sidebar.onboarding-initiation",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [
              {
                key: "doc-approve-list",
                path: `/${companySlug}/doc-approve-list`,
                title: "sidebar.doc-approve-list",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
              }] : [],
            ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [
              {
                key: "new-joinees-list",
                path: `/${companySlug}/new-joinees-list`,
                title: "sidebar.new-joinees-list",
                submenu: false,
                permission: [config.RESOURCES.NEW_JOINEES],
              }] : [],
          ],
        },
      ]
      : []),
    // ✅ "Audit" submenu - Same structure as Admin
    // ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"]) ||
    //   getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"]) ||
    //   getResource(config.RESOURCES.AUDIT_HEAD, ["read"])
    //   ? [
    //     {
    //       icon: <AuditIcon />,
    //       className: "main-submenu",
    //       key: "sub20",
    //       title: "Audit",
    //       submenu: true,
    //       subMenuInfo: [
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audit Master",
    //             path: `/${companySlug}/auditmaster`,
    //             title: "Audits Master",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audits",
    //             path: `/${companySlug}/audits`,
    //             title: "Audits",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [
    //           {
    //             key: "auditperformance",
    //             path: `/${companySlug}/auditperformance`,
    //             title: "Audit Performance",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDITS_PERFORMANCE],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [
    //           {
    //             key: "auditHead",
    //             path: `/${companySlug}/auditHead`,
    //             title: "Audit Head",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_HEAD],
    //           }] : [],
    //       ],
    //     },
    //   ]
    //   : []),

    // ...(getResource(config.RESOURCES.MANAGE_TICKET, ["read"])) ? [
    //   {
    //     key: "managetickets",
    //     path: `/${companySlug}/manage-tickets`,
    //     icon: <BsFillTicketDetailedFill />,
    //     title: "sidebar.manage-tickets",
    //     submenu: false,
    //     permission: [config.RESOURCES.MANAGE_TICKET],
    //   }] : [],
    // {
    //   key: "appsettings",
    //   path: `/${companySlug}/app-setting`,
    //   icon: <FaRegSun />,
    //   title: "sidebar.app-setting",
    //   submenu: false,
    //   permission: [config.RESOURCES.SETTINGS],
    // },
    // {
    //   key: "employee-in-out",
    //   path: `/${companySlug}/employee-in-out`,
    //   icon: <InOuticon />,
    //   title: "sidebar.Employee-In-Out",
    //   submenu: false,
    //   permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
    // },
    ...(getResource(config.RESOURCES["KPI"], ["read"])) ?
      [
        {
          key: "Kpis",
          path: `/${companySlug}/user-kpi`,
          icon: <FundOutlined />,
          title: "sidebar.kpis",
          submenu: false,
          permission: [config.RESOURCES["KPI"]],
        }] : [],


    // ✅ "Survey" submenu - Same structure as Admin
    ...((getResource(config.RESOURCES["SURVEY"], ["read"]) ||
      getResource(config.RESOURCES["360QUE"], ["read"]) ||
      getResource(config.RESOURCES["360REVW"], ["read"]))
      ? [
        {
          icon: <i className="fas fa-poll" />,
          className: "main-submenu",
          key: "sub22",
          title: "Survey",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES["SURVEY"], ["read"])
              ? [
                {
                  key: "360_survey",
                  path: `/${companySlug}/360-survey`,
                  title: "sidebar.360_survey",
                  submenu: false,
                  permission: [config.RESOURCES["SURVEY"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360QUE"], ["read"])
              ? [
                {
                  key: "360_questionnaire",
                  path: `/${companySlug}/360-questionnaire`,
                  title: "sidebar.360_questionnaire",
                  submenu: false,
                  permission: [config.RESOURCES["360QUE"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360REVW"], ["read"])
              ? [
                {
                  key: "360_reviews",
                  path: `/${companySlug}/360-reviews`,
                  title: "sidebar.360_reviews",
                  submenu: false,
                  permission: [config.RESOURCES["360REVW"]],
                },
              ]
              : []),
          ],
        },
      ]
      : []),

    // ─── LMS (Learning Management) sidebar ────────────────────────────
    ...((
      getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ||
      getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ||
      getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_REPORTS"], ["read"])
    ) ? [
      {
        icon: <i className="fas fa-graduation-cap" />,
        className: "main-submenu",
        key: "lms_module_sidebar",
        title: "Learning",
        submenu: true,
        subMenuInfo: [
          ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ? [{
            key: "lms_courses_sidebar",
            path: `/${companySlug}/lms/courses`,
            title: "Course Management",
            submenu: false,
            permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{
            key: "lms_assessments_sidebar",
            path: `/${companySlug}/lms/assessments`,
            title: "Assessments",
            submenu: false,
            permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ? [{
            key: "lms_enrollments_sidebar",
            path: `/${companySlug}/lms/enrollments`,
            title: "Enrollments",
            submenu: false,
            permission: [config.RESOURCES["LMS_ENROLLMENT"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ? [{
            key: "lms_my_learning_sidebar",
            path: `/${companySlug}/lms/my-learning`,
            title: "My Learning",
            submenu: false,
            permission: [config.RESOURCES["LMS_MY_LEARNING"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ? [{
            key: "lms_certificates_sidebar",
            path: `/${companySlug}/lms/certificates`,
            title: "Certificates",
            submenu: false,
            permission: [config.RESOURCES["LMS_CERTIFICATES"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ? [{
            key: "lms_learning_paths_sidebar",
            path: `/${companySlug}/lms/learning-paths`,
            title: "Learning Paths",
            submenu: false,
            permission: [config.RESOURCES["LMS_LEARNING_PATH"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ? [{
            key: "lms_auto_assignment_sidebar",
            path: `/${companySlug}/lms/auto-assignment`,
            title: "Auto-Assignment",
            submenu: false,
            permission: [config.RESOURCES["LMS_AUTO_ASSIGNMENT"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ? [{
            key: "lms_team_learning_sidebar",
            path: `/${companySlug}/lms/team-learning`,
            title: "Team Learning",
            submenu: false,
            permission: [config.RESOURCES["LMS_TEAM_LEARNING"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{
            key: "lms_reports_sidebar",
            path: `/${companySlug}/lms/reports`,
            title: "LMS Reports",
            submenu: false,
            permission: [config.RESOURCES["LMS_REPORTS"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{
            key: "lms_grading_sidebar",
            path: `/${companySlug}/lms/grading`,
            title: "Manual Grading",
            submenu: false,
            permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{
            key: "lms_data_retention_sidebar",
            path: `/${companySlug}/lms/settings/data-retention`,
            title: "Data Retention",
            submenu: false,
            permission: [config.RESOURCES["LMS_REPORTS"]],
          }] : []),
          ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["update"]) ? [{
            key: "lms_cert_template_sidebar",
            path: `/${companySlug}/lms/settings/certificate-template`,
            title: "Certificate Template",
            submenu: false,
            permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]],
          }] : []),
        ],
      },
    ] : []),
    ...(getResource(config.RESOURCES.SKILL, ["read"]) ||
      getResource(config.RESOURCES.SKILLS_MATRIX, ["read"]) ||
      getResource(config.RESOURCES.SKILL_INVENTORY, ["read"]) ||
      getResource(config.RESOURCES.CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_VIEW, ["read"]) ||
      getResource(config.RESOURCES.CAREER_READINESS, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_COMPETENCY, ["read"]) ||
      getResource(config.RESOURCES.COURSE_SKILL_MAPPING, ["read"]) ? [{
      icon: <i className="fas fa-user-graduate" />,
      key: "talent_development_sidebar",
      title: "Talent Development",
      path: `/${companySlug}/talent-development`,
      submenu: false,
      permission: [config.RESOURCES.SKILL, config.RESOURCES.CAREER_PATH],
    }] : []),


    {
      icon: <i className="fas fa-ellipsis-h" />,
      className: " main-submenu",
      key: "sub18",
      title: "More",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [
          {
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],

        ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [
          {
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],

        ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [
          {
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
        {
          key: "organization-policy",
          path: `/${companySlug}/organization-policy`,
          title: "sidebar.organization-policies",
          submenu: false,
          permission: [], // Always shown
        },
        ...(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"])) ? [
          {
            key: "Bill Reimbursement Approval",
            path: `/${companySlug}/admin-bill-reimbursement/submit`,
            title: "sidebar.admin-bill-reimbursement",
            submenu: false,
            permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL],
          },] : [],

      ],
    },

  ];

  const Auditor = [
    {
      key: "User Dashboard",
      path: `/${companySlug}/dashboard`,
      icon: <AiOutlineDashboard />,
      title: "sidebar.User_Dashboard",
      submenu: false,
      permission: [],
    },


    // ✅ "Me" submenu
    ...(getResource(config.RESOURCES.LEAVE, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION, ["read"]) ||
      getResource(config.RESOURCES.TICKET, ["read"]) ||
      getResource(config.RESOURCES.MY_ASSETS, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.RANKING, ["read"]) ||
      getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
      // getResource(config.RESOURCES.AUDITS, ["read"]) ||
      getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])

      ? [
        {
          key: "Me",
          title: "Me",
          className: " main-submenu",
          icon: <i className="fas fa-user " />,
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
              {
                key: "My-In-Out",
                path: `/${companySlug}/my-in-out`,
                title: "sidebar.My-In-Out",
                submenu: false,
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE, ["read"])
              ? [{
                key: "manage-leave-application",
                path: `/${companySlug}/leave-application`,
                // icon: <LeaveIcon />,
                title: "sidebar.leave-application",
                permission: [config.RESOURCES.LEAVE],
              }]
              : []),

            ...(getResource(config.RESOURCES.SHIFT_SWAP_APPLICATION, ["read"])
              ? [{
                key: "shift-change-requests",
                path: `/${companySlug}/shift-change-requests`,
                title: "sidebar.shift-change-requests",
                permission: [config.RESOURCES.SHIFT_SWAP_APPLICATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])
              ? [{
                key: "attendance-regularization",
                path: `/${companySlug}/employee-attendence-regularization`,
                // icon: <AttendanceIcon />,
                title: "sidebar.Attendence_Regularization",
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.SEPARATION, ["read"])
              ? [{
                key: "resignation",
                path: `/${companySlug}/resignation`,
                // icon: <SeparationIcon />,
                title: "sidebar.resignation",
                permission: [config.RESOURCES.SEPARATION],
              }]
              : []),


            // ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
            //   {
            //     key: "Attendance Regularization",
            //     path: `/${companySlug}/employee-attendence-regularization`,
            //     // icon: <AttendanceIcon />,
            //     title: "sidebar.Attendence_Regularization",
            //     submenu: false,
            //     permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
            //   }] : [],

            ...(getResource(config.RESOURCES.TICKET, ["read"])
              ? [{
                key: "tickets",
                path: `/${companySlug}/tickets`,
                // icon: <ImTicket />,
                title: "sidebar.tickets",
                permission: [config.RESOURCES.TICKET],
              }]
              : []),

            ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])
              ? [{
                key: "assets",
                path: `/${companySlug}/user-assets`,
                // icon: <AssetsIcon />,
                title: "sidebar.user-assets",
                permission: [config.RESOURCES.MY_ASSETS],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])
              ? [{
                key: "user-reports",
                path: `/${companySlug}/user-reports`,
                // icon: <ReportsIcon />,
                title: "sidebar.user-reports",
                permission: [config.RESOURCES.ATTENDANCE_REPORT],
              }]
              : []),


            ...(getResource(config.RESOURCES.RANKING, ["read"])
              ? [{
                key: "individual-ranking",
                path: `/${companySlug}/individual-rank`,
                // icon: <RankingIcon />,
                title: "sidebar.individual-rank",
                permission: [config.RESOURCES.RANKING],
              }]
              : []),

            // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])
            //   ? [{
            //     key: "goals",
            //     path: `/${companySlug}/my-goals`,
            //     // icon: <GiOnTarget />,
            //     title: "sidebar.create-goals",
            //     permission: [config.RESOURCES.MY_GOAL],
            //   }]
            //   : []),

            // ...(getResource(config.RESOURCES.AUDITS, ["read"])
            //   ? [{
            //     key: "audits",
            //     path: `/${companySlug}/my-audit-history`,
            //     // icon: <FaFileInvoice />,
            //     title: "Audits",
            //     permission: [config.RESOURCES.AUDITS],
            //   }]
            //   : []),
            ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
              {
                key: "Bill Reimbursement",
                path: `/${companySlug}/bill-reimbursement`,
                title: "sidebar.bill-reimbursement",
                submenu: false,
                permission: [config.RESOURCES.BILLREIMBURSEMENT],
              }] : [],


          ],
        },] : []),

    // ✅ "Team" submenu - Same structure as Admin
    ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
      ? [
        {
          icon: <TeamInfoIcon />,
          className: "main-submenu",
          key: "sub16",
          title: "Team",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [
              {
                key: "employee-master",
                path: `/${companySlug}/employee-master`,
                title: "sidebar.employee-master",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_MASTER],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [
              {
                key: "employee-in-out",
                path: `/${companySlug}/employee-in-out`,
                title: "sidebar.Employee-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
            ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [
              {
                key: "resignation-employee-list",
                path: `/${companySlug}/resignation-employee-list`,
                title: "sidebar.resignation-employee-list",
                submenu: false,
                permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
              ? [{
                key: "last-working-day-emps",
                path: `/${companySlug}/last-working-day-emps`,
                // icon: <SeparationIcon />,
                title: "Last Working Day List",
                permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
              }]
              : []),
            // Milestone review forms - the HR/Admin tracker.
            //
            // Gated on the ROLE as well as the permission, because the server
            // gates this screen on role (`requireAdminOrHr`) and not on the
            // resource. Keying the menu off `create` alone would put a dead
            // link in front of anyone who was granted `create` without being an
            // Admin or HR - which is easy to do from the permission manager.
            ...((getRoles(["Admin"]) || getRoles(["HR"])) &&
              getResource(config.RESOURCES.REVIEW_FORMS, ["create"])
              ? [{
                key: "review-forms",
                path: `/${companySlug}/review-forms`,
                title: "Review Forms",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            // The approvals queue. Gated on `update`, which Reporting Managers
            // and ARMs hold as well - they are the people who actually decide.
            // The screen shows an explicit empty state to anyone with no
            // reports, so a stray viewer sees nothing rather than an error.
            ...(getResource(config.RESOURCES.REVIEW_FORMS, ["update"])
              ? [{
                key: "review-form-approvals",
                path: `/${companySlug}/review-form-approvals`,
                title: "Review Form Approvals",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [
              {
                key: "admin-reports",
                path: `/${companySlug}/admin-reports`,
                title: "sidebar.admin-reports",
                submenu: false,
                // Reports.js already renders one card per report family the caller may
                // read, so reaching the hub must not hinge on EMPLOYEE_REPORT alone -
                // a role granted only LEAVE_REPORT (e.g. an employee who may see their
                // own Leave Balance History) could otherwise never navigate here.
                permission: [
                  config.RESOURCES.EMPLOYEE_REPORT,
                  config.RESOURCES.LEAVE_REPORT,
                  config.RESOURCES.ATTENDANCE_REPORT,
                ],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [
              {
                key: "tickets",
                path: `/${companySlug}/employee-tickets`,
                title: "sidebar.tickets",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_TICKET],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "admin-leave-approval",
                path: `/${companySlug}/admin-leave-approval`,
                title: "sidebar.admin-leave-management",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "user-leave-approval",
                path: `/${companySlug}/user-leave-approve`,
                title: "sidebar.user-leave-management",
                submenu: false,
                permission: [config.RESOURCES.LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [
              {
                key: "admin-regularizatrion",
                path: `/${companySlug}/admin-regularizatrion`,
                title: "sidebar.admin-regularizatrion",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [
              {
                key: "ManageTeamRegularization",
                path: `/${companySlug}/manage-team-regularization`,
                title: "sidebar.ManageTeamRegularization",
                submenu: false,
                permission: [config.RESOURCES.TEAM_REGULARIZATION],
              }] : [],
            // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [
            //   {
            //     key: "Kpis",
            //     path: `/${companySlug}/Kpis`,
            //     title: "sidebar.manage-kpis",
            //     submenu: false,
            //     permission: [config.RESOURCES.KPI],
            //   }] : [],

          ],
        },
      ]
      : []),


    // ✅ "Onboarding" submenu - Same structure as Admin
    ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),
    ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),

    ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
      getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"]) ||
      getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"]) ||
      getResource(config.RESOURCES.NEW_JOINEES, ["read"])
      ? [
        {
          icon: <OnboardingIcon />,
          className: "main-submenu",
          key: "sub19",
          title: "Onboarding",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "company-doc-master",
                path: `/${companySlug}/company-doc-master`,
                title: "sidebar.docMaster",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-approvel",
                path: `/${companySlug}/candidate-approval`,
                title: "sidebar.candidate-approvel",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-list",
                path: `/${companySlug}/candidate-list`,
                title: "sidebar.candidate-approvel-list",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "initiation",
                path: `/${companySlug}/onboarding-initiation`,
                title: "sidebar.onboarding-initiation",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [
              {
                key: "doc-approve-list",
                path: `/${companySlug}/doc-approve-list`,
                title: "sidebar.doc-approve-list",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
              }] : [],
            ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [
              {
                key: "new-joinees-list",
                path: `/${companySlug}/new-joinees-list`,
                title: "sidebar.new-joinees-list",
                submenu: false,
                permission: [config.RESOURCES.NEW_JOINEES],
              }] : [],
          ],
        },
      ]
      : []),
    // ...(getResource(config.RESOURCES.MANAGE_TICKET, ["read"])) ? [
    //   {
    //     key: "managetickets",
    //     path: `/${companySlug}/manage-tickets`,
    //     icon: <BsFillTicketDetailedFill />,
    //     title: "sidebar.manage-tickets",
    //     submenu: false,
    //     permission: [config.RESOURCES.MANAGE_TICKET],
    //   }] : [],
    // ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"]) ||
    //   getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"]) ||
    //   getResource(config.RESOURCES.AUDIT_HEAD, ["read"])
    //   ? [
    //     {
    //       icon: <AuditIcon />,
    //       className: " main-submenu",
    //       key: "sub20",
    //       title: "Audit",
    //       submenu: true,
    //       subMenuInfo: [
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audit Master",
    //             path: `/${companySlug}/auditmaster`,
    //             title: "Audits Master",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audits",
    //             path: `/${companySlug}/audits`,
    //             title: "Audits",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [
    //           {
    //             key: "auditperformance",
    //             path: `/${companySlug}/auditperformance`,
    //             title: "Audit Performance",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDITS_PERFORMANCE],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [
    //           {
    //             key: "auditHead",
    //             path: `/${companySlug}/auditHead`,
    //             title: "Audit Head",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_HEAD],
    //           }] : [],
    //       ],
    //     },
    //   ]
    //   : []),


    // ✅ "Team" submenu - Same structure as Admin


    // {
    //   key: "appsettings",
    //   path: `/${companySlug}/app-setting`,
    //   icon: <FaRegSun />,
    //   title: "sidebar.app-setting",
    //   submenu: false,
    //   permission: [config.RESOURCES.SETTINGS],
    // },


    ...(getResource(config.RESOURCES["KPI"], ["read"])) ?
      [
        {
          key: "Kpis",
          path: `/${companySlug}/user-kpi`,
          icon: <FundOutlined />,
          title: "sidebar.kpis",
          submenu: false,
          permission: [config.RESOURCES["KPI"]],
        }] : [],


    // ✅ "Survey" submenu - Same structure as Admin
    ...((getResource(config.RESOURCES["SURVEY"], ["read"]) ||
      getResource(config.RESOURCES["360QUE"], ["read"]) ||
      getResource(config.RESOURCES["360REVW"], ["read"]))
      ? [
        {
          icon: <i className="fas fa-poll" />,
          className: "main-submenu",
          key: "sub22",
          title: "Survey",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES["SURVEY"], ["read"])
              ? [
                {
                  key: "360_survey",
                  path: `/${companySlug}/360-survey`,
                  title: "sidebar.360_survey",
                  submenu: false,
                  permission: [config.RESOURCES["SURVEY"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360QUE"], ["read"])
              ? [
                {
                  key: "360_questionnaire",
                  path: `/${companySlug}/360-questionnaire`,
                  title: "sidebar.360_questionnaire",
                  submenu: false,
                  permission: [config.RESOURCES["360QUE"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360REVW"], ["read"])
              ? [
                {
                  key: "360_reviews",
                  path: `/${companySlug}/360-reviews`,
                  title: "sidebar.360_reviews",
                  submenu: false,
                  permission: [config.RESOURCES["360REVW"]],
                },
              ]
              : []),

          ],
        },
      ]
      : []),
    {
      icon: <i className="fas fa-ellipsis-h" />,
      className: " main-submenu",
      key: "sub18",
      title: "More",
      submenu: true,
      subMenuInfo: [
        // ...(getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"])) ? [
        //   {
        //     key: "employee-goals",
        //     path: `/${companySlug}/employee-goals`,
        //     title: "sidebar.employee-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.EMPLOYEE_GOAL],
        //   }] : [],
        // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [
        //   {
        //     key: "team-goals",
        //     path: `/${companySlug}/team-goals`,
        //     title: "sidebar.team-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.TEAM_GOAL],
        //   }] : [],
        ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [
          {
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],

        ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [
          {
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],


        ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [
          {
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
        {
          key: "organization-policy",
          path: `/${companySlug}/organization-policy`,
          title: "sidebar.organization-policies",
          submenu: false,
          permission: [], // Always shown
        },
        ...(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"])) ? [
          {
            key: "Bill Reimbursement Approval",
            path: `/${companySlug}/admin-bill-reimbursement/submit`,
            title: "sidebar.admin-bill-reimbursement",
            submenu: false,
            permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL],
          },] : [],
      ],
    },

    // ─── LMS (Learning Management) sidebar ────────────────────────────
    ...((
      getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ||
      getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ||
      getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_REPORTS"], ["read"])
    ) ? [{
      icon: <i className="fas fa-graduation-cap" />,
      className: "main-submenu",
      key: "lms_module_sidebar",
      title: "Learning",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ? [{ key: "lms_courses_sidebar", path: `/${companySlug}/lms/courses`, title: "Course Management", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_assessments_sidebar", path: `/${companySlug}/lms/assessments`, title: "Assessments", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ? [{ key: "lms_enrollments_sidebar", path: `/${companySlug}/lms/enrollments`, title: "Enrollments", submenu: false, permission: [config.RESOURCES["LMS_ENROLLMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ? [{ key: "lms_my_learning_sidebar", path: `/${companySlug}/lms/my-learning`, title: "My Learning", submenu: false, permission: [config.RESOURCES["LMS_MY_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ? [{ key: "lms_certificates_sidebar", path: `/${companySlug}/lms/certificates`, title: "Certificates", submenu: false, permission: [config.RESOURCES["LMS_CERTIFICATES"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ? [{ key: "lms_learning_paths_sidebar", path: `/${companySlug}/lms/learning-paths`, title: "Learning Paths", submenu: false, permission: [config.RESOURCES["LMS_LEARNING_PATH"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ? [{ key: "lms_auto_assignment_sidebar", path: `/${companySlug}/lms/auto-assignment`, title: "Auto-Assignment", submenu: false, permission: [config.RESOURCES["LMS_AUTO_ASSIGNMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ? [{ key: "lms_team_learning_sidebar", path: `/${companySlug}/lms/team-learning`, title: "Team Learning", submenu: false, permission: [config.RESOURCES["LMS_TEAM_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_reports_sidebar", path: `/${companySlug}/lms/reports`, title: "LMS Reports", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_grading_sidebar", path: `/${companySlug}/lms/grading`, title: "Manual Grading", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_data_retention_sidebar", path: `/${companySlug}/lms/settings/data-retention`, title: "Data Retention", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["update"]) ? [{ key: "lms_cert_template_sidebar", path: `/${companySlug}/lms/settings/certificate-template`, title: "Certificate Template", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
      ],
    }] : []),
    ...(getResource(config.RESOURCES.SKILL, ["read"]) ||
      getResource(config.RESOURCES.SKILLS_MATRIX, ["read"]) ||
      getResource(config.RESOURCES.SKILL_INVENTORY, ["read"]) ||
      getResource(config.RESOURCES.CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_VIEW, ["read"]) ||
      getResource(config.RESOURCES.CAREER_READINESS, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_COMPETENCY, ["read"]) ||
      getResource(config.RESOURCES.COURSE_SKILL_MAPPING, ["read"]) ? [{
      icon: <i className="fas fa-user-graduate" />,
      key: "talent_development_sidebar",
      title: "Talent Development",
      path: `/${companySlug}/talent-development`,
      submenu: false,
      permission: [config.RESOURCES.SKILL, config.RESOURCES.CAREER_PATH],
    }] : []),


  ];

  const Reporting_Manager = [
    {
      key: "Admin Dashboard",
      path: `/${companySlug}/dashboard`,
      icon: <AiOutlineDashboard />,
      title: "sidebar.Admin_Dashboard",
      submenu: false,
      permission: [],
    },

    // ✅ "Me" submenu
    ...(getResource(config.RESOURCES.LEAVE, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION, ["read"]) ||
      getResource(config.RESOURCES.TICKET, ["read"]) ||
      getResource(config.RESOURCES.MY_ASSETS, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.RANKING, ["read"]) ||
      getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
      // getResource(config.RESOURCES.AUDITS, ["read"]) ||
      getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])

      ? [
        {
          key: "Me",
          title: "Me",
          className: " main-submenu",
          icon: <i className="fas fa-user " />,
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
              {
                key: "My-In-Out",
                path: `/${companySlug}/my-in-out`,
                title: "sidebar.My-In-Out",
                submenu: false,
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE, ["read"])
              ? [{
                key: "manage-leave-application",
                path: `/${companySlug}/leave-application`,
                // icon: <LeaveIcon />,
                title: "sidebar.leave-application",
                permission: [config.RESOURCES.LEAVE],
              }]
              : []),

            ...(getResource(config.RESOURCES.SHIFT_SWAP_APPLICATION, ["read"])
              ? [{
                key: "shift-change-requests",
                path: `/${companySlug}/shift-change-requests`,
                title: "sidebar.shift-change-requests",
                permission: [config.RESOURCES.SHIFT_SWAP_APPLICATION],
              }]
              : []),
            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])
              ? [{
                key: "attendance-regularization",
                path: `/${companySlug}/employee-attendence-regularization`,
                // icon: <AttendanceIcon />,
                title: "sidebar.Attendence_Regularization",
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }]
              : []),
            ...(getResource(config.RESOURCES.SEPARATION, ["read"])
              ? [{
                key: "resignation",
                path: `/${companySlug}/resignation`,
                // icon: <SeparationIcon />,
                title: "sidebar.resignation",
                permission: [config.RESOURCES.SEPARATION],
              }]
              : []),
            ...(getResource(config.RESOURCES.TICKET, ["read"])
              ? [{
                key: "tickets",
                path: `/${companySlug}/tickets`,
                // icon: <ImTicket />,
                title: "sidebar.tickets",
                permission: [config.RESOURCES.TICKET],
              }]
              : []),
            ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])
              ? [{
                key: "assets",
                path: `/${companySlug}/user-assets`,
                // icon: <AssetsIcon />,
                title: "sidebar.user-assets",
                permission: [config.RESOURCES.MY_ASSETS],
              }]
              : []),
            ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])
              ? [{
                key: "user-reports",
                path: `/${companySlug}/user-reports`,
                // icon: <ReportsIcon />,
                title: "sidebar.user-reports",
                permission: [config.RESOURCES.ATTENDANCE_REPORT],
              }]
              : []),
            ...(getResource(config.RESOURCES.RANKING, ["read"])
              ? [{
                key: "individual-ranking",
                path: `/${companySlug}/individual-rank`,
                // icon: <RankingIcon />,
                title: "sidebar.individual-rank",
                permission: [config.RESOURCES.RANKING],
              }]
              : []),
            // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])
            //   ? [{
            //     key: "goals",
            //     path: `/${companySlug}/my-goals`,
            //     // icon: <GiOnTarget />,
            //     title: "sidebar.create-goals",
            //     permission: [config.RESOURCES.MY_GOAL],
            //   }]
            //   : []),

            // ...(getResource(config.RESOURCES.AUDITS, ["read"])
            //   ? [{
            //     key: "audits",
            //     path: `/${companySlug}/my-audit-history`,
            //     // icon: <FaFileInvoice />,
            //     title: "Audits",
            //     permission: [config.RESOURCES.AUDITS],
            //   }]
            //   : []),
            ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
              {
                key: "Bill Reimbursement",
                path: `/${companySlug}/bill-reimbursement`,
                title: "sidebar.bill-reimbursement",
                submenu: false,
                permission: [config.RESOURCES.BILLREIMBURSEMENT],
              }] : [],

          ],
        },] : []),
    // ✅ "Team" submenu - Same structure as Admin
    ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.KPI, ["read"]) ||
      getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
      ? [
        {
          icon: <TeamInfoIcon />,
          className: "main-submenu",
          key: "sub16",
          title: "Team",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [
              {
                key: "employee-master",
                path: `/${companySlug}/employee-master`,
                title: "sidebar.employee-master",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_MASTER],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [
              {
                key: "employee-in-out",
                path: `/${companySlug}/employee-in-out`,
                title: "sidebar.Employee-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
            ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [
              {
                key: "resignation-employee-list",
                path: `/${companySlug}/resignation-employee-list`,
                title: "sidebar.resignation-employee-list",
                submenu: false,
                permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
              ? [{
                key: "last-working-day-emps",
                path: `/${companySlug}/last-working-day-emps`,
                // icon: <SeparationIcon />,
                title: "Last Working Day List",
                permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
              }]
              : []),
            // Milestone review forms - the HR/Admin tracker.
            //
            // Gated on the ROLE as well as the permission, because the server
            // gates this screen on role (`requireAdminOrHr`) and not on the
            // resource. Keying the menu off `create` alone would put a dead
            // link in front of anyone who was granted `create` without being an
            // Admin or HR - which is easy to do from the permission manager.
            ...((getRoles(["Admin"]) || getRoles(["HR"])) &&
              getResource(config.RESOURCES.REVIEW_FORMS, ["create"])
              ? [{
                key: "review-forms",
                path: `/${companySlug}/review-forms`,
                title: "Review Forms",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            // The approvals queue. Gated on `update`, which Reporting Managers
            // and ARMs hold as well - they are the people who actually decide.
            // The screen shows an explicit empty state to anyone with no
            // reports, so a stray viewer sees nothing rather than an error.
            ...(getResource(config.RESOURCES.REVIEW_FORMS, ["update"])
              ? [{
                key: "review-form-approvals",
                path: `/${companySlug}/review-form-approvals`,
                title: "Review Form Approvals",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [
              {
                key: "admin-reports",
                path: `/${companySlug}/admin-reports`,
                title: "sidebar.admin-reports",
                submenu: false,
                // Reports.js already renders one card per report family the caller may
                // read, so reaching the hub must not hinge on EMPLOYEE_REPORT alone -
                // a role granted only LEAVE_REPORT (e.g. an employee who may see their
                // own Leave Balance History) could otherwise never navigate here.
                permission: [
                  config.RESOURCES.EMPLOYEE_REPORT,
                  config.RESOURCES.LEAVE_REPORT,
                  config.RESOURCES.ATTENDANCE_REPORT,
                ],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [
              {
                key: "tickets",
                path: `/${companySlug}/employee-tickets`,
                title: "sidebar.tickets",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_TICKET],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "admin-leave-approval",
                path: `/${companySlug}/admin-leave-approval`,
                title: "sidebar.admin-leave-management",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "user-leave-approval",
                path: `/${companySlug}/user-leave-approve`,
                title: "sidebar.user-leave-management",
                submenu: false,
                permission: [config.RESOURCES.LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [
              {
                key: "admin-regularizatrion",
                path: `/${companySlug}/admin-regularizatrion`,
                title: "sidebar.admin-regularizatrion",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [
              {
                key: "ManageTeamRegularization",
                path: `/${companySlug}/manage-team-regularization`,
                title: "sidebar.ManageTeamRegularization",
                submenu: false,
                permission: [config.RESOURCES.TEAM_REGULARIZATION],
              }] : [],
            // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [
            //   {
            //     key: "Kpis",
            //     path: `/${companySlug}/kpis-management`,
            //     title: "sidebar.manage-kpis",
            //     submenu: false,
            //     permission: [config.RESOURCES.KPI],
            //   }] : [],

          ],
        },
      ]
      : []),
    // ...(getResource(config.RESOURCES.WAFFLE, ["read"])
    //   ? [
    //     {
    //       icon: <i className="fas fa-stroopwafel" />,
    //       className: "main-submenu",
    //       key: "sub17",
    //       title: "Waffle ",
    //       submenu: true,
    //       subMenuInfo: [
    //         {
    //           key: "karma-send",
    //           path: `/${companySlug}/Waffle`,
    //           title: "sidebar.karmaSend",
    //           submenu: false,
    //           permission: [config.RESOURCES.WAFFLE],
    //         },
    //         {
    //           key: "karma-weightage",
    //           path: `/${companySlug}/Waffle-weightage`,
    //           title: "sidebar.karmaWeightage",
    //           submenu: false,
    //           permission: [config.RESOURCES.WAFFLE_WEIGHTAGE],
    //         },
    //         ...(getResource(config.RESOURCES.WAFFLE, ["create"])
    //           ? [
    //             {
    //               key: "karma-karmaCategory",
    //               path: `/${companySlug}/Waffle-category`,
    //               title: "sidebar.karmaCategory",
    //               submenu: false,
    //               permission: [config.RESOURCES.WAFFLE],
    //             },
    //           ]
    //           : []),
    //         ...(getResource(config.RESOURCES.WAFFLE_REWARD, ["read"])
    //           ? [
    //             {
    //               key: "karma-reward-approvel",
    //               path: `/${companySlug}/Waffle-reward-approvel`,
    //               title: "sidebar.karmaRewardApprovel",
    //               submenu: false,
    //               permission: [config.RESOURCES.WAFFLE_REWARD],
    //             },
    //           ]
    //           : []),
    //       ],
    //     },
    //   ]
    //   : []),

    // ✅ "Onboarding" submenu - Same structure as Admin
    ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),
    ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),

    ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
      getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"]) ||
      getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"]) ||
      getResource(config.RESOURCES.NEW_JOINEES, ["read"])
      ? [
        {
          icon: <OnboardingIcon />,
          className: "main-submenu",
          key: "sub19",
          title: "Onboarding",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "company-doc-master",
                path: `/${companySlug}/company-doc-master`,
                title: "sidebar.docMaster",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-approvel",
                path: `/${companySlug}/candidate-approval`,
                title: "sidebar.candidate-approvel",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-list",
                path: `/${companySlug}/candidate-list`,
                title: "sidebar.candidate-approvel-list",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "initiation",
                path: `/${companySlug}/onboarding-initiation`,
                title: "sidebar.onboarding-initiation",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [
              {
                key: "doc-approve-list",
                path: `/${companySlug}/doc-approve-list`,
                title: "sidebar.doc-approve-list",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
              }] : [],
            ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [
              {
                key: "new-joinees-list",
                path: `/${companySlug}/new-joinees-list`,
                title: "sidebar.new-joinees-list",
                submenu: false,
                permission: [config.RESOURCES.NEW_JOINEES],
              }] : [],
          ],
        },
      ]
      : []),
    // ...(getResource(config.RESOURCES.AUDITS, ["read"])) ? [
    //   // {
    //   //   key: "Audits",
    //   //   path: `/${companySlug}/audit-history`,
    //   //   icon: <FaFileInvoice />,
    //   //   title: "Audits",
    //   //   submenu: false,
    //   //   permission: [config.RESOURCES.AUDITS],
    //   // }
    //   {
    //     icon: <AuditIcon />,
    //     className: "main-submenu",
    //     key: 'Audits',
    //     title: "Audit",
    //     submenu: true,
    //     subMenuInfo: [
    //       {
    //         key: "Audit Master",
    //         path: `/${companySlug}/auditmaster`,
    //         title: "Audits Master",
    //         submenu: false,
    //         permission: [config.RESOURCES.AUDIT_MASTER],
    //       },
    //       {
    //         key: "Audits",
    //         path: `/${companySlug}/audit-history`,
    //         title: "Audits",
    //         submenu: false,
    //         permission: [config.RESOURCES.AUDITS],
    //       },
    //       {
    //         key: "auditperformance",
    //         path: `/${companySlug}/auditperformance`,
    //         title: "Audit Performance",
    //         submenu: false,
    //         permission: [config.RESOURCES.AUDITS_PERFORMANCE],
    //       },
    //       {
    //         key: "auditHead",
    //         path: `/${companySlug}/auditHead`,
    //         title: "Audit Head",
    //         submenu: false,
    //         permission: [config.RESOURCES.AUDIT_HEAD],
    //       },
    //     ],
    //   }
    // ] : [],


    // ...(getResource(config.RESOURCES.MANAGE_TICKET, ["read"])) ? [
    //   {
    //     key: "managetickets",
    //     path: `/${companySlug}/manage-tickets`,
    //     icon: <BsFillTicketDetailedFill />,
    //     title: "sidebar.manage-tickets",
    //     submenu: false,
    //     permission: [config.RESOURCES.MANAGE_TICKET],
    //   }] : [],


    // ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //   {
    //     key: "Audits",
    //     path: `/${companySlug}/audits`,
    //     icon: <AuditIcon />,
    //     title: "Audits",
    //     submenu: false,
    //     permission: [config.RESOURCES.AUDIT_MASTER],
    //   }] : [],
    // {
    //   key: "appsettings",
    //   path: `/${companySlug}/app-setting`,
    //   icon: <FaRegSun />,
    //   title: "sidebar.app-setting",
    //   submenu: false,
    //   permission: [config.RESOURCES.SETTINGS],
    // },


    // ✅ "Survey" submenu - Same structure as Admin
    ...((getResource(config.RESOURCES["SURVEY"], ["read"]) ||
      getResource(config.RESOURCES["360QUE"], ["read"]) ||
      getResource(config.RESOURCES["360REVW"], ["read"]))
      ? [
        {
          icon: <i className="fas fa-poll" />,
          className: "main-submenu",
          key: "sub22",
          title: "Survey",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES["SURVEY"], ["read"])
              ? [
                {
                  key: "360_survey",
                  path: `/${companySlug}/360-survey`,
                  title: "sidebar.360_survey",
                  submenu: false,
                  permission: [config.RESOURCES["SURVEY"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360QUE"], ["read"])
              ? [
                {
                  key: "360_questionnaire",
                  path: `/${companySlug}/360-questionnaire`,
                  title: "sidebar.360_questionnaire",
                  submenu: false,
                  permission: [config.RESOURCES["360QUE"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360REVW"], ["read"])
              ? [
                {
                  key: "360_reviews",
                  path: `/${companySlug}/360-reviews`,
                  title: "sidebar.360_reviews",
                  submenu: false,
                  permission: [config.RESOURCES["360REVW"]],
                },
              ]
              : []),
          ],
        },
      ]
      : []),
    {
      icon: <i className="fas fa-ellipsis-h" />,
      className: "main-submenu",
      key: "sub18",
      title: "More",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [
          {
            key: "user-penalty-revoke",
            path: `/${companySlug}/user-penalty-revoke`,
            title: "sidebar.penalty-revoke",
            submenu: false,
            permission: [config.RESOURCES.LEAVE_MANAGEMENT],
          }] : [],
        // ...(getResource(config.RESOURCES.SUBSCRIPTION, ["read"])) ? [
        //   {
        //     key: "subscription",
        //     path: `/${companySlug}/subscription`,
        //     title: "sidebar.subscription",
        //     submenu: false,
        //     permission: [config.RESOURCES.SUBSCRIPTION],
        //   }] : [],
        ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [
          {
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],

        // ...(getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"])) ? [
        //   {
        //     key: "employee-goals",
        //     path: `/${companySlug}/employee-goals`,
        //     title: "sidebar.employee-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.EMPLOYEE_GOAL],
        //   }] : [],
        // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [
        //   {
        //     key: "team-goals",
        //     path: `/${companySlug}/team-goals`,
        //     title: "sidebar.team-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.TEAM_GOAL],
        //   }] : [],
        ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [
          {
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
        // ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
        //   {
        //     key: "Bill Reimbursement",
        //     path: `/${companySlug}/bill-reimbursement`,
        //     title: "sidebar.bill-reimbursement",
        //     submenu: false,
        //     permission: [config.RESOURCES.BILLREIMBURSEMENT],
        //   }] : [],

        ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [
          {
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
        ...(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"])) ? [
          {
            key: "Bill Reimbursement Approval",
            path: `/${companySlug}/admin-bill-reimbursement/submit`,
            title: "sidebar.admin-bill-reimbursement",
            submenu: false,
            permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL],
          },] : [],
        {
          key: "organization-policy",
          path: `/${companySlug}/organization-policy`,
          title: "sidebar.organization-policies",
          submenu: false,
          permission: [], // Always shown
        },

      ],
    },

    // ─── LMS (Learning Management) sidebar ────────────────────────────
    ...((
      getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ||
      getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ||
      getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_REPORTS"], ["read"])
    ) ? [{
      icon: <i className="fas fa-graduation-cap" />,
      className: "main-submenu",
      key: "lms_module_sidebar",
      title: "Learning",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ? [{ key: "lms_courses_sidebar", path: `/${companySlug}/lms/courses`, title: "Course Management", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_assessments_sidebar", path: `/${companySlug}/lms/assessments`, title: "Assessments", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ? [{ key: "lms_enrollments_sidebar", path: `/${companySlug}/lms/enrollments`, title: "Enrollments", submenu: false, permission: [config.RESOURCES["LMS_ENROLLMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ? [{ key: "lms_my_learning_sidebar", path: `/${companySlug}/lms/my-learning`, title: "My Learning", submenu: false, permission: [config.RESOURCES["LMS_MY_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ? [{ key: "lms_certificates_sidebar", path: `/${companySlug}/lms/certificates`, title: "Certificates", submenu: false, permission: [config.RESOURCES["LMS_CERTIFICATES"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ? [{ key: "lms_learning_paths_sidebar", path: `/${companySlug}/lms/learning-paths`, title: "Learning Paths", submenu: false, permission: [config.RESOURCES["LMS_LEARNING_PATH"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ? [{ key: "lms_auto_assignment_sidebar", path: `/${companySlug}/lms/auto-assignment`, title: "Auto-Assignment", submenu: false, permission: [config.RESOURCES["LMS_AUTO_ASSIGNMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ? [{ key: "lms_team_learning_sidebar", path: `/${companySlug}/lms/team-learning`, title: "Team Learning", submenu: false, permission: [config.RESOURCES["LMS_TEAM_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_reports_sidebar", path: `/${companySlug}/lms/reports`, title: "LMS Reports", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_grading_sidebar", path: `/${companySlug}/lms/grading`, title: "Manual Grading", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_data_retention_sidebar", path: `/${companySlug}/lms/settings/data-retention`, title: "Data Retention", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["update"]) ? [{ key: "lms_cert_template_sidebar", path: `/${companySlug}/lms/settings/certificate-template`, title: "Certificate Template", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
      ],
    }] : []),
    ...(getResource(config.RESOURCES.SKILL, ["read"]) ||
      getResource(config.RESOURCES.SKILLS_MATRIX, ["read"]) ||
      getResource(config.RESOURCES.SKILL_INVENTORY, ["read"]) ||
      getResource(config.RESOURCES.CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_VIEW, ["read"]) ||
      getResource(config.RESOURCES.CAREER_READINESS, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_COMPETENCY, ["read"]) ||
      getResource(config.RESOURCES.COURSE_SKILL_MAPPING, ["read"]) ? [{
      icon: <i className="fas fa-user-graduate" />,
      key: "talent_development_sidebar",
      title: "Talent Development",
      path: `/${companySlug}/talent-development`,
      submenu: false,
      permission: [config.RESOURCES.SKILL, config.RESOURCES.CAREER_PATH],
    }] : []),


  ];

  const Assistant_Reporting_Manager = [
    {
      key: "Admin Dashboard",
      path: `/${companySlug}/dashboard`,
      icon: <AiOutlineDashboard />,
      title: "sidebar.Admin_Dashboard",
      submenu: false,
      permission: [],
    },
    // ✅ "Me" submenu
    ...(getResource(config.RESOURCES.LEAVE, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION, ["read"]) ||
      getResource(config.RESOURCES.TICKET, ["read"]) ||
      getResource(config.RESOURCES.MY_ASSETS, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.RANKING, ["read"]) ||
      getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
      // getResource(config.RESOURCES.AUDITS, ["read"]) ||
      getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])


      ? [
        {
          key: "Me",
          title: "Me",
          className: " main-submenu",
          icon: <i className="fas fa-user " />,
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
              {
                key: "My-In-Out",
                path: `/${companySlug}/my-in-out`,
                title: "sidebar.My-In-Out",
                submenu: false,
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE, ["read"])
              ? [{
                key: "manage-leave-application",
                path: `/${companySlug}/leave-application`,
                // icon: <LeaveIcon />,
                title: "sidebar.leave-application",
                permission: [config.RESOURCES.LEAVE],
              }]
              : []),

            ...(getResource(config.RESOURCES.SHIFT_SWAP_APPLICATION, ["read"])
              ? [{
                key: "shift-change-requests",
                path: `/${companySlug}/shift-change-requests`,
                title: "sidebar.shift-change-requests",
                permission: [config.RESOURCES.SHIFT_SWAP_APPLICATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])
              ? [{
                key: "attendance-regularization",
                path: `/${companySlug}/employee-attendence-regularization`,
                // icon: <AttendanceIcon />,
                title: "sidebar.Attendence_Regularization",
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.SEPARATION, ["read"])
              ? [{
                key: "resignation",
                path: `/${companySlug}/resignation`,
                // icon: <SeparationIcon />,
                title: "sidebar.resignation",
                permission: [config.RESOURCES.SEPARATION],
              }]
              : []),


            // ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
            //   {
            //     key: "Attendance Regularization",
            //     path: `/${companySlug}/employee-attendence-regularization`,
            //     // icon: <AttendanceIcon />,
            //     title: "sidebar.Attendence_Regularization",
            //     submenu: false,
            //     permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
            //   }] : [],

            ...(getResource(config.RESOURCES.TICKET, ["read"])
              ? [{
                key: "tickets",
                path: `/${companySlug}/tickets`,
                // icon: <ImTicket />,
                title: "sidebar.tickets",
                permission: [config.RESOURCES.TICKET],
              }]
              : []),

            ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])
              ? [{
                key: "assets",
                path: `/${companySlug}/user-assets`,
                // icon: <AssetsIcon />,
                title: "sidebar.user-assets",
                permission: [config.RESOURCES.MY_ASSETS],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])
              ? [{
                key: "user-reports",
                path: `/${companySlug}/user-reports`,
                // icon: <ReportsIcon />,
                title: "sidebar.user-reports",
                permission: [config.RESOURCES.ATTENDANCE_REPORT],
              }]
              : []),


            ...(getResource(config.RESOURCES.RANKING, ["read"])
              ? [{
                key: "individual-ranking",
                path: `/${companySlug}/individual-rank`,
                // icon: <RankingIcon />,
                title: "sidebar.individual-rank",
                permission: [config.RESOURCES.RANKING],
              }]
              : []),

            // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])
            //   ? [{
            //     key: "goals",
            //     path: `/${companySlug}/my-goals`,
            //     // icon: <GiOnTarget />,
            //     title: "sidebar.create-goals",
            //     permission: [config.RESOURCES.MY_GOAL],
            //   }]
            //   : []),

            // ...(getResource(config.RESOURCES.AUDITS, ["read"])
            //   ? [{
            //     key: "audits",
            //     path: `/${companySlug}/my-audit-history`,
            //     // icon: <FaFileInvoice />,
            //     title: "Audits",
            //     permission: [config.RESOURCES.AUDITS],
            //   }]
            //   : []),
            ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
              {
                key: "Bill Reimbursement",
                path: `/${companySlug}/bill-reimbursement`,
                title: "sidebar.bill-reimbursement",
                submenu: false,
                permission: [config.RESOURCES.BILLREIMBURSEMENT],
              }] : [],

          ],
        },] : []),
    // ✅ "Team" submenu - Same structure as Admin
    ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.KPI, ["read"]) ||
      getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
      ? [
        {
          icon: <TeamInfoIcon />,
          className: "main-submenu",
          key: "sub16",
          title: "Team",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [
              {
                key: "employee-master",
                path: `/${companySlug}/employee-master`,
                title: "sidebar.employee-master",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_MASTER],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [
              {
                key: "employee-in-out",
                path: `/${companySlug}/employee-in-out`,
                title: "sidebar.Employee-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
            ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [
              {
                key: "resignation-employee-list",
                path: `/${companySlug}/resignation-employee-list`,
                title: "sidebar.resignation-employee-list",
                submenu: false,
                permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
              ? [{
                key: "last-working-day-emps",
                path: `/${companySlug}/last-working-day-emps`,
                // icon: <SeparationIcon />,
                title: "Last Working Day List",
                permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
              }]
              : []),
            // Milestone review forms - the HR/Admin tracker.
            //
            // Gated on the ROLE as well as the permission, because the server
            // gates this screen on role (`requireAdminOrHr`) and not on the
            // resource. Keying the menu off `create` alone would put a dead
            // link in front of anyone who was granted `create` without being an
            // Admin or HR - which is easy to do from the permission manager.
            ...((getRoles(["Admin"]) || getRoles(["HR"])) &&
              getResource(config.RESOURCES.REVIEW_FORMS, ["create"])
              ? [{
                key: "review-forms",
                path: `/${companySlug}/review-forms`,
                title: "Review Forms",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            // The approvals queue. Gated on `update`, which Reporting Managers
            // and ARMs hold as well - they are the people who actually decide.
            // The screen shows an explicit empty state to anyone with no
            // reports, so a stray viewer sees nothing rather than an error.
            ...(getResource(config.RESOURCES.REVIEW_FORMS, ["update"])
              ? [{
                key: "review-form-approvals",
                path: `/${companySlug}/review-form-approvals`,
                title: "Review Form Approvals",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [
              {
                key: "admin-reports",
                path: `/${companySlug}/admin-reports`,
                title: "sidebar.admin-reports",
                submenu: false,
                // Reports.js already renders one card per report family the caller may
                // read, so reaching the hub must not hinge on EMPLOYEE_REPORT alone -
                // a role granted only LEAVE_REPORT (e.g. an employee who may see their
                // own Leave Balance History) could otherwise never navigate here.
                permission: [
                  config.RESOURCES.EMPLOYEE_REPORT,
                  config.RESOURCES.LEAVE_REPORT,
                  config.RESOURCES.ATTENDANCE_REPORT,
                ],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [
              {
                key: "tickets",
                path: `/${companySlug}/employee-tickets`,
                title: "sidebar.tickets",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_TICKET],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "admin-leave-approval",
                path: `/${companySlug}/admin-leave-approval`,
                title: "sidebar.admin-leave-management",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "user-leave-approval",
                path: `/${companySlug}/user-leave-approve`,
                title: "sidebar.user-leave-management",
                submenu: false,
                permission: [config.RESOURCES.LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [
              {
                key: "admin-regularizatrion",
                path: `/${companySlug}/admin-regularizatrion`,
                title: "sidebar.admin-regularizatrion",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [
              {
                key: "ManageTeamRegularization",
                path: `/${companySlug}/manage-team-regularization`,
                title: "sidebar.ManageTeamRegularization",
                submenu: false,
                permission: [config.RESOURCES.TEAM_REGULARIZATION],
              }] : [],
            // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [
            //   {
            //     key: "Kpis",
            //     path: `/${companySlug}/kpis-management`,
            //     title: "sidebar.manage-kpis",
            //     submenu: false,
            //     permission: [config.RESOURCES.KPI],
            //   }] : [],

          ],
        },
      ]
      : []),

    // ✅ "Onboarding" submenu - Same structure as Admin
    ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),
    ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),

    ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
      getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"]) ||
      getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"]) ||
      getResource(config.RESOURCES.NEW_JOINEES, ["read"])
      ? [
        {
          icon: <OnboardingIcon />,
          className: "main-submenu",
          key: "sub19",
          title: "Onboarding",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "company-doc-master",
                path: `/${companySlug}/company-doc-master`,
                title: "sidebar.docMaster",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-approvel",
                path: `/${companySlug}/candidate-approval`,
                title: "sidebar.candidate-approvel",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-list",
                path: `/${companySlug}/candidate-list`,
                title: "sidebar.candidate-approvel-list",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "initiation",
                path: `/${companySlug}/onboarding-initiation`,
                title: "sidebar.onboarding-initiation",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [
              {
                key: "doc-approve-list",
                path: `/${companySlug}/doc-approve-list`,
                title: "sidebar.doc-approve-list",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
              }] : [],
            ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [
              {
                key: "new-joinees-list",
                path: `/${companySlug}/new-joinees-list`,
                title: "sidebar.new-joinees-list",
                submenu: false,
                permission: [config.RESOURCES.NEW_JOINEES],
              }] : [],
          ],
        },
      ]
      : []),
    // ✅ "Audit" submenu - Same structure as Admin
    // ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"]) ||
    //   getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"]) ||
    //   getResource(config.RESOURCES.AUDIT_HEAD, ["read"])
    //   ? [
    //     {
    //       icon: <AuditIcon />,
    //       className: "main-submenu",
    //       key: "sub20",
    //       title: "Audit",
    //       submenu: true,
    //       subMenuInfo: [
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audit Master",
    //             path: `/${companySlug}/auditmaster`,
    //             title: "Audits Master",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audits",
    //             path: `/${companySlug}/audits`,
    //             title: "Audits",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [
    //           {
    //             key: "auditperformance",
    //             path: `/${companySlug}/auditperformance`,
    //             title: "Audit Performance",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDITS_PERFORMANCE],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [
    //           {
    //             key: "auditHead",
    //             path: `/${companySlug}/auditHead`,
    //             title: "Audit Head",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_HEAD],
    //           }] : [],
    //       ],
    //     },
    //   ]
    //   : []),


    // ...(getResource(config.RESOURCES.MANAGE_TICKET, ["read"])) ? [
    //   {
    //     key: "managetickets",
    //     path: `/${companySlug}/manage-tickets`,
    //     icon: <BsFillTicketDetailedFill />,
    //     title: "sidebar.manage-tickets",
    //     submenu: false,
    //     permission: [config.RESOURCES.MANAGE_TICKET],
    //   }] : [],


    // {
    //   key: "appsettings",
    //   path: `/${companySlug}/app-setting`,
    //   icon: <FaRegSun />,
    //   title: "sidebar.app-setting",
    //   submenu: false,
    //   permission: [config.RESOURCES.SETTINGS],
    // },

    // ...(getResource(config.RESOURCES.WAFFLE, ["read"])
    //   ? [
    //     {
    //       icon: <i className="fas fa-stroopwafel " />,
    //       className: " main-submenu",
    //       key: "sub17",
    //       title: "Waffle ",
    //       submenu: true,
    //       subMenuInfo: [
    //         {
    //           key: "karma-send",
    //           path: `/${companySlug}/Waffle`,
    //           title: "sidebar.karmaSend",
    //           submenu: false,
    //           permission: [config.RESOURCES.WAFFLE],
    //         },
    //         {
    //           key: "karma-weightage",
    //           path: `/${companySlug}/Waffle-weightage`,
    //           title: "sidebar.karmaWeightage",
    //           submenu: false,
    //           permission: [config.RESOURCES.WAFFLE_WEIGHTAGE],
    //         },
    //         ...(getResource(config.RESOURCES.WAFFLE, ["create"])
    //           ? [
    //             {
    //               key: "karma-karmaCategory",
    //               path: `/${companySlug}/Waffle-category`,
    //               title: "sidebar.karmaCategory",
    //               submenu: false,
    //               permission: [config.RESOURCES.WAFFLE],
    //             },
    //           ]
    //           : []),
    //         ...(getResource(config.RESOURCES.WAFFLE_REWARD, ["read"])
    //           ? [
    //             {
    //               key: "karma-reward-approvel",
    //               path: `/${companySlug}/Waffle-reward-approvel`,
    //               title: "sidebar.karmaRewardApprovel",
    //               submenu: false,
    //               permission: [config.RESOURCES.WAFFLE_REWARD],
    //             },
    //           ]
    //           : []),
    //       ],
    //     },
    //   ]
    //   : []),

    // ✅ "Survey" submenu - Same structure as Admin
    ...((getResource(config.RESOURCES["SURVEY"], ["read"]) ||
      getResource(config.RESOURCES["360QUE"], ["read"]) ||
      getResource(config.RESOURCES["360REVW"], ["read"]))
      ? [
        {
          icon: <i className="fas fa-poll" />,
          className: "main-submenu",
          key: "sub22",
          title: "Survey",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES["SURVEY"], ["read"])
              ? [
                {
                  key: "360_survey",
                  path: `/${companySlug}/360-survey`,
                  title: "sidebar.360_survey",
                  submenu: false,
                  permission: [config.RESOURCES["SURVEY"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360QUE"], ["read"])
              ? [
                {
                  key: "360_questionnaire",
                  path: `/${companySlug}/360-questionnaire`,
                  title: "sidebar.360_questionnaire",
                  submenu: false,
                  permission: [config.RESOURCES["360QUE"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360REVW"], ["read"])
              ? [
                {
                  key: "360_reviews",
                  path: `/${companySlug}/360-reviews`,
                  title: "sidebar.360_reviews",
                  submenu: false,
                  permission: [config.RESOURCES["360REVW"]],
                },
              ]
              : []),
          ],
        },
      ]
      : []),
    {
      icon: <i className="fas fa-ellipsis-h" />,
      className: "main-submenu",
      key: "sub18",
      title: "More",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [
          {
            key: "user-penalty-revoke",
            path: `/${companySlug}/user-penalty-revoke`,
            title: "sidebar.penalty-revoke",
            submenu: false,
            permission: [config.RESOURCES.LEAVE_MANAGEMENT],
          }] : [],
        // ...(getResource(config.RESOURCES.SUBSCRIPTION, ["read"])) ? [
        //   {
        //     key: "subscription",
        //     path: `/${companySlug}/subscription`,
        //     title: "sidebar.subscription",
        //     submenu: false,
        //     permission: [config.RESOURCES.SUBSCRIPTION],
        //   }] : [],
        ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [
          {
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],

        // ...(getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"])) ? [
        //   {
        //     key: "employee-goals",
        //     path: `/${companySlug}/employee-goals`,
        //     title: "sidebar.employee-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.EMPLOYEE_GOAL],
        //   }] : [],
        // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [
        //   {
        //     key: "team-goals",
        //     path: `/${companySlug}/team-goals`,
        //     title: "sidebar.team-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.TEAM_GOAL],
        //   }] : [],
        ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [
          {
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
        // "Bill Reimbursement" (/bill-reimbursement) is the employee's own screen and
        // belongs to the "Me" submenu, which already lists it. Keeping a duplicate here
        // sent "More > Bill Reimbursement" to a path that only "Me" owns, so the header
        // tabs switched to the Me tab set. "More" keeps the approval screen below.
        // Already commented out the same way in the Reporting_Manager array above.
        // ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
        //   {
        //     key: "Bill Reimbursement",
        //     path: `/${companySlug}/bill-reimbursement`,
        //     title: "sidebar.bill-reimbursement",
        //     submenu: false,
        //     permission: [config.RESOURCES.BILLREIMBURSEMENT],
        //   }] : [],

        ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [
          {
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
        ...(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"])) ? [
          {
            key: "Bill Reimbursement Approval",
            path: `/${companySlug}/admin-bill-reimbursement/submit`,
            title: "sidebar.admin-bill-reimbursement",
            submenu: false,
            permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL],
          },] : [],
        {
          key: "organization-policy",
          path: `/${companySlug}/organization-policy`,
          title: "sidebar.organization-policies",
          submenu: false,
          permission: [], // Always shown
        },
      ],
    },
    // ─── LMS (Learning Management) sidebar ────────────────────────────
    ...((
      getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ||
      getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ||
      getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_REPORTS"], ["read"])
    ) ? [{
      icon: <i className="fas fa-graduation-cap" />,
      className: "main-submenu",
      key: "lms_module_sidebar",
      title: "Learning",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ? [{ key: "lms_courses_sidebar", path: `/${companySlug}/lms/courses`, title: "Course Management", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_assessments_sidebar", path: `/${companySlug}/lms/assessments`, title: "Assessments", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ? [{ key: "lms_enrollments_sidebar", path: `/${companySlug}/lms/enrollments`, title: "Enrollments", submenu: false, permission: [config.RESOURCES["LMS_ENROLLMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ? [{ key: "lms_my_learning_sidebar", path: `/${companySlug}/lms/my-learning`, title: "My Learning", submenu: false, permission: [config.RESOURCES["LMS_MY_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ? [{ key: "lms_certificates_sidebar", path: `/${companySlug}/lms/certificates`, title: "Certificates", submenu: false, permission: [config.RESOURCES["LMS_CERTIFICATES"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ? [{ key: "lms_learning_paths_sidebar", path: `/${companySlug}/lms/learning-paths`, title: "Learning Paths", submenu: false, permission: [config.RESOURCES["LMS_LEARNING_PATH"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ? [{ key: "lms_auto_assignment_sidebar", path: `/${companySlug}/lms/auto-assignment`, title: "Auto-Assignment", submenu: false, permission: [config.RESOURCES["LMS_AUTO_ASSIGNMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ? [{ key: "lms_team_learning_sidebar", path: `/${companySlug}/lms/team-learning`, title: "Team Learning", submenu: false, permission: [config.RESOURCES["LMS_TEAM_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_reports_sidebar", path: `/${companySlug}/lms/reports`, title: "LMS Reports", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_grading_sidebar", path: `/${companySlug}/lms/grading`, title: "Manual Grading", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_data_retention_sidebar", path: `/${companySlug}/lms/settings/data-retention`, title: "Data Retention", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["update"]) ? [{ key: "lms_cert_template_sidebar", path: `/${companySlug}/lms/settings/certificate-template`, title: "Certificate Template", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
      ],
    }] : []),
    ...(getResource(config.RESOURCES.SKILL, ["read"]) ||
      getResource(config.RESOURCES.SKILLS_MATRIX, ["read"]) ||
      getResource(config.RESOURCES.SKILL_INVENTORY, ["read"]) ||
      getResource(config.RESOURCES.CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_VIEW, ["read"]) ||
      getResource(config.RESOURCES.CAREER_READINESS, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_COMPETENCY, ["read"]) ||
      getResource(config.RESOURCES.COURSE_SKILL_MAPPING, ["read"]) ? [{
      icon: <i className="fas fa-user-graduate" />,
      key: "talent_development_sidebar",
      title: "Talent Development",
      path: `/${companySlug}/talent-development`,
      submenu: false,
      permission: [config.RESOURCES.SKILL, config.RESOURCES.CAREER_PATH],
    }] : []),


  ];

  const Network_Team = [
    {
      key: "User Dashboard",
      path: `/${companySlug}/dashboard`,
      icon: <AiOutlineDashboard />,
      title: "sidebar.User_Dashboard",
      submenu: false,
      permission: [],
    },

    // ✅ "Me" submenu
    ...(getResource(config.RESOURCES.LEAVE, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION, ["read"]) ||
      getResource(config.RESOURCES.TICKET, ["read"]) ||
      getResource(config.RESOURCES.MY_ASSETS, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.RANKING, ["read"]) ||
      getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
      // getResource(config.RESOURCES.AUDITS, ["read"]) ||
      getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])

      ? [
        {
          key: "Me",
          title: "Me",
          className: " main-submenu",
          icon: <i className="fas fa-user " />,
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
              {
                key: "My-In-Out",
                path: `/${companySlug}/my-in-out`,
                title: "sidebar.My-In-Out",
                submenu: false,
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE, ["read"])
              ? [{
                key: "manage-leave-application",
                path: `/${companySlug}/leave-application`,
                // icon: <LeaveIcon />,
                title: "sidebar.leave-application",
                permission: [config.RESOURCES.LEAVE],
              }]
              : []),

            ...(getResource(config.RESOURCES.SHIFT_SWAP_APPLICATION, ["read"])
              ? [{
                key: "shift-change-requests",
                path: `/${companySlug}/shift-change-requests`,
                title: "sidebar.shift-change-requests",
                permission: [config.RESOURCES.SHIFT_SWAP_APPLICATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])
              ? [{
                key: "attendance-regularization",
                path: `/${companySlug}/employee-attendence-regularization`,
                // icon: <AttendanceIcon />,
                title: "sidebar.Attendence_Regularization",
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.SEPARATION, ["read"])
              ? [{
                key: "resignation",
                path: `/${companySlug}/resignation`,
                // icon: <SeparationIcon />,
                title: "sidebar.resignation",
                permission: [config.RESOURCES.SEPARATION],
              }]
              : []),


            // ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
            //   {
            //     key: "Attendance Regularization",
            //     path: `/${companySlug}/employee-attendence-regularization`,
            //     // icon: <AttendanceIcon />,
            //     title: "sidebar.Attendence_Regularization",
            //     submenu: false,
            //     permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
            //   }] : [],

            ...(getResource(config.RESOURCES.TICKET, ["read"])
              ? [{
                key: "tickets",
                path: `/${companySlug}/tickets`,
                // icon: <ImTicket />,
                title: "sidebar.tickets",
                permission: [config.RESOURCES.TICKET],
              }]
              : []),

            ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])
              ? [{
                key: "assets",
                path: `/${companySlug}/user-assets`,
                // icon: <AssetsIcon />,
                title: "sidebar.user-assets",
                permission: [config.RESOURCES.MY_ASSETS],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])
              ? [{
                key: "user-reports",
                path: `/${companySlug}/user-reports`,
                // icon: <ReportsIcon />,
                title: "sidebar.user-reports",
                permission: [config.RESOURCES.ATTENDANCE_REPORT],
              }]
              : []),


            ...(getResource(config.RESOURCES.RANKING, ["read"])
              ? [{
                key: "individual-ranking",
                path: `/${companySlug}/individual-rank`,
                // icon: <RankingIcon />,
                title: "sidebar.individual-rank",
                permission: [config.RESOURCES.RANKING],
              }]
              : []),

            // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])
            //   ? [{
            //     key: "goals",
            //     path: `/${companySlug}/my-goals`,
            //     // icon: <GiOnTarget />,
            //     title: "sidebar.create-goals",
            //     permission: [config.RESOURCES.MY_GOAL],
            //   }]
            //   : []),

            // ...(getResource(config.RESOURCES.AUDITS, ["read"])
            //   ? [{
            //     key: "audits",
            //     path: `/${companySlug}/my-audit-history`,
            //     // icon: <FaFileInvoice />,
            //     title: "Audits",
            //     permission: [config.RESOURCES.AUDITS],
            //   }]
            //   : []),
            ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
              {
                key: "Bill Reimbursement",
                path: `/${companySlug}/bill-reimbursement`,
                title: "sidebar.bill-reimbursement",
                submenu: false,
                permission: [config.RESOURCES.BILLREIMBURSEMENT],
              }] : [],

          ],
        },] : []),

    ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
      ? [
        {
          icon: <TeamInfoIcon />,
          className: "main-submenu",
          key: "sub16",
          title: "Team",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [
              {
                key: "employee-master",
                path: `/${companySlug}/employee-master`,
                title: "sidebar.employee-master",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_MASTER],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [
              {
                key: "employee-in-out",
                path: `/${companySlug}/employee-in-out`,
                title: "sidebar.Employee-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
            ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [
              {
                key: "resignation-employee-list",
                path: `/${companySlug}/resignation-employee-list`,
                title: "sidebar.resignation-employee-list",
                submenu: false,
                permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
              ? [{
                key: "last-working-day-emps",
                path: `/${companySlug}/last-working-day-emps`,
                // icon: <SeparationIcon />,
                title: "Last Working Day List",
                permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
              }]
              : []),
            // Milestone review forms - the HR/Admin tracker.
            //
            // Gated on the ROLE as well as the permission, because the server
            // gates this screen on role (`requireAdminOrHr`) and not on the
            // resource. Keying the menu off `create` alone would put a dead
            // link in front of anyone who was granted `create` without being an
            // Admin or HR - which is easy to do from the permission manager.
            ...((getRoles(["Admin"]) || getRoles(["HR"])) &&
              getResource(config.RESOURCES.REVIEW_FORMS, ["create"])
              ? [{
                key: "review-forms",
                path: `/${companySlug}/review-forms`,
                title: "Review Forms",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            // The approvals queue. Gated on `update`, which Reporting Managers
            // and ARMs hold as well - they are the people who actually decide.
            // The screen shows an explicit empty state to anyone with no
            // reports, so a stray viewer sees nothing rather than an error.
            ...(getResource(config.RESOURCES.REVIEW_FORMS, ["update"])
              ? [{
                key: "review-form-approvals",
                path: `/${companySlug}/review-form-approvals`,
                title: "Review Form Approvals",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [
              {
                key: "admin-reports",
                path: `/${companySlug}/admin-reports`,
                title: "sidebar.admin-reports",
                submenu: false,
                // Reports.js already renders one card per report family the caller may
                // read, so reaching the hub must not hinge on EMPLOYEE_REPORT alone -
                // a role granted only LEAVE_REPORT (e.g. an employee who may see their
                // own Leave Balance History) could otherwise never navigate here.
                permission: [
                  config.RESOURCES.EMPLOYEE_REPORT,
                  config.RESOURCES.LEAVE_REPORT,
                  config.RESOURCES.ATTENDANCE_REPORT,
                ],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [
              {
                key: "tickets",
                path: `/${companySlug}/employee-tickets`,
                title: "sidebar.tickets",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_TICKET],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "admin-leave-approval",
                path: `/${companySlug}/admin-leave-approval`,
                title: "sidebar.admin-leave-management",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "user-leave-approval",
                path: `/${companySlug}/user-leave-approve`,
                title: "sidebar.user-leave-management",
                submenu: false,
                permission: [config.RESOURCES.LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [
              {
                key: "admin-regularizatrion",
                path: `/${companySlug}/admin-regularizatrion`,
                title: "sidebar.admin-regularizatrion",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [
              {
                key: "ManageTeamRegularization",
                path: `/${companySlug}/manage-team-regularization`,
                title: "sidebar.ManageTeamRegularization",
                submenu: false,
                permission: [config.RESOURCES.TEAM_REGULARIZATION],
              }] : [],
            // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [
            //   {
            //     key: "Kpis",
            //     path: `/${companySlug}/Kpis`,
            //     title: "sidebar.manage-kpis",
            //     submenu: false,
            //     permission: [config.RESOURCES.KPI],
            //   }] : [],

          ],
        },
      ]
      : []),


    // ...(getResource(config.RESOURCES.MANAGE_TICKET, ["read"])) ? [
    //   {
    //     key: "managetickets",
    //     path: `/${companySlug}/manage-tickets`,
    //     icon: <BsFillTicketDetailedFill />,
    //     title: "sidebar.manage-tickets",
    //     submenu: false,
    //     permission: [config.RESOURCES.MANAGE_TICKET],
    //   }] : [],


    // {
    //   key: "appsettings",
    //   path: `/${companySlug}/app-setting`,
    //   icon: <FaRegSun />,
    //   title: "sidebar.app-setting",
    //   submenu: false,
    //   permission: [config.RESOURCES.SETTINGS],
    // },
    // {
    //   key: "employee-in-out",
    //   path: `/${companySlug}/employee-in-out`,
    //   icon: <InOuticon />,
    //   title: "sidebar.Employee-In-Out",
    //   submenu: false,
    //   permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
    // },


    // ...(getResource(config.RESOURCES.WAFFLE, ["read"])
    //   ? [
    //     {
    //       icon: <i className="fas fa-stroopwafel " />,
    //       className: " main-submenu",
    //       key: "sub17",
    //       title: "Waffle",
    //       submenu: true,
    //       subMenuInfo: [
    //         {
    //           key: "karma-send",
    //           path: `/${companySlug}/Waffle`,
    //           title: "sidebar.karmaSend",
    //           submenu: false,
    //           permission: [config.RESOURCES.WAFFLE],
    //         },
    //         {
    //           key: "karma-weightage",
    //           path: `/${companySlug}/Waffle-weightage`,
    //           title: "sidebar.karmaWeightage",
    //           submenu: false,
    //           permission: [config.RESOURCES.WAFFLE_WEIGHTAGE],
    //         },
    //         ...(getResource(config.RESOURCES.WAFFLE, ["create"])
    //           ? [
    //             {
    //               key: "karma-karmaCategory",
    //               path: `/${companySlug}/Waffle-category`,
    //               title: "sidebar.karmaCategory",
    //               submenu: false,
    //               permission: [config.RESOURCES.WAFFLE],
    //             },
    //           ]
    //           : []),
    //         ...(getResource(config.RESOURCES.WAFFLE_REWARD, ["read"])
    //           ? [
    //             {
    //               key: "karma-reward-approvel",
    //               path: `/${companySlug}/Waffle-reward-approvel`,
    //               title: "sidebar.karmaRewardApprovel",
    //               submenu: false,
    //               permission: [config.RESOURCES.WAFFLE_REWARD],
    //             },
    //           ]
    //           : []),
    //       ],
    //     },
    //   ]
    //   : []),
    // ✅ "Onboarding" submenu - Same structure as Admin
    ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),
    ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),

    ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
      getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"]) ||
      getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"]) ||
      getResource(config.RESOURCES.NEW_JOINEES, ["read"])
      ? [
        {
          icon: <OnboardingIcon />,
          className: "main-submenu",
          key: "sub19",
          title: "Onboarding",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "company-doc-master",
                path: `/${companySlug}/company-doc-master`,
                title: "sidebar.docMaster",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-approvel",
                path: `/${companySlug}/candidate-approval`,
                title: "sidebar.candidate-approvel",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-list",
                path: `/${companySlug}/candidate-list`,
                title: "sidebar.candidate-approvel-list",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "initiation",
                path: `/${companySlug}/onboarding-initiation`,
                title: "sidebar.onboarding-initiation",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [
              {
                key: "doc-approve-list",
                path: `/${companySlug}/doc-approve-list`,
                title: "sidebar.doc-approve-list",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
              }] : [],
            ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [
              {
                key: "new-joinees-list",
                path: `/${companySlug}/new-joinees-list`,
                title: "sidebar.new-joinees-list",
                submenu: false,
                permission: [config.RESOURCES.NEW_JOINEES],
              }] : [],
          ],
        },
      ]
      : []),
    // ✅ "Audit" submenu - Same structure as Admin
    // ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"]) ||
    //   getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"]) ||
    //   getResource(config.RESOURCES.AUDIT_HEAD, ["read"])
    //   ? [
    //     {
    //       icon: <AuditIcon />,
    //       className: "main-submenu",
    //       key: "sub20",
    //       title: "Audit",
    //       submenu: true,
    //       subMenuInfo: [
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audit Master",
    //             path: `/${companySlug}/auditmaster`,
    //             title: "Audits Master",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audits",
    //             path: `/${companySlug}/audits`,
    //             title: "Audits",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [
    //           {
    //             key: "auditperformance",
    //             path: `/${companySlug}/auditperformance`,
    //             title: "Audit Performance",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDITS_PERFORMANCE],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [
    //           {
    //             key: "auditHead",
    //             path: `/${companySlug}/auditHead`,
    //             title: "Audit Head",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_HEAD],
    //           }] : [],
    //       ],
    //     },
    //   ]
    //   : []),


    ...(getResource(config.RESOURCES["KPI"], ["read"])) ?
      [{
        key: "Kpis",
        path: `/${companySlug}/user-kpi`,
        icon: <FundOutlined />,
        title: "sidebar.kpis",
        submenu: false,
        permission: [],
      }] : [],

    // ✅ "Survey" submenu - Same structure as Admin
    ...((getResource(config.RESOURCES["SURVEY"], ["read"]) ||
      getResource(config.RESOURCES["360QUE"], ["read"]) ||
      getResource(config.RESOURCES["360REVW"], ["read"]))
      ? [
        {
          icon: <i className="fas fa-poll" />,
          className: "main-submenu",
          key: "sub22",
          title: "Survey",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES["SURVEY"], ["read"])
              ? [
                {
                  key: "360_survey",
                  path: `/${companySlug}/360-survey`,
                  title: "sidebar.360_survey",
                  submenu: false,
                  permission: [config.RESOURCES["SURVEY"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360QUE"], ["read"])
              ? [
                {
                  key: "360_questionnaire",
                  path: `/${companySlug}/360-questionnaire`,
                  title: "sidebar.360_questionnaire",
                  submenu: false,
                  permission: [config.RESOURCES["360QUE"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360REVW"], ["read"])
              ? [
                {
                  key: "360_reviews",
                  path: `/${companySlug}/360-reviews`,
                  title: "sidebar.360_reviews",
                  submenu: false,
                  permission: [config.RESOURCES["360REVW"]],
                },
              ]
              : []),
          ],
        },
      ]
      : []),
    {
      icon: <i className="fas fa-ellipsis-h" />,
      className: " main-submenu",
      key: "sub18",
      title: "More",
      submenu: true,
      subMenuInfo: [


        // ...(getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"])) ? [
        //   {
        //     key: "employee-goals",
        //     path: `/${companySlug}/employee-goals`,
        //     title: "sidebar.employee-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.EMPLOYEE_GOAL],
        //   }] : [],
        // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [
        //   {
        //     key: "team-goals",
        //     path: `/${companySlug}/team-goals`,
        //     title: "sidebar.team-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.TEAM_GOAL],
        //   }] : [],
        ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [
          {
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],

        ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [
          {
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],

        ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [
          {
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
        {
          key: "organization-policy",
          path: `/${companySlug}/organization-policy`,
          title: "sidebar.organization-policies",
          submenu: false,
          permission: [], // Always shown
        },
        ...(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"])) ? [
          {
            key: "Bill Reimbursement Approval",
            path: `/${companySlug}/admin-bill-reimbursement/submit`,
            title: "sidebar.admin-bill-reimbursement",
            submenu: false,
            permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL],
          },] : [],
      ],
    },
    // ─── LMS (Learning Management) sidebar ────────────────────────────
    ...((
      getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ||
      getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ||
      getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_REPORTS"], ["read"])
    ) ? [{
      icon: <i className="fas fa-graduation-cap" />,
      className: "main-submenu",
      key: "lms_module_sidebar",
      title: "Learning",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ? [{ key: "lms_courses_sidebar", path: `/${companySlug}/lms/courses`, title: "Course Management", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_assessments_sidebar", path: `/${companySlug}/lms/assessments`, title: "Assessments", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ? [{ key: "lms_enrollments_sidebar", path: `/${companySlug}/lms/enrollments`, title: "Enrollments", submenu: false, permission: [config.RESOURCES["LMS_ENROLLMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ? [{ key: "lms_my_learning_sidebar", path: `/${companySlug}/lms/my-learning`, title: "My Learning", submenu: false, permission: [config.RESOURCES["LMS_MY_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ? [{ key: "lms_certificates_sidebar", path: `/${companySlug}/lms/certificates`, title: "Certificates", submenu: false, permission: [config.RESOURCES["LMS_CERTIFICATES"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ? [{ key: "lms_learning_paths_sidebar", path: `/${companySlug}/lms/learning-paths`, title: "Learning Paths", submenu: false, permission: [config.RESOURCES["LMS_LEARNING_PATH"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ? [{ key: "lms_auto_assignment_sidebar", path: `/${companySlug}/lms/auto-assignment`, title: "Auto-Assignment", submenu: false, permission: [config.RESOURCES["LMS_AUTO_ASSIGNMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ? [{ key: "lms_team_learning_sidebar", path: `/${companySlug}/lms/team-learning`, title: "Team Learning", submenu: false, permission: [config.RESOURCES["LMS_TEAM_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_reports_sidebar", path: `/${companySlug}/lms/reports`, title: "LMS Reports", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_grading_sidebar", path: `/${companySlug}/lms/grading`, title: "Manual Grading", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_data_retention_sidebar", path: `/${companySlug}/lms/settings/data-retention`, title: "Data Retention", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["update"]) ? [{ key: "lms_cert_template_sidebar", path: `/${companySlug}/lms/settings/certificate-template`, title: "Certificate Template", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
      ],
    }] : []),
    ...(getResource(config.RESOURCES.SKILL, ["read"]) ||
      getResource(config.RESOURCES.SKILLS_MATRIX, ["read"]) ||
      getResource(config.RESOURCES.SKILL_INVENTORY, ["read"]) ||
      getResource(config.RESOURCES.CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_VIEW, ["read"]) ||
      getResource(config.RESOURCES.CAREER_READINESS, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_COMPETENCY, ["read"]) ||
      getResource(config.RESOURCES.COURSE_SKILL_MAPPING, ["read"]) ? [{
      icon: <i className="fas fa-user-graduate" />,
      key: "talent_development_sidebar",
      title: "Talent Development",
      path: `/${companySlug}/talent-development`,
      submenu: false,
      permission: [config.RESOURCES.SKILL, config.RESOURCES.CAREER_PATH],
    }] : []),


  ];

  const HR = [
    {
      key: "User Dashboard",
      path: `/${companySlug}/dashboard`,
      icon: <AiOutlineDashboard />,
      title: "sidebar.User_Dashboard",
      submenu: false,
      permission: [],
    },

    // ✅ "Me" submenu
    ...(getResource(config.RESOURCES.LEAVE, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION, ["read"]) ||
      getResource(config.RESOURCES.TICKET, ["read"]) ||
      getResource(config.RESOURCES.MY_ASSETS, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.RANKING, ["read"]) ||
      getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
      // getResource(config.RESOURCES.AUDITS, ["read"]) ||
      getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])

      ? [
        {
          key: "Me",
          title: "Me",
          className: " main-submenu",
          icon: <i className="fas fa-user " />,
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
              {
                key: "My-In-Out",
                path: `/${companySlug}/my-in-out`,
                title: "sidebar.My-In-Out",
                submenu: false,
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE, ["read"])
              ? [{
                key: "manage-leave-application",
                path: `/${companySlug}/leave-application`,
                // icon: <LeaveIcon />,
                title: "sidebar.leave-application",
                permission: [config.RESOURCES.LEAVE],
              }]
              : []),

            ...(getResource(config.RESOURCES.SHIFT_SWAP_APPLICATION, ["read"])
              ? [{
                key: "shift-change-requests",
                path: `/${companySlug}/shift-change-requests`,
                title: "sidebar.shift-change-requests",
                permission: [config.RESOURCES.SHIFT_SWAP_APPLICATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])
              ? [{
                key: "attendance-regularization",
                path: `/${companySlug}/employee-attendence-regularization`,
                // icon: <AttendanceIcon />,
                title: "sidebar.Attendence_Regularization",
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.SEPARATION, ["read"])
              ? [{
                key: "resignation",
                path: `/${companySlug}/resignation`,
                // icon: <SeparationIcon />,
                title: "sidebar.resignation",
                permission: [config.RESOURCES.SEPARATION],
              }]
              : []),


            // ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
            //   {
            //     key: "Attendance Regularization",
            //     path: `/${companySlug}/employee-attendence-regularization`,
            //     // icon: <AttendanceIcon />,
            //     title: "sidebar.Attendence_Regularization",
            //     submenu: false,
            //     permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
            //   }] : [],

            ...(getResource(config.RESOURCES.TICKET, ["read"])
              ? [{
                key: "tickets",
                path: `/${companySlug}/tickets`,
                // icon: <ImTicket />,
                title: "sidebar.tickets",
                permission: [config.RESOURCES.TICKET],
              }]
              : []),

            ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])
              ? [{
                key: "assets",
                path: `/${companySlug}/user-assets`,
                // icon: <AssetsIcon />,
                title: "sidebar.user-assets",
                permission: [config.RESOURCES.MY_ASSETS],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])
              ? [{
                key: "user-reports",
                path: `/${companySlug}/user-reports`,
                // icon: <ReportsIcon />,
                title: "sidebar.user-reports",
                permission: [config.RESOURCES.ATTENDANCE_REPORT],
              }]
              : []),


            ...(getResource(config.RESOURCES.RANKING, ["read"])
              ? [{
                key: "individual-ranking",
                path: `/${companySlug}/individual-rank`,
                // icon: <RankingIcon />,
                title: "sidebar.individual-rank",
                permission: [config.RESOURCES.RANKING],
              }]
              : []),

            // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])
            //   ? [{
            //     key: "goals",
            //     path: `/${companySlug}/my-goals`,
            //     // icon: <GiOnTarget />,
            //     title: "sidebar.create-goals",
            //     permission: [config.RESOURCES.MY_GOAL],
            //   }]
            //   : []),

            // ...(getResource(config.RESOURCES.AUDITS, ["read"])
            //   ? [{
            //     key: "audits",
            //     path: `/${companySlug}/my-audit-history`,
            //     // icon: <FaFileInvoice />,
            //     title: "Audits",
            //     permission: [config.RESOURCES.AUDITS],
            //   }]
            //   : []),
            ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
              {
                key: "Bill Reimbursement",
                path: `/${companySlug}/bill-reimbursement`,
                title: "sidebar.bill-reimbursement",
                submenu: false,
                permission: [config.RESOURCES.BILLREIMBURSEMENT],
              }] : [],

          ],
        },] : []),

    ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.KPI, ["read"]) ||
      getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
      ? [
        {
          icon: <TeamInfoIcon />,
          className: "main-submenu",
          key: "sub16",
          title: "Team",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [
              {
                key: "employee-master",
                path: `/${companySlug}/employee-master`,
                title: "sidebar.employee-master",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_MASTER],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [
              {
                key: "employee-in-out",
                path: `/${companySlug}/employee-in-out`,
                title: "sidebar.Employee-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
            ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [
              {
                key: "resignation-employee-list",
                path: `/${companySlug}/resignation-employee-list`,
                title: "sidebar.resignation-employee-list",
                submenu: false,
                permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
              ? [{
                key: "last-working-day-emps",
                path: `/${companySlug}/last-working-day-emps`,
                // icon: <SeparationIcon />,
                title: "Last Working Day List",
                permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
              }]
              : []),
            // Milestone review forms - the HR/Admin tracker.
            //
            // Gated on the ROLE as well as the permission, because the server
            // gates this screen on role (`requireAdminOrHr`) and not on the
            // resource. Keying the menu off `create` alone would put a dead
            // link in front of anyone who was granted `create` without being an
            // Admin or HR - which is easy to do from the permission manager.
            ...((getRoles(["Admin"]) || getRoles(["HR"])) &&
              getResource(config.RESOURCES.REVIEW_FORMS, ["create"])
              ? [{
                key: "review-forms",
                path: `/${companySlug}/review-forms`,
                title: "Review Forms",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            // The approvals queue. Gated on `update`, which Reporting Managers
            // and ARMs hold as well - they are the people who actually decide.
            // The screen shows an explicit empty state to anyone with no
            // reports, so a stray viewer sees nothing rather than an error.
            ...(getResource(config.RESOURCES.REVIEW_FORMS, ["update"])
              ? [{
                key: "review-form-approvals",
                path: `/${companySlug}/review-form-approvals`,
                title: "Review Form Approvals",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [
              {
                key: "admin-reports",
                path: `/${companySlug}/admin-reports`,
                title: "sidebar.admin-reports",
                submenu: false,
                // Reports.js already renders one card per report family the caller may
                // read, so reaching the hub must not hinge on EMPLOYEE_REPORT alone -
                // a role granted only LEAVE_REPORT (e.g. an employee who may see their
                // own Leave Balance History) could otherwise never navigate here.
                permission: [
                  config.RESOURCES.EMPLOYEE_REPORT,
                  config.RESOURCES.LEAVE_REPORT,
                  config.RESOURCES.ATTENDANCE_REPORT,
                ],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [
              {
                key: "tickets",
                path: `/${companySlug}/employee-tickets`,
                title: "sidebar.tickets",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_TICKET],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "admin-leave-approval",
                path: `/${companySlug}/admin-leave-approval`,
                title: "sidebar.admin-leave-management",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "user-leave-approval",
                path: `/${companySlug}/user-leave-approve`,
                title: "sidebar.user-leave-management",
                submenu: false,
                permission: [config.RESOURCES.LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [
              {
                key: "admin-regularizatrion",
                path: `/${companySlug}/admin-regularizatrion`,
                title: "sidebar.admin-regularizatrion",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [
              {
                key: "ManageTeamRegularization",
                path: `/${companySlug}/manage-team-regularization`,
                title: "sidebar.ManageTeamRegularization",
                submenu: false,
                permission: [config.RESOURCES.TEAM_REGULARIZATION],
              }] : [],
            // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [
            //   {
            //     key: "Kpis",
            //     path: `/${companySlug}/Kpis`,
            //     title: "sidebar.manage-kpis",
            //     submenu: false,
            //     permission: [config.RESOURCES.KPI],
            //   }] : [],

          ],
        },
      ]
      : []),
    ...(getResource(config.RESOURCES.ACHIEVEMENTS, ["read"]) ||
      getResource(config.RESOURCES.SETTINGS, ["read"]) ||
      getResource(config.RESOURCES.DEPARTMENTS, ["read"]) ||
      getResource(config.RESOURCES.DESIGNATION, ["read"]) ||
      getResource(config.RESOURCES.HOLIDAYS, ["read"]) ||
      getResource(config.RESOURCES.SHIFT, ["read"])
      ? [
        {
          icon: <PermissionmanagerIcon />,

          className: "main-submenu",
          key: "sub17",
          title: "Management",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ACHIEVEMENTS, ["read"])) ? [
              {
                key: "achivements",
                path: `/${companySlug}/achivements`,
                title: "sidebar.achivements",
                submenu: false,
                permission: [config.RESOURCES.ACHIEVEMENTS],
              }] : [],
            ...(getResource(config.RESOURCES.SETTINGS, ["read"])) ? [
              {
                key: "permission",
                path: `/${companySlug}/permission-manager`,
                title: "sidebar.permission",
                submenu: false,
                permission: [config.RESOURCES.SETTINGS],
              }] : [],
            ...(getResourceRaw(config.RESOURCES.SETTINGS, ["read"]) &&
              (sidebarModuleAccess.size === 0 || sidebarModuleAccess.has("zoho_payroll_integration"))) ? [
              {
                key: "zoho-payroll-integration",
                path: `/${companySlug}/admin/zoho-payroll-integration`,
                title: "Zoho Payroll Integration",
                submenu: false,
                permission: [config.RESOURCES.SETTINGS],
              }] : [],
            ...(getResource(config.RESOURCES.DEPARTMENTS, ["read"])) ? [
              {
                key: "admin-department",
                path: `/${companySlug}/admin-department`,
                title: "sidebar.Department",
                submenu: false,
                permission: [config.RESOURCES.DEPARTMENTS],
              }] : [],
            ...(getResource(config.RESOURCES.DESIGNATION, ["read"])) ? [
              {
                key: "admin-designation",
                path: `/${companySlug}/admin-designation`,
                title: "sidebar.designation",
                submenu: false,
                permission: [config.RESOURCES.DESIGNATION],
              }] : [],
            ...(getResource(config.RESOURCES.SHIFT, ["read"])) ? [
              {
                key: "admin-shift",
                path: `/${companySlug}/admin-shift`,
                title: "sidebar.shift",
                submenu: false,
                permission: [config.RESOURCES.SHIFT],
              }] : [],
            ...(getResource(config.RESOURCES.BUSINESS_UNIT, ["read"])) ? [
              {
                key: "admin-businessunit",
                path: `/${companySlug}/admin-businessunit`,
                // icon: <FaSitemap />,
                title: "sidebar.businessunit",
                submenu: false,
                permission: [config.RESOURCES.BUSINESS_UNIT]
              }] : [],
            ...(getResource(config.RESOURCES.SUBDEPARTMENT, ["read"])) ? [
              {
                key: "admin-subdepartment",
                path: `/${companySlug}/admin-subdepartment`,
                title: "sidebar.subdepartment",
                submenu: false,
                permission: [config.RESOURCES.SUBDEPARTMENT],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEMENT_TYPE, ["read"])) ? [
              {
                key: "admin-employment-type",
                path: `/${companySlug}/admin-employment-type`,
                title: "Employment Type",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEMENT_TYPE],
              }] : [],
            ...(getResource(config.RESOURCES.HOLIDAYS, ["read"])) ? [
              {
                key: "holidays",
                path: `/${companySlug}/holidays`,
                title: "sidebar.holidays",
                submenu: false,
                permission: [config.RESOURCES.HOLIDAYS],
              }] : [],
            ...(getResource(config.RESOURCES.SHIFT, ["read"])) ? [
              {
                key: "admin-roster",
                path: `/${companySlug}/admin-roster`,
                title: "sidebar.roster",
                submenu: false,
                permission: [config.RESOURCES.SHIFT],
              }
            ]
              : [],
          ],
        },
      ]
      : []),

    ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),
    ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),

    ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
      getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"]) ||
      getResource(config.RESOURCES.NEW_JOINEES, ["read"])
      ? [
        {
          icon: <OnboardingIcon />,

          className: " main-submenu",
          key: "sub19",
          title: "Onboarding",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "company-doc-master",
                path: `/${companySlug}/company-doc-master`,
                title: "sidebar.docMaster",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-approvel",
                path: `/${companySlug}/candidate-approval`,
                title: "sidebar.candidate-approvel",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-list",
                path: `/${companySlug}/candidate-list`,
                title: "sidebar.candidate-approvel-list",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "initiation",
                path: `/${companySlug}/onboarding-initiation`,
                title: "sidebar.onboarding-initiation",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [
              {
                key: "doc-approve-list",
                path: `/${companySlug}/doc-approve-list`,
                title: "sidebar.doc-approve-list",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
              }] : [],
            ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [
              {
                key: "new-joinees-list",
                path: `/${companySlug}/new-joinees-list`,
                title: "sidebar.new-joinees-list",
                submenu: false,
                permission: [config.RESOURCES.NEW_JOINEES],
              }] : [],
          ],
        },
      ]
      : []),

    // ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"]) ||
    //   getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"]) ||
    //   getResource(config.RESOURCES.AUDIT_HEAD, ["read"])
    //   ? [
    //     {
    //       icon: <AuditIcon />,
    //       className: "main-submenu",
    //       key: "sub20",
    //       title: "Audit",
    //       submenu: true,
    //       subMenuInfo: [
    //         ...(getResource(config.RESOURCES.AUDITS, ["read"])) ? [
    //           {
    //             key: "Audit",
    //             path: `/${companySlug}/audit-history`,
    //             title: "Audit History",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDITS],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audit Master",
    //             path: `/${companySlug}/auditmaster`,
    //             title: "Audits Master",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audits",
    //             path: `/${companySlug}/audits`,
    //             title: "Audits",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [
    //           {
    //             key: "auditperformance",
    //             path: `/${companySlug}/auditperformance`,
    //             title: "Audit Performance",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDITS_PERFORMANCE],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [
    //           {
    //             key: "auditHead",
    //             path: `/${companySlug}/auditHead`,
    //             title: "Audit Head",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_HEAD],
    //           }] : [],
    //       ],
    //     },
    //   ]
    //   : []),

    {
      icon: <i className="fas fa-ellipsis-h" />,
      className: " main-submenu",
      key: "sub18",
      title: "More",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
          {
            key: "penalty-revoke",
            path: `/${companySlug}/penalty-revoke`,
            title: "sidebar.penalty-revoke",
            submenu: false,
            permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
          }] : [],
        ...(getResource(config.RESOURCES.POLICIES, ["read"])) ? [
          {
            key: "policies",
            path: `/${companySlug}/policies`,
            title: "sidebar.policies",
            submenu: false,
            permission: [config.RESOURCES.POLICIES],
          }] : [],
        // ...(getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"])) ? [{
        //   key: "employee-goals",
        //   path: `/${companySlug}/employee-goals`,
        //   title: "sidebar.employee-goals",
        //   submenu: false,
        //   permission: [config.RESOURCES.EMPLOYEE_GOAL],
        // }] : [],
        // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [
        //   {
        //     key: "team-goals",
        //     path: `/${companySlug}/team-goals`,
        //     title: "sidebar.team-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.TEAM_GOAL],
        //   }] : [],
        ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [
          {
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
        ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [
          {
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],
        ...(getResource(config.RESOURCES.LEAVE_MASTER, ["read"])) ? [
          {
            key: "leave-master",
            path: `/${companySlug}/admin-leave-master`,
            title: "sidebar.admin-leave-master",
            submenu: false,
            permission: [config.RESOURCES.LEAVE_MASTER],
          }] : [],
        ...(getResource(config.RESOURCES.MANAGE_LEAVE_BALANCE, ["read"])) ? [
          {
            key: "employee-leave-balance",
            path: `/${companySlug}/employee-leave-balance`,
            title: "sidebar.employee-leave-balance",
            submenu: false,
            permission: [config.RESOURCES.MANAGE_LEAVE_BALANCE],
          }] : [],
        {
          key: "mood-analytics",
          path: `/${companySlug}/mood-list`,
          // icon: <i className="fas fa-stroopwafel" />,
          title: "Mood Analytics",
          submenu: false,
          permission: [],
        },
        ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [
          {
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
        {
          key: "organization-policy",
          path: `/${companySlug}/organization-policy`,
          title: "sidebar.organization-policies",
          submenu: false,
          permission: [], // Always shown
        },
        {
          key: "frs-management",
          path: `/${companySlug}/frs-management`,
          title: "Face Identity Vault",
          submenu: false,
          permission: [],
        },
        ...(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"])) ? [
          {
            key: "Bill Reimbursement Approval",
            path: `/${companySlug}/admin-bill-reimbursement/submit`,
            title: "sidebar.admin-bill-reimbursement",
            submenu: false,
            permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL],
          },] : [],
      ],
    },
    // ✅ "Survey" submenu - Same structure as Admin
    ...((getResource(config.RESOURCES["SURVEY"], ["read"]) ||
      getResource(config.RESOURCES["360QUE"], ["read"]) ||
      getResource(config.RESOURCES["360REVW"], ["read"]))
      ? [
        {
          icon: <i className="fas fa-poll" />,
          className: "main-submenu",
          key: "sub22",
          title: "Survey",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES["SURVEY"], ["read"])
              ? [
                {
                  key: "360_survey",
                  path: `/${companySlug}/360-survey`,
                  title: "sidebar.360_survey",
                  submenu: false,
                  permission: [config.RESOURCES["SURVEY"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360QUE"], ["read"])
              ? [
                {
                  key: "360_questionnaire",
                  path: `/${companySlug}/360-questionnaire`,
                  title: "sidebar.360_questionnaire",
                  submenu: false,
                  permission: [config.RESOURCES["360QUE"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360REVW"], ["read"])
              ? [
                {
                  key: "360_reviews",
                  path: `/${companySlug}/360-reviews`,
                  title: "sidebar.360_reviews",
                  submenu: false,
                  permission: [config.RESOURCES["360REVW"]],
                },
              ]
              : []),
          ],
        },
      ]
      : []),
    // ─── Engage — Announcements moved out of "More" ───────────────────
    {
      icon: <i className="fas fa-bullhorn" />,
      className: " main-submenu",
      key: "engage",
      title: "Engage",
      submenu: true,
      subMenuInfo: [
        {
          key: "engage-overview",
          path: `/${companySlug}/engage/overview`,
          title: "sidebar.engageOverview",
          submenu: false,
          permission: [config.RESOURCES.ENGAGE_PANEL],
        },
        {
          key: "engage-announcements",
          path: `/${companySlug}/engage/announcements`,
          title: "sidebar.announcement",
          submenu: false,
          permission: [],
        },
        {
          key: "engage-celebrations",
          path: `/${companySlug}/engage/celebrations`,
          title: "sidebar.celebrations",
          submenu: false,
          permission: [],
        },
        {
          key: "engage-awards",
          path: `/${companySlug}/engage/awards`,
          title: "sidebar.awards",
          submenu: false,
          permission: [config.RESOURCES.ENGAGE_PANEL],
        },
        {
          key: "engage-badges",
          path: `/${companySlug}/engage/badges`,
          title: "sidebar.badges",
          submenu: false,
          permission: [config.RESOURCES.ENGAGE_PANEL],
        },
        {
          key: "engage-wall",
          path: `/${companySlug}/engage/wall`,
          title: "sidebar.engageWall",
          submenu: false,
          permission: [config.RESOURCES.ENGAGE_WALL],
        },
        {
          key: "engage-moderation",
          path: `/${companySlug}/engage/moderation`,
          title: "sidebar.engageModeration",
          submenu: false,
          permission: [config.RESOURCES.ENGAGE_PANEL],
        },
        {
          key: "engage-settings",
          path: `/${companySlug}/engage/settings`,
          title: "sidebar.engageSettings",
          submenu: false,
          permission: [config.RESOURCES.ENGAGE_PANEL],
        },
      ],
    },
    // ─── LMS (Learning Management) sidebar ────────────────────────────
    ...((
      getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ||
      getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ||
      getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_REPORTS"], ["read"])
    ) ? [{
      icon: <i className="fas fa-graduation-cap" />,
      className: "main-submenu",
      key: "lms_module_sidebar",
      title: "Learning",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ? [{ key: "lms_courses_sidebar", path: `/${companySlug}/lms/courses`, title: "Course Management", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_assessments_sidebar", path: `/${companySlug}/lms/assessments`, title: "Assessments", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ? [{ key: "lms_enrollments_sidebar", path: `/${companySlug}/lms/enrollments`, title: "Enrollments", submenu: false, permission: [config.RESOURCES["LMS_ENROLLMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ? [{ key: "lms_my_learning_sidebar", path: `/${companySlug}/lms/my-learning`, title: "My Learning", submenu: false, permission: [config.RESOURCES["LMS_MY_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ? [{ key: "lms_certificates_sidebar", path: `/${companySlug}/lms/certificates`, title: "Certificates", submenu: false, permission: [config.RESOURCES["LMS_CERTIFICATES"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ? [{ key: "lms_learning_paths_sidebar", path: `/${companySlug}/lms/learning-paths`, title: "Learning Paths", submenu: false, permission: [config.RESOURCES["LMS_LEARNING_PATH"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ? [{ key: "lms_auto_assignment_sidebar", path: `/${companySlug}/lms/auto-assignment`, title: "Auto-Assignment", submenu: false, permission: [config.RESOURCES["LMS_AUTO_ASSIGNMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ? [{ key: "lms_team_learning_sidebar", path: `/${companySlug}/lms/team-learning`, title: "Team Learning", submenu: false, permission: [config.RESOURCES["LMS_TEAM_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_reports_sidebar", path: `/${companySlug}/lms/reports`, title: "LMS Reports", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_grading_sidebar", path: `/${companySlug}/lms/grading`, title: "Manual Grading", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_data_retention_sidebar", path: `/${companySlug}/lms/settings/data-retention`, title: "Data Retention", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["update"]) ? [{ key: "lms_cert_template_sidebar", path: `/${companySlug}/lms/settings/certificate-template`, title: "Certificate Template", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
      ],
    }] : []),
    ...(getResource(config.RESOURCES.SKILL, ["read"]) ||
      getResource(config.RESOURCES.SKILLS_MATRIX, ["read"]) ||
      getResource(config.RESOURCES.SKILL_INVENTORY, ["read"]) ||
      getResource(config.RESOURCES.CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_VIEW, ["read"]) ||
      getResource(config.RESOURCES.CAREER_READINESS, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_COMPETENCY, ["read"]) ||
      getResource(config.RESOURCES.COURSE_SKILL_MAPPING, ["read"]) ? [{
      icon: <i className="fas fa-user-graduate" />,
      key: "talent_development_sidebar",
      title: "Talent Development",
      path: `/${companySlug}/talent-development`,
      submenu: false,
      permission: [config.RESOURCES.SKILL, config.RESOURCES.CAREER_PATH],
    }] : []),


  ];

  // const Intern = [
  //   {
  //     key: "User Dashboard",
  //     path: `/${companySlug}/dashboard`,
  //     icon: <AiOutlineDashboard />,
  //     title: "sidebar.User_Dashboard",
  //     submenu: false,
  //     permission: [],
  //   },
  //   ...(getResource(config.RESOURCES.LEAVE, ["read"])) ? [
  //     {
  //       key: "manage-leave-application",
  //       path: `/${companySlug}/leave-application`,
  //       icon: <LeaveIcon />,
  //       title: "sidebar.leave-application",
  //       submenu: false,
  //       permission: [config.RESOURCES.LEAVE],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.SEPARATION, ["read"])) ? [
  //     {
  //       key: "resignation",
  //       path: `/${companySlug}/resignation`,
  //       icon: <SeparationIcon />,
  //       title: "sidebar.resignation",
  //       submenu: false,
  //       permission: [config.RESOURCES.SEPARATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])) ? [
  //     {
  //       key: "last-working-day-emps",
  //       path: `/${companySlug}/last-working-day-emps`,
  //       icon: <SeparationIcon />,
  //       title: "Last Working Day List",
  //       submenu: false,
  //       permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
  //     {
  //       key: "Attendance Regularization",
  //       path: `/${companySlug}/employee-attendence-regularization`,
  //       icon: <AttendanceIcon />,
  //       title: "sidebar.Attendence_Regularization",
  //       submenu: false,
  //       permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.TICKET, ["read"])) ? [
  //     {
  //       key: "tickets",
  //       path: `/${companySlug}/tickets`,
  //       icon: <ImTicket />,
  //       title: "sidebar.tickets",
  //       submenu: false,
  //       permission: [config.RESOURCES.TICKET],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])) ? [
  //     {
  //       key: "assets",
  //       path: `/${companySlug}/user-assets`,
  //       icon: <AssetsIcon />,
  //       title: "sidebar.user-assets",
  //       submenu: false,
  //       permission: [config.RESOURCES.MY_ASSETS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [
  //     {
  //       key: "user-reports",
  //       path: `/${companySlug}/user-reports`,
  //       icon: <ReportsIcon />,
  //       title: "sidebar.user-reports",
  //       submenu: false,
  //       permission: [config.RESOURCES.ATTENDANCE_REPORT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"])) ? [
  //     {
  //       key: "admin-reports",
  //       path: `/${companySlug}/admin-reports`,
  //       icon: <ReportsIcon />,
  //       title: "sidebar.admin-reports",
  //       submenu: false,
  //       permission: [config.RESOURCES.EMPLOYEE_REPORT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.RANKING, ["read"])) ? [
  //     {
  //       key: "individual-ranking",
  //       path: `/${companySlug}/individual-rank`,
  //       icon: <RankingIcon />,
  //       title: "sidebar.individual-rank",
  //       submenu: false,
  //       permission: [config.RESOURCES.RANKING],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.MY_GOAL, ["read"])) ? [
  //     {
  //       key: "goals",
  //       path: `/${companySlug}/my-goals`,
  //       icon: <GiOnTarget />,
  //       title: "sidebar.create-goals",
  //       submenu: false,
  //       permission: [config.RESOURCES.MY_GOAL],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [
  //     {
  //       key: "Job Opening",
  //       path: `/${companySlug}/jobs/openings`,
  //       icon: <JobopeningIcon />,
  //       title: "sidebar.admin-jobopening",
  //       submenu: false,
  //       permission: [config.RESOURCES.JOB_OPENING],
  //     }] : [],
  //   {
  //     key: "organization-policy",
  //     path: `/${companySlug}/organization-policy`,
  //     icon: <BsClipboard2CheckFill />,
  //     title: "sidebar.organization-policies",
  //     submenu: false,
  //     permission: [],
  //   },
  //   ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [
  //     {
  //       key: "inventory",
  //       path: `/${companySlug}/inventory`,
  //       icon: <InventoryIcon />,
  //       title: "sidebar.user-assetstabpane",
  //       submenu: false,
  //       permission: [config.RESOURCES.INVENTORY],
  //     }] : [],
  //   // ...(getResource(config.RESOURCES.MANAGE_TICKET, ["read"])) ? [
  //   //   {
  //   //     key: "managetickets",
  //   //     path: `/${companySlug}/manage-tickets`,
  //   //     icon: <BsFillTicketDetailedFill />,
  //   //     title: "sidebar.manage-tickets",
  //   //     submenu: false,
  //   //     permission: [config.RESOURCES.MANAGE_TICKET],
  //   //   }] : [],
  //   ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
  //     {
  //       key: "Audit Master",
  //       path: `/${companySlug}/auditmaster`,
  //       icon: <FaHouseUser />,
  //       title: "Audit Master",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDIT_MASTER],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.AUDITS, ["read"])) ? [
  //     {
  //       key: "Audits",
  //       path: `/${companySlug}/audit-history`,
  //       icon: <FaFileInvoice />,
  //       title: "Audits",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDITS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [
  //     {
  //       key: "auditperformance",
  //       path: `/${companySlug}/auditperformance`,
  //       icon: <AuditIcon />,
  //       title: "Audit Performance",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDITS_PERFORMANCE],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [
  //     {
  //       key: "auditHead",
  //       path: `/${companySlug}/auditHead`,
  //       icon: <AuditmasterIcon />,
  //       title: "Audit Head",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDIT_HEAD],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [
  //     {
  //       key: "employee-master",
  //       path: `/${companySlug}/employee-master`,
  //       icon: <EmployeesmasterIcon />,
  //       title: "sidebar.employee-master",
  //       submenu: false,
  //       permission: [config.RESOURCES.EMPLOYEE_MASTER],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [
  //     {
  //       key: "employee-in-out",
  //       path: `/${companySlug}/employee-in-out`,
  //       icon: <InOuticon />,
  //       title: "sidebar.Employee-In-Out",
  //       submenu: false,
  //       permission: [config.RESOURCES.EMPLOYEE_IN_OUT],


  //     }] : [],
  //   ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
  //     {
  //       key: "admin-leave-approval",
  //       path: `/${companySlug}/admin-leave-approval`,
  //       icon: <LeaveManagement />,
  //       title: "sidebar.admin-leave-management",
  //       submenu: false,
  //       permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.PENDING_TASK, ["read"])) ? [
  //     {
  //       key: "pendingtask",
  //       path: `/${companySlug}/pending-task-list`,
  //       icon: <MdOutlinePendingActions />,
  //       title: "sidebar.pending-task",
  //       submenu: false,
  //       permission: [config.RESOURCES.PENDING_TASK],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [
  //     {
  //       key: "admin-regularizatrion",
  //       path: `/${companySlug}/admin-regularizatrion`,
  //       icon: <FaUserCog />,
  //       title: "sidebar.admin-regularizatrion",
  //       submenu: false,
  //       permission: [config.RESOURCES.ADMIN_REGULARIZATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ACHIEVEMENTS, ["read"])) ? [

  //     {
  //       key: "achivements",
  //       path: `/${companySlug}/achivements`,
  //       icon: <AchivementIcon />,
  //       title: "sidebar.achivements",
  //       submenu: false,
  //       permission: [config.RESOURCES.ACHIEVEMENTS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.SHIFT, ["read"])) ? [
  //     {
  //       key: "admin-roster",
  //       path: `/${companySlug}/admin-roster`,
  //       icon: <FaClock />,
  //       title: "sidebar.roster",
  //       submenu: false,
  //       permission: [config.RESOURCES.SHIFT],
  //     }
  //   ]
  //     : [],
  //   ...(getResource(config.RESOURCES.SETTINGS, ["read"])) ? [
  //     {
  //       key: "permission",
  //       path: `/${companySlug}/permission-manager`,
  //       icon: <PermissionmanagerIcon />,
  //       title: "sidebar.permission",
  //       submenu: false,
  //       permission: [config.RESOURCES.SETTINGS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.DEPARTMENTS, ["read"])) ? [
  //     {
  //       key: "admin-department",
  //       path: `/${companySlug}/admin-department`,
  //       icon: <DepartmentIcon />,
  //       title: "sidebar.Department",
  //       submenu: false,
  //       permission: [config.RESOURCES.DEPARTMENTS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.DESIGNATION, ["read"])) ? [
  //     {
  //       key: "admin-designation",
  //       path: `/${companySlug}/admin-designation`,
  //       icon: <FaUserCog />,
  //       title: "sidebar.designation",
  //       submenu: false,
  //       permission: [config.RESOURCES.DESIGNATION],
  //     }] : [],

  //   ...(getResource(config.RESOURCES.SHIFT, ["read"])) ? [
  //     {
  //       key: "admin-shift",
  //       path: `/${companySlug}/admin-shift`,
  //       icon: <FaClock />,
  //       title: "sidebar.shift",
  //       submenu: false,
  //       permission: [config.RESOURCES.SHIFT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.BUSINESS_UNIT, ["read"])) ? [

  //     {
  //       key: "admin-businessunit",
  //       path: `/${companySlug}/admin-businessunit`,
  //       icon: <FaSitemap />,
  //       title: "sidebar.businessunit",
  //       submenu: false,
  //       permission: [config.RESOURCES.BUSINESS_UNIT]
  //     }] : [],
  //   ...(getResource(config.RESOURCES.SUBDEPARTMENT, ["read"])) ? [

  //     {
  //       key: "admin-subdepartment",
  //       path: `/${companySlug}/admin-subdepartment`,
  //       icon: <FaSitemap />,
  //       title: "sidebar.subdepartment",
  //       submenu: false,
  //       permission: [config.RESOURCES.SUBDEPARTMENT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.EMPLOYEMENT_TYPE, ["read"])) ? [
  //     {
  //       key: "admin-employment-type",
  //       path: `/${companySlug}/admin-employment-type`,
  //       icon: <FaSitemap />,
  //       title: "Employment Type",
  //       submenu: false,
  //       permission: [config.RESOURCES.EMPLOYEMENT_TYPE],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.LEAVE_MASTER, ["read"])) ? [

  //     {
  //       key: "leave-master",
  //       path: `/${companySlug}/admin-leave-master`,
  //       icon: <FaUserCog />,
  //       title: "sidebar.admin-leave-master",
  //       submenu: false,
  //       permission: [config.RESOURCES.LEAVE_MASTER],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.MANAGE_LEAVE_BALANCE, ["read"])) ? [
  //     {
  //       key: "employee-leave-balance",
  //       path: `/${companySlug}/employee-leave-balance`,
  //       icon: <FaHouseUser />,
  //       title: "sidebar.employee-leave-balance",
  //       submenu: false,
  //       permission: [config.RESOURCES.MANAGE_LEAVE_BALANCE],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
  //     {
  //       key: "initiation",
  //       path: `/${companySlug}/onboarding-initiation`,
  //       icon: <OnboardingIcon />,
  //       title: "sidebar.onboarding-initiation",
  //       submenu: false,
  //       permission: [config.RESOURCES.ON_BOARDING_INITIATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [
  //     {
  //       key: "doc-approve-list",
  //       path: `/${companySlug}/doc-approve-list`,
  //       icon: <FaRegFileArchive />,
  //       title: "sidebar.doc-approve-list",
  //       submenu: false,
  //       permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [

  //     {
  //       key: "new-joinees-list",
  //       path: `/${companySlug}/new-joinees-list`,
  //       icon: <NewemployIcon />,
  //       title: "sidebar.new-joinees-list",
  //       submenu: false,
  //       permission: [config.RESOURCES.NEW_JOINEES],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.HOLIDAYS, ["read"])) ? [
  //     {
  //       key: "holidays",
  //       path: `/${companySlug}/holidays`,
  //       icon: <FaRegFileArchive />,
  //       title: "sidebar.holidays",
  //       submenu: false,
  //       permission: [config.RESOURCES.HOLIDAYS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.POLICIES, ["read"])) ? [

  //     {
  //       key: "policies",
  //       path: `/${companySlug}/policies`,
  //       icon: <BsFillFileEarmarkMedicalFill />,
  //       title: "sidebar.policies",
  //       submenu: false,
  //       permission: [config.RESOURCES.POLICIES],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [

  //     {
  //       key: "Audits",
  //       path: `/${companySlug}/audits`,
  //       icon: <AuditIcon />,
  //       title: "Audits",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDIT_MASTER],
  //     }] : [],

  //   // {
  //   //   key: "appsettings",
  //   //   path: `/${companySlug}/app-setting`,
  //   //   icon: <FaRegSun />,
  //   //   title: "sidebar.app-setting",
  //   //   submenu: false,
  //   //   permission: [config.RESOURCES.SETTINGS],
  //   // },
  //   ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [

  //     {
  //       key: "resignation-employee-list",
  //       path: `/${companySlug}/resignation-employee-list`,
  //       icon: <SeparationIcon />,
  //       title: "sidebar.resignation-employee-list",
  //       submenu: false,
  //       permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [

  //     {
  //       key: "ManageTeamRegularization",
  //       path: `/${companySlug}/manage-team-regularization`,
  //       icon: <FaUserFriends />,
  //       title: "sidebar.ManageTeamRegularization",
  //       submenu: false,
  //       permission: [config.RESOURCES.TEAM_REGULARIZATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [

  //     {
  //       key: "user-leave-approval",
  //       path: `/${companySlug}/user-leave-approve`,
  //       icon: <LeaveManagement />,
  //       title: "sidebar.user-leave-management",
  //       submenu: false,
  //       permission: [config.RESOURCES.LEAVE_MANAGEMENT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [

  //     {
  //       key: "view-employee-details",
  //       path: `/${companySlug}/viewEmployeeDetails`,
  //       icon: <FaHouseUser />,
  //       title: "sidebar.employee-details",
  //       submenu: false,
  //       permission: [config.RESOURCES.EMPLOYEE_DETAILS],
  //     }] : [],

  //   // {
  //   //   key: "initiation",
  //   //   path: `/${companySlug}/onboarding-initiation`,
  //   //   icon: <OnboardingIcon />,
  //   //   title: "sidebar.onboarding-initiation",
  //   //   submenu: false,
  //   //   permission: [config.RESOURCES.ON_BOARDING_INITIATION],
  //   // },
  //   // {
  //   //   key: "doc-approve-list",
  //   //   path: `/${companySlug}/doc-approve-list`,
  //   //   icon: <FaRegFileArchive />,
  //   //   title: "sidebar.doc-approve-list",
  //   //   submenu: false,
  //   //   permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
  //   // },
  //   // ...(getResource(config.RESOURCES.WAFFLE, ["read"])
  //   //   ? [
  //   //     {
  //   //       icon: <i className="fas fa-stroopwafel " />,

  //   //       className: "main-submenu",
  //   //       key: "sub17",
  //   //       title: "Waffle ",
  //   //       // expandIcon: <FaChevronDown />,
  //   //       submenu: true,
  //   //       subMenuInfo: [
  //   //         {
  //   //           key: "karma-send",
  //   //           path: `/${companySlug}/Waffle`,
  //   //           title: "sidebar.karmaSend",
  //   //           submenu: false,
  //   //           permission: [config.RESOURCES.WAFFLE],
  //   //         },
  //   //         {
  //   //           key: "karma-reward",
  //   //           path: `/${companySlug}/Waffle-reward`,
  //   //           title: "sidebar.karmaReward",
  //   //           submenu: false,
  //   //           permission: [config.RESOURCES.WAFFLE_REWARD],
  //   //         },
  //   //         {
  //   //           key: "karma-weightage",
  //   //           path: `/${companySlug}/Waffle-weightage`,
  //   //           title: "sidebar.karmaWeightage",
  //   //           submenu: false,
  //   //           permission: [config.RESOURCES.WAFFLE_WEIGHTAGE],
  //   //         },
  //   //         ...(getResource(config.RESOURCES.WAFFLE_REWARD, ["read"])
  //   //           ? [
  //   //             {
  //   //               key: "karma-reward-approvel",
  //   //               path: `/${companySlug}/Waffle-reward-approvel`,
  //   //               title: "sidebar.karmaRewardApprovel",
  //   //               submenu: false,
  //   //               permission: [config.RESOURCES.WAFFLE_REWARD],
  //   //             },
  //   //             {
  //   //               key: "karma-karmaCategory",
  //   //               path: `/${companySlug}/Waffle-category`,
  //   //               title: "sidebar.karmaCategory",
  //   //               submenu: false,
  //   //               permission: [config.RESOURCES.WAFFLE_REWARD],
  //   //             },
  //   //           ]
  //   //           : []),
  //   //       ],
  //   //     },
  //   //   ]
  //   //   : []),
  //   ...(getResource(config.RESOURCES["SURVEY"], ["read"])) ? [
  //     {
  //       key: "360_survey",
  //       path: `/${companySlug}/360-survey`,
  //       icon: <AuditOutlined />,
  //       title: "sidebar.360_survey",
  //       submenu: false,
  //       permission: [config.RESOURCES["SURVEY"]],
  //     }] : [],
  //   {
  //     key: "organisationtree",
  //     path: `/${companySlug}/organization-view`,
  //     icon: <ImTree />,
  //     title: "sidebar.organisation-tree",
  //     submenu: false,
  //     permission: [],
  //   },
  // ];

  // const Recruiter = [
  //   {
  //     key: "Admin Dashboard",
  //     path: `/${companySlug}/dashboard`,
  //     icon: <AiOutlineDashboard />,
  //     title: "sidebar.Admin_Dashboard",
  //     submenu: false,
  //     permission: [],
  //   },
  //   ...(getResource(config.RESOURCES.LEAVE, ["read"]) ||
  //     getResource(config.RESOURCES.SEPARATION, ["read"]) ||
  //     getResource(config.RESOURCES.RANKING, ["read"]) ||
  //     getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
  //     getResource(config.RESOURCES.MY_ASSETS, ["read"])
  //     ? [
  //       {
  //         icon: <i className="fas fa-user " />,

  //         className: " main-submenu",
  //         key: "sub15",
  //         title: "Me",
  //         submenu: true,
  //         subMenuInfo: [
  //           ...(getResource(config.RESOURCES.LEAVE, ["read"])) ? [

  //             {
  //               key: "manage-leave-application",
  //               path: `/${companySlug}/leave-application`,
  //               title: "sidebar.leave-application",
  //               submenu: false,
  //               permission: [config.RESOURCES.LEAVE],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.SEPARATION, ["read"])) ? [
  //             {
  //               key: "resignation",
  //               path: `/${companySlug}/resignation`,
  //               title: "sidebar.resignation",
  //               submenu: false,
  //               permission: [config.RESOURCES.SEPARATION],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.RANKING, ["read"])) ? [
  //             {
  //               key: "individual-ranking",
  //               path: `/${companySlug}/individual-rank`,
  //               title: "sidebar.individual-rank",
  //               submenu: false,
  //               permission: [config.RESOURCES.RANKING],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
  //             {
  //               key: "Attendance Regularization",
  //               path: `/${companySlug}/employee-attendence-regularization`,
  //               title: "sidebar.Attendence_Regularization",
  //               submenu: false,
  //               permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])) ? [

  //             {
  //               key: "assets",
  //               path: `/${companySlug}/user-assets`,
  //               title: "sidebar.user-assets",
  //               submenu: false,
  //               permission: [config.RESOURCES.MY_ASSETS],
  //             }] : [],
  //         ],
  //       },
  //     ]
  //     : []),

  //   ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
  //     getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
  //     getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
  //     getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
  //     getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
  //     getResource(config.RESOURCES.TICKET, ["read"]) ||
  //     getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
  //     getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
  //     getResource(config.RESOURCES.PENDING_TASK, ["read"]) ||
  //     getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
  //     getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
  //     getResource(config.RESOURCES.KPI, ["read"])
  //     ? [
  //       {
  //         icon: <TeamInfoIcon />,

  //         className: " main-submenu",
  //         key: "sub16",
  //         title: "Team",
  //         submenu: true,
  //         subMenuInfo: [
  //           ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [

  //             {
  //               key: "employee-master",
  //               path: `/${companySlug}/employee-master`,
  //               title: "sidebar.employee-master",
  //               submenu: false,
  //               permission: [config.RESOURCES.EMPLOYEE_MASTER],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [

  //             {
  //               key: "employee-in-out",
  //               path: `/${companySlug}/employee-in-out`,
  //               title: "sidebar.Employee-In-Out",
  //               submenu: false,
  //               permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [
  //             {
  //               key: "resignation-employee-list",
  //               path: `/${companySlug}/resignation-employee-list`,
  //               title: "sidebar.resignation-employee-list",
  //               submenu: false,
  //               permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"])) ? [
  //             {
  //               key: "admin-reports",
  //               path: `/${companySlug}/admin-reports`,
  //               title: "sidebar.admin-reports",
  //               submenu: false,
  //               permission: [config.RESOURCES.EMPLOYEE_REPORT],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [
  //             {
  //               key: "tickets",
  //               path: `/${companySlug}/employee-tickets`,
  //               title: "sidebar.tickets",
  //               submenu: false,
  //               permission: [config.RESOURCES.EMPLOYEE_TICKET],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.TICKET, ["read"])) ? [

  //             {
  //               key: "tickets",
  //               path: `/${companySlug}/tickets`,
  //               title: "sidebar.tickets",
  //               submenu: false,
  //               permission: [config.RESOURCES.TICKET],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
  //             {
  //               key: "admin-leave-approval",
  //               path: `/${companySlug}/admin-leave-approval`,
  //               title: "sidebar.admin-leave-management",
  //               submenu: false,
  //               permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [

  //             {
  //               key: "user-leave-approval",
  //               path: `/${companySlug}/user-leave-approve`,
  //               title: "sidebar.user-leave-management",
  //               submenu: false,
  //               permission: [config.RESOURCES.LEAVE_MANAGEMENT],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.PENDING_TASK, ["read"])) ? [

  //             {
  //               key: "pendingtask",
  //               path: `/${companySlug}/pending-task-list`,
  //               title: "sidebar.pending-task",
  //               submenu: false,
  //               permission: [config.RESOURCES.PENDING_TASK],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [
  //             {
  //               key: "admin-regularizatrion",
  //               path: `/${companySlug}/admin-regularizatrion`,
  //               title: "sidebar.admin-regularizatrion",
  //               submenu: false,
  //               permission: [config.RESOURCES.ADMIN_REGULARIZATION],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [

  //             {
  //               key: "ManageTeamRegularization",
  //               path: `/${companySlug}/manage-team-regularization`,
  //               title: "sidebar.ManageTeamRegularization",
  //               submenu: false,
  //               permission: [config.RESOURCES.TEAM_REGULARIZATION],
  //             }] : [],
  //           ...(getResource(config.RESOURCES.KPI, ["read"])) ? [
  //             {
  //               key: "Kpis",
  //               path: `/${companySlug}/Kpis`,
  //               title: "sidebar.manage-kpis",
  //               submenu: false,
  //               permission: [config.RESOURCES.KPI],
  //             }] : [],
  //         ],
  //       },
  //     ]
  //     : []),


  //   {
  //     key: "Audits",
  //     path: `/${companySlug}/audit-history`,
  //     icon: <AuditIcon />,
  //     title: "Audit History",
  //     submenu: false,
  //     permission: [],
  //   },
  //   ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])) ? [

  //     {
  //       key: "last-working-day-emps",
  //       path: `/${companySlug}/last-working-day-emps`,
  //       icon: <SeparationIcon />,
  //       title: "Last Working Day List",
  //       submenu: false,
  //       permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [

  //     {
  //       key: "inventory",
  //       path: `/${companySlug}/inventory`,
  //       icon: <InventoryIcon />,
  //       title: "sidebar.user-assetstabpane",
  //       submenu: false,
  //       permission: [config.RESOURCES.INVENTORY],
  //     }] : [],
  //   // ...(getResource(config.RESOURCES.MANAGE_TICKET, ["read"])) ? [
  //   //   {
  //   //     key: "managetickets",
  //   //     path: `/${companySlug}/manage-tickets`,
  //   //     icon: <BsFillTicketDetailedFill />,
  //   //     title: "sidebar.manage-tickets",
  //   //     submenu: false,
  //   //     permission: [config.RESOURCES.MANAGE_TICKET],
  //   //   }] : [],
  //   ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [

  //     {
  //       key: "Audit Master",
  //       path: `/${companySlug}/auditmaster`,
  //       icon: <AuditmasterIcon />,
  //       title: "Audit Master",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDIT_MASTER],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [

  //     {
  //       key: "auditperformance",
  //       path: `/${companySlug}/auditperformance`,
  //       icon: <AuditIcon />,
  //       title: "Audit Performance",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDITS_PERFORMANCE],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [

  //     {
  //       key: "auditHead",
  //       path: `/${companySlug}/auditHead`,
  //       icon: <AuditmasterIcon />,
  //       title: "Audit Head",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDIT_HEAD],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [

  //     {
  //       key: "admin-leave-approval",
  //       path: `/${companySlug}/admin-leave-approval`,
  //       icon: <LeaveManagement />,
  //       title: "sidebar.admin-leave-management",
  //       submenu: false,
  //       permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [

  //     {
  //       key: "admin-regularizatrion",
  //       path: `/${companySlug}/admin-regularizatrion`,
  //       icon: <FaUserCog />,
  //       title: "sidebar.admin-regularizatrion",
  //       submenu: false,
  //       permission: [config.RESOURCES.ADMIN_REGULARIZATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ACHIEVEMENTS, ["read"])) ? [

  //     {
  //       key: "achivements",
  //       path: `/${companySlug}/achivements`,
  //       icon: <AchivementIcon />,
  //       title: "sidebar.achivements",
  //       submenu: false,
  //       permission: [config.RESOURCES.ACHIEVEMENTS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.SETTINGS, ["read"])) ? [

  //     {
  //       key: "permission",
  //       path: `/${companySlug}/permission-manager`,
  //       icon: <PermissionmanagerIcon />,
  //       title: "sidebar.permission",
  //       submenu: false,
  //       permission: [config.RESOURCES.SETTINGS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.DEPARTMENTS, ["read"])) ? [

  //     {
  //       key: "admin-department",
  //       path: `/${companySlug}/admin-department`,
  //       icon: <DepartmentIcon />,
  //       title: "sidebar.Department",
  //       submenu: false,
  //       permission: [config.RESOURCES.DEPARTMENTS],
  //     }] : [],

  //   ...(getResource(config.RESOURCES.DESIGNATION, ["read"])) ? [

  //     {
  //       key: "admin-designation",
  //       path: `/${companySlug}/admin-designation`,
  //       icon: <FaUserCog />,
  //       title: "sidebar.designation",
  //       submenu: false,
  //       permission: [config.RESOURCES.DESIGNATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.SHIFT, ["read"])) ? [

  //     {
  //       key: "admin-shift",
  //       path: `/${companySlug}/admin-shift`,
  //       icon: <FaClock />,
  //       title: "sidebar.shift",
  //       submenu: false,
  //       permission: [config.RESOURCES.SHIFT],
  //     }] : [],

  //   ...(getResource(config.RESOURCES.BUSINESS_UNIT, ["read"])) ? [

  //     {
  //       key: "admin-businessunit",
  //       path: `/${companySlug}/admin-businessunit`,
  //       icon: <FaSitemap />,
  //       title: "sidebar.businessunit",
  //       submenu: false,
  //       permission: [config.RESOURCES.BUSINESS_UNIT]
  //     }] : [],
  //   ...(getResource(config.RESOURCES.SUBDEPARTMENT, ["read"])) ? [

  //     {
  //       key: "admin-subdepartment",
  //       path: `/${companySlug}/admin-subdepartment`,
  //       icon: <FaSitemap />,
  //       title: "sidebar.subdepartment",
  //       submenu: false,
  //       permission: [config.RESOURCES.SUBDEPARTMENT],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.EMPLOYEMENT_TYPE, ["read"])) ? [
  //     {
  //       key: "admin-employment-type",
  //       path: `/${companySlug}/admin-employment-type`,
  //       icon: <FaSitemap />,
  //       title: "Employment Type",
  //       submenu: false,
  //       permission: [config.RESOURCES.EMPLOYEMENT_TYPE],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.LEAVE_MASTER, ["read"])) ? [

  //     {
  //       key: "leave-master",
  //       path: `/${companySlug}/admin-leave-master`,
  //       icon: <FaUserCog />,
  //       title: "sidebar.admin-leave-master",
  //       submenu: false,
  //       permission: [config.RESOURCES.LEAVE_MASTER],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.MANAGE_LEAVE_BALANCE, ["read"])) ? [

  //     {
  //       key: "employee-leave-balance",
  //       path: `/${companySlug}/employee-leave-balance`,
  //       icon: <FaHouseUser />,
  //       title: "sidebar.employee-leave-balance",
  //       submenu: false,
  //       permission: [config.RESOURCES.MANAGE_LEAVE_BALANCE],
  //     }] : [],

  //   ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [

  //     {
  //       key: "candidate-approvel",
  //       path: `/${companySlug}/candidate-approval`,
  //       icon: <FaUserTie />,
  //       title: "sidebar.candidate-approvel-list",
  //       submenu: false,
  //       permission: [config.RESOURCES.ON_BOARDING_INITIATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [

  //     {
  //       key: "candidate-list",
  //       path: `/${companySlug}/candidate-list`,
  //       icon: <FaUserCheck />,
  //       title: "sidebar.candidate-approvel",
  //       submenu: false,
  //       permission: [config.RESOURCES.CANDIDATE_APPROVAL],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [

  //     {
  //       key: "initiation",
  //       path: `/${companySlug}/onboarding-initiation`,
  //       icon: <OnboardingIcon />,
  //       title: "sidebar.onboarding-initiation",
  //       submenu: false,
  //       permission: [config.RESOURCES.ON_BOARDING_INITIATION],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [

  //     {
  //       key: "doc-approve-list",
  //       path: `/${companySlug}/doc-approve-list`,
  //       icon: <FaRegFileArchive />,
  //       title: "sidebar.doc-approve-list",
  //       submenu: false,
  //       permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [

  //     {
  //       key: "new-joinees-list",
  //       path: `/${companySlug}/new-joinees-list`,
  //       icon: <NewemployIcon />,
  //       title: "sidebar.new-joinees-list",
  //       submenu: false,
  //       permission: [config.RESOURCES.NEW_JOINEES],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.HOLIDAYS, ["read"])) ? [

  //     {
  //       key: "holidays",
  //       path: `/${companySlug}/holidays`,
  //       icon: <FaRegFileArchive />,
  //       title: "sidebar.holidays",
  //       submenu: false,
  //       permission: [config.RESOURCES.HOLIDAYS],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.POLICIES, ["read"])) ? [

  //     {
  //       key: "policies",
  //       path: `/${companySlug}/policies`,
  //       icon: <BsFillFileEarmarkMedicalFill />,
  //       title: "sidebar.policies",
  //       submenu: false,
  //       permission: [config.RESOURCES.POLICIES],
  //     }] : [],
  //   ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [

  //     {
  //       key: "Audits",
  //       path: `/${companySlug}/audits`,
  //       icon: <AuditIcon />,
  //       title: "Audits",
  //       submenu: false,
  //       permission: [config.RESOURCES.AUDIT_MASTER],
  //     }] : [],
  //   // {
  //   //   key: "appsettings",
  //   //   path: `/${companySlug}/app-setting`,
  //   //   icon: <FaRegSun />,
  //   //   title: "sidebar.app-setting",
  //   //   submenu: false,
  //   //   permission: [config.RESOURCES.SETTINGS],
  //   // },
  //   ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [

  //     {
  //       key: "view-employee-details",
  //       path: `/${companySlug}/viewEmployeeDetails`,
  //       icon: <FaHouseUser />,
  //       title: "sidebar.employee-details",
  //       submenu: false,
  //       permission: [config.RESOURCES.EMPLOYEE_DETAILS],
  //     }] : [],
  //   // ...(getResource(config.RESOURCES.WAFFLE, ["read"])
  //   //   ? [
  //   //     {
  //   //       icon: <i className="fas fa-stroopwafel" />,

  //   //       className: " main-submenu",
  //   //       key: "sub17",
  //   //       title: "Waffle ",
  //   //       submenu: true,
  //   //       subMenuInfo: [
  //   //         {
  //   //           key: "karma-send",
  //   //           path: `/${companySlug}/Waffle`,
  //   //           title: "sidebar.karmaSend",
  //   //           submenu: false,
  //   //           permission: [config.RESOURCES.WAFFLE],
  //   //         },
  //   //         {
  //   //           key: "karma-weightage",
  //   //           path: `/${companySlug}/Waffle-weightage`,
  //   //           title: "sidebar.karmaWeightage",
  //   //           submenu: false,
  //   //           permission: [config.RESOURCES.WAFFLE_WEIGHTAGE],
  //   //         },
  //   //         ...(getResource(config.RESOURCES.WAFFLE, ["create"])
  //   //           ? [
  //   //             {
  //   //               key: "karma-karmaCategory",
  //   //               path: `/${companySlug}/Waffle-category`,
  //   //               title: "sidebar.karmaCategory",
  //   //               submenu: false,
  //   //               permission: [config.RESOURCES.WAFFLE],
  //   //             },
  //   //           ]
  //   //           : []),

  //   //         ...(getResource(config.RESOURCES.WAFFLE_REWARD, ["read"])
  //   //           ? [
  //   //             {
  //   //               key: "karma-reward-approvel",
  //   //               path: `/${companySlug}/Waffle-reward-approvel`,
  //   //               title: "sidebar.karmaRewardApprovel",
  //   //               submenu: false,
  //   //               permission: [config.RESOURCES.WAFFLE_REWARD],
  //   //             },
  //   //           ]
  //   //           : []),
  //   //       ],
  //   //     },
  //   //   ]
  //   //   : []),
  //   ...(getResource(config.RESOURCES["SURVEY"], ["read"])) ? [

  //     {
  //       key: "360_survey",
  //       path: `/${companySlug}/360-survey`,
  //       icon: <AuditOutlined />,
  //       title: "sidebar.360_survey",
  //       submenu: false,
  //       permission: [config.RESOURCES["SURVEY"]],
  //     }] : [],
  //   {
  //     icon: <i className="fas fa-ellipsis-h " />,

  //     className: "main-submenu",
  //     key: "sub18",
  //     title: "More",
  //     submenu: true,
  //     subMenuInfo: [
  //       ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [

  //         {
  //           key: "Job Opening",
  //           path: `/${companySlug}/jobs/openings`,
  //           title: "sidebar.admin-jobopening",
  //           submenu: false,
  //           permission: [config.RESOURCES.JOB_OPENING],
  //         }] : [],
  //       {
  //         key: "organization-policy",
  //         path: `/${companySlug}/organization-policy`,
  //         title: "sidebar.organization-policies",
  //         submenu: false,
  //         permission: [],
  //       },
  //       ...(getResource(config.RESOURCES.MY_GOAL, ["read"])) ? [

  //         {
  //           key: "goals",
  //           path: `/${companySlug}/goals`,
  //           title: "sidebar.create-goals",
  //           submenu: false,
  //           permission: [config.RESOURCES.MY_GOAL],
  //         }] : [],
  //       ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [

  //         {
  //           key: "user-reports",
  //           path: `/${companySlug}/user-reports`,
  //           title: "sidebar.user-reports",
  //           submenu: false,
  //           permission: [config.RESOURCES.ATTENDANCE_REPORT],
  //         }] : [],
  //     ],
  //   },
  //   {
  //     key: "organisationtree",
  //     path: `/${companySlug}/organization-view`,
  //     icon: <ImTree />,
  //     title: "sidebar.organisation-tree",
  //     submenu: false,
  //     permission: [],
  //   },
  //   ...(getResource(config.RESOURCES["KPI"], ["read"])) ?
  //     [{
  //       key: "Kpis",
  //       path: `/${companySlug}/user-kpi`,
  //       icon: <FundOutlined />,
  //       title: "sidebar.kpis",
  //       submenu: false,
  //       permission: [config.RESOURCES.KPI],
  //     }] : []

  // ];

  const Intern = [
    {
      key: "User Dashboard",
      path: `/${companySlug}/dashboard`,
      icon: <AiOutlineDashboard />,
      title: "sidebar.User_Dashboard",
      submenu: false,
      permission: [], // always visible
    },

    // className: " main-submenu",
    // key: "sub15",
    // title: "Me",
    // submenu: true,

    // ✅ "Me" submenu
    ...(getResource(config.RESOURCES.LEAVE, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION, ["read"]) ||
      getResource(config.RESOURCES.TICKET, ["read"]) ||
      getResource(config.RESOURCES.MY_ASSETS, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.RANKING, ["read"]) ||
      getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
      // getResource(config.RESOURCES.AUDITS, ["read"]) ||
      getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])
      ? [
        {
          key: "Me",
          title: "Me",
          className: " main-submenu",
          icon: <i className="fas fa-user " />,
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
              {
                key: "My-In-Out",
                path: `/${companySlug}/my-in-out`,
                title: "sidebar.My-In-Out",
                submenu: false,
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE, ["read"])
              ? [{
                key: "manage-leave-application",
                path: `/${companySlug}/leave-application`,
                // icon: <LeaveIcon />,
                title: "sidebar.leave-application",
                permission: [config.RESOURCES.LEAVE],
              }]
              : []),

            ...(getResource(config.RESOURCES.SHIFT_SWAP_APPLICATION, ["read"])
              ? [{
                key: "shift-change-requests",
                path: `/${companySlug}/shift-change-requests`,
                title: "sidebar.shift-change-requests",
                permission: [config.RESOURCES.SHIFT_SWAP_APPLICATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])
              ? [{
                key: "attendance-regularization",
                path: `/${companySlug}/employee-attendence-regularization`,
                // icon: <AttendanceIcon />,
                title: "sidebar.Attendence_Regularization",
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.SEPARATION, ["read"])
              ? [{
                key: "resignation",
                path: `/${companySlug}/resignation`,
                // icon: <SeparationIcon />,
                title: "sidebar.resignation",
                permission: [config.RESOURCES.SEPARATION],
              }]
              : []),


            // ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
            //   {
            //     key: "Attendance Regularization",
            //     path: `/${companySlug}/employee-attendence-regularization`,
            //     // icon: <AttendanceIcon />,
            //     title: "sidebar.Attendence_Regularization",
            //     submenu: false,
            //     permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
            //   }] : [],

            ...(getResource(config.RESOURCES.TICKET, ["read"])
              ? [{
                key: "tickets",
                path: `/${companySlug}/tickets`,
                // icon: <ImTicket />,
                title: "sidebar.tickets",
                permission: [config.RESOURCES.TICKET],
              }]
              : []),

            ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])
              ? [{
                key: "assets",
                path: `/${companySlug}/user-assets`,
                // icon: <AssetsIcon />,
                title: "sidebar.user-assets",
                permission: [config.RESOURCES.MY_ASSETS],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])
              ? [{
                key: "user-reports",
                path: `/${companySlug}/user-reports`,
                // icon: <ReportsIcon />,
                title: "sidebar.user-reports",
                permission: [config.RESOURCES.ATTENDANCE_REPORT],
              }]
              : []),


            ...(getResource(config.RESOURCES.RANKING, ["read"])
              ? [{
                key: "individual-ranking",
                path: `/${companySlug}/individual-rank`,
                // icon: <RankingIcon />,
                title: "sidebar.individual-rank",
                permission: [config.RESOURCES.RANKING],
              }]
              : []),

            // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])
            //   ? [{
            //     key: "goals",
            //     path: `/${companySlug}/my-goals`,
            //     // icon: <GiOnTarget />,
            //     title: "sidebar.create-goals",
            //     permission: [config.RESOURCES.MY_GOAL],
            //   }]
            //   : []),

            // ...(getResource(config.RESOURCES.AUDITS, ["read"])
            //   ? [{
            //     key: "audits",
            //     path: `/${companySlug}/my-audit-history`,
            //     // icon: <FaFileInvoice />,
            //     title: "Audits",
            //     permission: [config.RESOURCES.AUDITS],
            //   }]
            //   : []),

            // {
            //   key: "proejct-overview",
            //   path: `/${companySlug}/project-overview`,
            //   // icon: <GiOnTarget />,
            //   title: "sidebar.project-overview",
            //   permission: [],
            // },
            ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
              {
                key: "Bill Reimbursement",
                path: `/${companySlug}/bill-reimbursement`,
                title: "sidebar.bill-reimbursement",
                submenu: false,
                permission: [config.RESOURCES.BILLREIMBURSEMENT],
              }] : [],
          ],
        },] : []),

    // ✅ "Team" submenu - Same structure as Admin
    ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.KPI, ["read"]) ||
      getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
      ? [
        {
          icon: <TeamInfoIcon />,
          className: "main-submenu",
          key: "sub16",
          title: "Team",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [
              {
                key: "employee-master",
                path: `/${companySlug}/employee-master`,
                title: "sidebar.employee-master",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_MASTER],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [
              {
                key: "employee-in-out",
                path: `/${companySlug}/employee-in-out`,
                title: "sidebar.Employee-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
            ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [
              {
                key: "resignation-employee-list",
                path: `/${companySlug}/resignation-employee-list`,
                title: "sidebar.resignation-employee-list",
                submenu: false,
                permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
              ? [{
                key: "last-working-day-emps",
                path: `/${companySlug}/last-working-day-emps`,
                // icon: <SeparationIcon />,
                title: "Last Working Day List",
                permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
              }]
              : []),
            // Milestone review forms - the HR/Admin tracker.
            //
            // Gated on the ROLE as well as the permission, because the server
            // gates this screen on role (`requireAdminOrHr`) and not on the
            // resource. Keying the menu off `create` alone would put a dead
            // link in front of anyone who was granted `create` without being an
            // Admin or HR - which is easy to do from the permission manager.
            ...((getRoles(["Admin"]) || getRoles(["HR"])) &&
              getResource(config.RESOURCES.REVIEW_FORMS, ["create"])
              ? [{
                key: "review-forms",
                path: `/${companySlug}/review-forms`,
                title: "Review Forms",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            // The approvals queue. Gated on `update`, which Reporting Managers
            // and ARMs hold as well - they are the people who actually decide.
            // The screen shows an explicit empty state to anyone with no
            // reports, so a stray viewer sees nothing rather than an error.
            ...(getResource(config.RESOURCES.REVIEW_FORMS, ["update"])
              ? [{
                key: "review-form-approvals",
                path: `/${companySlug}/review-form-approvals`,
                title: "Review Form Approvals",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [
              {
                key: "admin-reports",
                path: `/${companySlug}/admin-reports`,
                title: "sidebar.admin-reports",
                submenu: false,
                // Reports.js already renders one card per report family the caller may
                // read, so reaching the hub must not hinge on EMPLOYEE_REPORT alone -
                // a role granted only LEAVE_REPORT (e.g. an employee who may see their
                // own Leave Balance History) could otherwise never navigate here.
                permission: [
                  config.RESOURCES.EMPLOYEE_REPORT,
                  config.RESOURCES.LEAVE_REPORT,
                  config.RESOURCES.ATTENDANCE_REPORT,
                ],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [
              {
                key: "tickets",
                path: `/${companySlug}/employee-tickets`,
                title: "sidebar.tickets",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_TICKET],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "admin-leave-approval",
                path: `/${companySlug}/admin-leave-approval`,
                title: "sidebar.admin-leave-management",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "user-leave-approval",
                path: `/${companySlug}/user-leave-approve`,
                title: "sidebar.user-leave-management",
                submenu: false,
                permission: [config.RESOURCES.LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [
              {
                key: "admin-regularizatrion",
                path: `/${companySlug}/admin-regularizatrion`,
                title: "sidebar.admin-regularizatrion",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [
              {
                key: "ManageTeamRegularization",
                path: `/${companySlug}/manage-team-regularization`,
                title: "sidebar.ManageTeamRegularization",
                submenu: false,
                permission: [config.RESOURCES.TEAM_REGULARIZATION],
              }] : [],
            // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [
            //   {
            //     key: "Kpis",
            //     path: `/${companySlug}/Kpis`,
            //     title: "sidebar.manage-kpis",
            //     submenu: false,
            //     permission: [config.RESOURCES.KPI],
            //   }] : [],

          ],
        },
      ]
      : []),

    ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),
    ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),
    ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
      getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"]) ||
      getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"]) ||
      getResource(config.RESOURCES.NEW_JOINEES, ["read"])
      ? [
        {
          icon: <OnboardingIcon />,
          className: "main-submenu",
          key: "sub19",
          title: "Onboarding",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "company-doc-master",
                path: `/${companySlug}/company-doc-master`,
                title: "sidebar.docMaster",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-approvel",
                path: `/${companySlug}/candidate-approval`,
                title: "sidebar.candidate-approvel",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-list",
                path: `/${companySlug}/candidate-list`,
                title: "sidebar.candidate-approvel-list",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "initiation",
                path: `/${companySlug}/onboarding-initiation`,
                title: "sidebar.onboarding-initiation",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [
              {
                key: "doc-approve-list",
                path: `/${companySlug}/doc-approve-list`,
                title: "sidebar.doc-approve-list",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
              }] : [],
            ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [
              {
                key: "new-joinees-list",
                path: `/${companySlug}/new-joinees-list`,
                title: "sidebar.new-joinees-list",
                submenu: false,
                permission: [config.RESOURCES.NEW_JOINEES],
              }] : [],
          ],
        },
      ]
      : []),
    // ✅ "Audit" submenu - Same structure as Admin
    // ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"]) ||
    //   getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"]) ||
    //   getResource(config.RESOURCES.AUDIT_HEAD, ["read"])
    //   ? [
    //     {
    //       icon: <AuditIcon />,
    //       className: "main-submenu",
    //       key: "sub20",
    //       title: "Audit",
    //       submenu: true,
    //       subMenuInfo: [
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audit Master",
    //             path: `/${companySlug}/auditmaster`,
    //             title: "Audits Master",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audits",
    //             path: `/${companySlug}/audits`,
    //             title: "Audits",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [
    //           {
    //             key: "auditperformance",
    //             path: `/${companySlug}/auditperformance`,
    //             title: "Audit Performance",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDITS_PERFORMANCE],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [
    //           {
    //             key: "auditHead",
    //             path: `/${companySlug}/auditHead`,
    //             title: "Audit Head",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_HEAD],
    //           }] : [],
    //       ],
    //     },
    //   ]
    //   : []),


    // ...(getResource(config.RESOURCES.MANAGE_TICKET, ["read"])) ? [
    //   {
    //     key: "managetickets",
    //     path: `/${companySlug}/manage-tickets`,
    //     icon: <BsFillTicketDetailedFill />,
    //     title: "sidebar.manage-tickets",
    //     submenu: false,
    //     permission: [config.RESOURCES.MANAGE_TICKET],
    //   }] : [],


    ...(getResource(config.RESOURCES["KPI"], ["read"])) ?
      [{
        key: "Kpis",
        path: `/${companySlug}/user-kpi`,
        icon: <FundOutlined />,
        title: "sidebar.kpis",
        submenu: false,
        permission: [],
      }] : [],

    // ✅ "Survey" submenu - Same structure as Admin
    ...((getResource(config.RESOURCES["SURVEY"], ["read"]) ||
      getResource(config.RESOURCES["360QUE"], ["read"]) ||
      getResource(config.RESOURCES["360REVW"], ["read"]))
      ? [
        {
          icon: <i className="fas fa-poll" />,
          className: "main-submenu",
          key: "sub22",
          title: "Survey",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES["SURVEY"], ["read"])
              ? [
                {
                  key: "360_survey",
                  path: `/${companySlug}/360-survey`,
                  title: "sidebar.360_survey",
                  submenu: false,
                  permission: [config.RESOURCES["SURVEY"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360QUE"], ["read"])
              ? [
                {
                  key: "360_questionnaire",
                  path: `/${companySlug}/360-questionnaire`,
                  title: "sidebar.360_questionnaire",
                  submenu: false,
                  permission: [config.RESOURCES["360QUE"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360REVW"], ["read"])
              ? [
                {
                  key: "360_reviews",
                  path: `/${companySlug}/360-reviews`,
                  title: "sidebar.360_reviews",
                  submenu: false,
                  permission: [config.RESOURCES["360REVW"]],
                },
              ]
              : []),
          ],
        },
      ]
      : []),

    {
      icon: <i className="fas fa-ellipsis-h" />,
      className: "main-submenu",
      key: "sub18",
      title: "More",
      submenu: true,
      subMenuInfo: [
        // ...(getResource(config.RESOURCES.SUBSCRIPTION, ["read"])) ? [
        //   {
        //     key: "subscription",
        //     path: `/${companySlug}/subscription`,
        //     title: "sidebar.subscription",
        //     submenu: false,
        //     permission: [config.RESOURCES.SUBSCRIPTION],
        //   }] : [],
        ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [
          {
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],

        // ...(getResource(config.RESOURCES.EMPLOYEE_GOAL, ["read"])) ? [
        //   {
        //     key: "employee-goals",
        //     path: `/${companySlug}/employee-goals`,
        //     title: "sidebar.employee-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.EMPLOYEE_GOAL],
        //   }] : [],
        // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [
        //   {
        //     key: "team-goals",
        //     path: `/${companySlug}/team-goals`,
        //     title: "sidebar.team-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.TEAM_GOAL],
        //   }] : [],
        ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [
          {
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
        ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [
          {
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
        {
          key: "organization-policy",
          path: `/${companySlug}/organization-policy`,
          title: "sidebar.organization-policies",
          submenu: false,
          permission: [], // Always shown
        },
        ...(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"])) ? [
          {
            key: "Bill Reimbursement Approval",
            path: `/${companySlug}/admin-bill-reimbursement/submit`,
            title: "sidebar.admin-bill-reimbursement",
            submenu: false,
            permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL],
          },] : [],
      ],
    },


    // ─── LMS (Learning Management) sidebar ────────────────────────────
    ...((
      getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ||
      getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ||
      getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_REPORTS"], ["read"])
    ) ? [{
      icon: <i className="fas fa-graduation-cap" />,
      className: "main-submenu",
      key: "lms_module_sidebar",
      title: "Learning",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ? [{ key: "lms_courses_sidebar", path: `/${companySlug}/lms/courses`, title: "Course Management", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_assessments_sidebar", path: `/${companySlug}/lms/assessments`, title: "Assessments", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ? [{ key: "lms_enrollments_sidebar", path: `/${companySlug}/lms/enrollments`, title: "Enrollments", submenu: false, permission: [config.RESOURCES["LMS_ENROLLMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ? [{ key: "lms_my_learning_sidebar", path: `/${companySlug}/lms/my-learning`, title: "My Learning", submenu: false, permission: [config.RESOURCES["LMS_MY_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ? [{ key: "lms_certificates_sidebar", path: `/${companySlug}/lms/certificates`, title: "Certificates", submenu: false, permission: [config.RESOURCES["LMS_CERTIFICATES"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ? [{ key: "lms_learning_paths_sidebar", path: `/${companySlug}/lms/learning-paths`, title: "Learning Paths", submenu: false, permission: [config.RESOURCES["LMS_LEARNING_PATH"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ? [{ key: "lms_auto_assignment_sidebar", path: `/${companySlug}/lms/auto-assignment`, title: "Auto-Assignment", submenu: false, permission: [config.RESOURCES["LMS_AUTO_ASSIGNMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ? [{ key: "lms_team_learning_sidebar", path: `/${companySlug}/lms/team-learning`, title: "Team Learning", submenu: false, permission: [config.RESOURCES["LMS_TEAM_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_reports_sidebar", path: `/${companySlug}/lms/reports`, title: "LMS Reports", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_grading_sidebar", path: `/${companySlug}/lms/grading`, title: "Manual Grading", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_data_retention_sidebar", path: `/${companySlug}/lms/settings/data-retention`, title: "Data Retention", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["update"]) ? [{ key: "lms_cert_template_sidebar", path: `/${companySlug}/lms/settings/certificate-template`, title: "Certificate Template", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
      ],
    }] : []),
    ...(getResource(config.RESOURCES.SKILL, ["read"]) ||
      getResource(config.RESOURCES.SKILLS_MATRIX, ["read"]) ||
      getResource(config.RESOURCES.SKILL_INVENTORY, ["read"]) ||
      getResource(config.RESOURCES.CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_VIEW, ["read"]) ||
      getResource(config.RESOURCES.CAREER_READINESS, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_COMPETENCY, ["read"]) ||
      getResource(config.RESOURCES.COURSE_SKILL_MAPPING, ["read"]) ? [{
      icon: <i className="fas fa-user-graduate" />,
      key: "talent_development_sidebar",
      title: "Talent Development",
      path: `/${companySlug}/talent-development`,
      submenu: false,
      permission: [config.RESOURCES.SKILL, config.RESOURCES.CAREER_PATH],
    }] : []),


    // ✅ "Onboarding" submenu - Same structure as Admin


  ];

  const Recruiter = [
    {
      key: "User Dashboard",
      path: `/${companySlug}/dashboard`,
      icon: <AiOutlineDashboard />,
      title: "sidebar.User_Dashboard",
      submenu: false,
      permission: [], // always visible
    },

    // className: " main-submenu",
    // key: "sub15",
    // title: "Me",
    // submenu: true,

    // ✅ "Me" submenu
    ...(getResource(config.RESOURCES.LEAVE, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION, ["read"]) ||
      getResource(config.RESOURCES.TICKET, ["read"]) ||
      getResource(config.RESOURCES.MY_ASSETS, ["read"]) ||
      getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.RANKING, ["read"]) ||
      getResource(config.RESOURCES.MY_GOAL, ["read"]) ||
      // getResource(config.RESOURCES.AUDITS, ["read"]) ||
      getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])
      ? [
        {
          key: "Me",
          title: "Me",
          className: " main-submenu",
          icon: <i className="fas fa-user " />,
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
              {
                key: "My-In-Out",
                path: `/${companySlug}/my-in-out`,
                title: "sidebar.My-In-Out",
                submenu: false,
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE, ["read"])
              ? [{
                key: "manage-leave-application",
                path: `/${companySlug}/leave-application`,
                // icon: <LeaveIcon />,
                title: "sidebar.leave-application",
                permission: [config.RESOURCES.LEAVE],
              }]
              : []),

            ...(getResource(config.RESOURCES.SHIFT_SWAP_APPLICATION, ["read"])
              ? [{
                key: "shift-change-requests",
                path: `/${companySlug}/shift-change-requests`,
                title: "sidebar.shift-change-requests",
                permission: [config.RESOURCES.SHIFT_SWAP_APPLICATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])
              ? [{
                key: "attendance-regularization",
                path: `/${companySlug}/employee-attendence-regularization`,
                // icon: <AttendanceIcon />,
                title: "sidebar.Attendence_Regularization",
                permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
              }]
              : []),

            ...(getResource(config.RESOURCES.SEPARATION, ["read"])
              ? [{
                key: "resignation",
                path: `/${companySlug}/resignation`,
                // icon: <SeparationIcon />,
                title: "sidebar.resignation",
                permission: [config.RESOURCES.SEPARATION],
              }]
              : []),


            // ...(getResource(config.RESOURCES.ATTENDANCE_REGULARIZATION, ["read"])) ? [
            //   {
            //     key: "Attendance Regularization",
            //     path: `/${companySlug}/employee-attendence-regularization`,
            //     // icon: <AttendanceIcon />,
            //     title: "sidebar.Attendence_Regularization",
            //     submenu: false,
            //     permission: [config.RESOURCES.ATTENDANCE_REGULARIZATION],
            //   }] : [],

            ...(getResource(config.RESOURCES.TICKET, ["read"])
              ? [{
                key: "tickets",
                path: `/${companySlug}/tickets`,
                // icon: <ImTicket />,
                title: "sidebar.tickets",
                permission: [config.RESOURCES.TICKET],
              }]
              : []),

            ...(getResource(config.RESOURCES.MY_ASSETS, ["read"])
              ? [{
                key: "assets",
                path: `/${companySlug}/user-assets`,
                // icon: <AssetsIcon />,
                title: "sidebar.user-assets",
                permission: [config.RESOURCES.MY_ASSETS],
              }]
              : []),

            ...(getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])
              ? [{
                key: "user-reports",
                path: `/${companySlug}/user-reports`,
                // icon: <ReportsIcon />,
                title: "sidebar.user-reports",
                permission: [config.RESOURCES.ATTENDANCE_REPORT],
              }]
              : []),


            ...(getResource(config.RESOURCES.RANKING, ["read"])
              ? [{
                key: "individual-ranking",
                path: `/${companySlug}/individual-rank`,
                // icon: <RankingIcon />,
                title: "sidebar.individual-rank",
                permission: [config.RESOURCES.RANKING],
              }]
              : []),

            // ...(getResource(config.RESOURCES.MY_GOAL, ["read"])
            //   ? [{
            //     key: "goals",
            //     path: `/${companySlug}/my-goals`,
            //     // icon: <GiOnTarget />,
            //     title: "sidebar.create-goals",
            //     permission: [config.RESOURCES.MY_GOAL],
            //   }]
            //   : []),

            // ...(getResource(config.RESOURCES.AUDITS, ["read"])
            //   ? [{
            //     key: "audits",
            //     path: `/${companySlug}/my-audit-history`,
            //     // icon: <FaFileInvoice />,
            //     title: "Audits",
            //     permission: [config.RESOURCES.AUDITS],
            //   }]
            //   : []),

            // {
            //   key: "proejct-overview",
            //   path: `/${companySlug}/project-overview`,
            //   // icon: <GiOnTarget />,
            //   title: "sidebar.project-overview",
            //   permission: [],
            // },
            ...(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"])) ? [
              {
                key: "Bill Reimbursement",
                path: `/${companySlug}/bill-reimbursement`,
                title: "sidebar.bill-reimbursement",
                submenu: false,
                permission: [config.RESOURCES.BILLREIMBURSEMENT],
              }] : [],
          ],
        },] : []),

    // ✅ "Team" submenu - Same structure as Admin
    ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"]) ||
      getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"]) ||
      getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"]) ||
      getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
      ? [
        {
          icon: <TeamInfoIcon />,
          className: "main-submenu",
          key: "sub16",
          title: "Team",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) ? [
              {
                key: "employee-master",
                path: `/${companySlug}/employee-master`,
                title: "sidebar.employee-master",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_MASTER],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_IN_OUT, ["read"])) ? [
              {
                key: "employee-in-out",
                path: `/${companySlug}/employee-in-out`,
                title: "sidebar.Employee-In-Out",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_IN_OUT],
              }] : [],
            ...(getResource(config.RESOURCES.SEPARATION_MANAGEMENT, ["read"])) ? [
              {
                key: "resignation-employee-list",
                path: `/${companySlug}/resignation-employee-list`,
                title: "sidebar.resignation-employee-list",
                submenu: false,
                permission: [config.RESOURCES.SEPARATION_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LAST_WORKING_DAY_LIST, ["read"])
              ? [{
                key: "last-working-day-emps",
                path: `/${companySlug}/last-working-day-emps`,
                // icon: <SeparationIcon />,
                title: "Last Working Day List",
                permission: [config.RESOURCES.LAST_WORKING_DAY_LIST],
              }]
              : []),
            // Milestone review forms - the HR/Admin tracker.
            //
            // Gated on the ROLE as well as the permission, because the server
            // gates this screen on role (`requireAdminOrHr`) and not on the
            // resource. Keying the menu off `create` alone would put a dead
            // link in front of anyone who was granted `create` without being an
            // Admin or HR - which is easy to do from the permission manager.
            ...((getRoles(["Admin"]) || getRoles(["HR"])) &&
              getResource(config.RESOURCES.REVIEW_FORMS, ["create"])
              ? [{
                key: "review-forms",
                path: `/${companySlug}/review-forms`,
                title: "Review Forms",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            // The approvals queue. Gated on `update`, which Reporting Managers
            // and ARMs hold as well - they are the people who actually decide.
            // The screen shows an explicit empty state to anyone with no
            // reports, so a stray viewer sees nothing rather than an error.
            ...(getResource(config.RESOURCES.REVIEW_FORMS, ["update"])
              ? [{
                key: "review-form-approvals",
                path: `/${companySlug}/review-form-approvals`,
                title: "Review Form Approvals",
                submenu: false,
                permission: [config.RESOURCES.REVIEW_FORMS],
              }]
              : []),
            ...(getResource(config.RESOURCES.EMPLOYEE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.LEAVE_REPORT, ["read"]) ||
              getResource(config.RESOURCES.ATTENDANCE_REPORT, ["read"])) ? [
              {
                key: "admin-reports",
                path: `/${companySlug}/admin-reports`,
                title: "sidebar.admin-reports",
                submenu: false,
                // Reports.js already renders one card per report family the caller may
                // read, so reaching the hub must not hinge on EMPLOYEE_REPORT alone -
                // a role granted only LEAVE_REPORT (e.g. an employee who may see their
                // own Leave Balance History) could otherwise never navigate here.
                permission: [
                  config.RESOURCES.EMPLOYEE_REPORT,
                  config.RESOURCES.LEAVE_REPORT,
                  config.RESOURCES.ATTENDANCE_REPORT,
                ],
              }] : [],
            ...(getResource(config.RESOURCES.EMPLOYEE_TICKET, ["read"])) ? [
              {
                key: "tickets",
                path: `/${companySlug}/employee-tickets`,
                title: "sidebar.tickets",
                submenu: false,
                permission: [config.RESOURCES.EMPLOYEE_TICKET],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "admin-leave-approval",
                path: `/${companySlug}/admin-leave-approval`,
                title: "sidebar.admin-leave-management",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.LEAVE_MANAGEMENT, ["read"])) ? [
              {
                key: "user-leave-approval",
                path: `/${companySlug}/user-leave-approve`,
                title: "sidebar.user-leave-management",
                submenu: false,
                permission: [config.RESOURCES.LEAVE_MANAGEMENT],
              }] : [],
            ...(getResource(config.RESOURCES.ADMIN_REGULARIZATION, ["read"])) ? [
              {
                key: "admin-regularizatrion",
                path: `/${companySlug}/admin-regularizatrion`,
                title: "sidebar.admin-regularizatrion",
                submenu: false,
                permission: [config.RESOURCES.ADMIN_REGULARIZATION],
              }] : [],
            ...(getResource(config.RESOURCES.TEAM_REGULARIZATION, ["read"])) ? [
              {
                key: "ManageTeamRegularization",
                path: `/${companySlug}/manage-team-regularization`,
                title: "sidebar.ManageTeamRegularization",
                submenu: false,
                permission: [config.RESOURCES.TEAM_REGULARIZATION],
              }] : [],
            // ...(getResource(config.RESOURCES.KPI, ["read"])) ? [
            //   {
            //     key: "Kpis",
            //     path: `/${companySlug}/Kpis`,
            //     title: "sidebar.manage-kpis",
            //     submenu: false,
            //     permission: [config.RESOURCES.KPI],
            //   }] : [],

          ],
        },
      ]
      : []),

    ...(performanceMenu.subMenuInfo.length > 0 ? [performanceMenu] : []),
    ...(expenseMenu.subMenuInfo.length > 0 ? [expenseMenu] : []),
    ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
      getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"]) ||
      getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"]) ||
      getResource(config.RESOURCES.NEW_JOINEES, ["read"])
      ? [
        {
          icon: <OnboardingIcon />,
          className: "main-submenu",
          key: "sub19",
          title: "Onboarding",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "company-doc-master",
                path: `/${companySlug}/company-doc-master`,
                title: "sidebar.docMaster",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-approvel",
                path: `/${companySlug}/candidate-approval`,
                title: "sidebar.candidate-approvel",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"])) ? [
              {
                key: "candidate-list",
                path: `/${companySlug}/candidate-list`,
                title: "sidebar.candidate-approvel-list",
                submenu: false,
                permission: [config.RESOURCES.CANDIDATE_APPROVAL],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"])) ? [
              {
                key: "initiation",
                path: `/${companySlug}/onboarding-initiation`,
                title: "sidebar.onboarding-initiation",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_INITIATION],
              }] : [],
            ...(getResource(config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST, ["read"])) ? [
              {
                key: "doc-approve-list",
                path: `/${companySlug}/doc-approve-list`,
                title: "sidebar.doc-approve-list",
                submenu: false,
                permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST],
              }] : [],
            ...(getResource(config.RESOURCES.NEW_JOINEES, ["read"])) ? [
              {
                key: "new-joinees-list",
                path: `/${companySlug}/new-joinees-list`,
                title: "sidebar.new-joinees-list",
                submenu: false,
                permission: [config.RESOURCES.NEW_JOINEES],
              }] : [],
          ],
        },
      ]
      : []),
    // ✅ "Audit" submenu - Same structure as Admin
    // ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"]) ||
    //   getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"]) ||
    //   getResource(config.RESOURCES.AUDIT_HEAD, ["read"])
    //   ? [
    //     {
    //       icon: <AuditIcon />,
    //       className: "main-submenu",
    //       key: "sub20",
    //       title: "Audit",
    //       submenu: true,
    //       subMenuInfo: [
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audit Master",
    //             path: `/${companySlug}/auditmaster`,
    //             title: "Audits Master",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) ? [
    //           {
    //             key: "Audits",
    //             path: `/${companySlug}/audits`,
    //             title: "Audits",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_MASTER],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) ? [
    //           {
    //             key: "auditperformance",
    //             path: `/${companySlug}/auditperformance`,
    //             title: "Audit Performance",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDITS_PERFORMANCE],
    //           }] : [],
    //         ...(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) ? [
    //           {
    //             key: "auditHead",
    //             path: `/${companySlug}/auditHead`,
    //             title: "Audit Head",
    //             submenu: false,
    //             permission: [config.RESOURCES.AUDIT_HEAD],
    //           }] : [],
    //       ],
    //     },
    //   ]
    //   : []),


    // ...(getResource(config.RESOURCES.MANAGE_TICKET, ["read"])) ? [
    //   {
    //     key: "managetickets",
    //     path: `/${companySlug}/manage-tickets`,
    //     icon: <BsFillTicketDetailedFill />,
    //     title: "sidebar.manage-tickets",
    //     submenu: false,
    //     permission: [config.RESOURCES.MANAGE_TICKET],
    //   }] : [],


    ...(getResource(config.RESOURCES["KPI"], ["read"])) ?
      [{
        key: "Kpis",
        path: `/${companySlug}/user-kpi`,
        icon: <FundOutlined />,
        title: "sidebar.kpis",
        submenu: false,
        permission: [],
      }] : [],

    // ✅ "Survey" submenu - Same structure as Admin
    ...((getResource(config.RESOURCES["SURVEY"], ["read"]) ||
      getResource(config.RESOURCES["360QUE"], ["read"]) ||
      getResource(config.RESOURCES["360REVW"], ["read"]))
      ? [
        {
          icon: <i className="fas fa-poll" />,
          className: "main-submenu",
          key: "sub22",
          title: "Survey",
          submenu: true,
          subMenuInfo: [
            ...(getResource(config.RESOURCES["SURVEY"], ["read"])
              ? [
                {
                  key: "360_survey",
                  path: `/${companySlug}/360-survey`,
                  title: "sidebar.360_survey",
                  submenu: false,
                  permission: [config.RESOURCES["SURVEY"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360QUE"], ["read"])
              ? [
                {
                  key: "360_questionnaire",
                  path: `/${companySlug}/360-questionnaire`,
                  title: "sidebar.360_questionnaire",
                  submenu: false,
                  permission: [config.RESOURCES["360QUE"]],
                },
              ]
              : []),

            ...(getResource(config.RESOURCES["360REVW"], ["read"])
              ? [
                {
                  key: "360_reviews",
                  path: `/${companySlug}/360-reviews`,
                  title: "sidebar.360_reviews",
                  submenu: false,
                  permission: [config.RESOURCES["360REVW"]],
                },
              ]
              : []),
          ],
        },
      ]
      : []),
    {
      icon: <i className="fas fa-ellipsis-h" />,
      className: "main-submenu",
      key: "sub18",
      title: "More",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES.JOB_OPENING, ["read"])) ? [
          {
            key: "Job Opening",
            path: `/${companySlug}/jobs/openings`,
            title: "sidebar.admin-jobopening",
            submenu: false,
            permission: [config.RESOURCES.JOB_OPENING],
          }] : [],
        // ...(getResource(config.RESOURCES.TEAM_GOAL, ["read"])) ? [
        //   {
        //     key: "team-goals",
        //     path: `/${companySlug}/team-goals`,
        //     title: "sidebar.team-goals",
        //     submenu: false,
        //     permission: [config.RESOURCES.TEAM_GOAL],
        //   }] : [],
        ...(getResource(config.RESOURCES.INVENTORY, ["read"])) ? [
          {
            key: "inventory",
            path: `/${companySlug}/inventory`,
            title: "sidebar.user-assetstabpane",
            submenu: false,
            permission: [config.RESOURCES.INVENTORY],
          }] : [],
        ...(getResource(config.RESOURCES.EMPLOYEE_DETAILS, ["read"])) ? [
          {
            key: "view-employee-details",
            path: `/${companySlug}/viewEmployeeDetails`,
            title: "sidebar.employee-details",
            submenu: false,
            permission: [config.RESOURCES.EMPLOYEE_DETAILS],
          }] : [],
        {
          key: "organization-policy",
          path: `/${companySlug}/organization-policy`,
          title: "sidebar.organization-policies",
          submenu: false,
          permission: [], // Always shown
        },
        ...(getResource(config.RESOURCES.BILLREIMBURSEMENT_APPROVAL, ["read"])) ? [
          {
            key: "Bill Reimbursement Approval",
            path: `/${companySlug}/admin-bill-reimbursement/submit`,
            title: "sidebar.admin-bill-reimbursement",
            submenu: false,
            permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL],
          },] : [],
      ],
    },

    // ─── LMS (Learning Management) sidebar ────────────────────────────
    ...((
      getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ||
      getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ||
      getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ||
      getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ||
      getResource(config.RESOURCES["LMS_REPORTS"], ["read"])
    ) ? [{
      icon: <i className="fas fa-graduation-cap" />,
      className: "main-submenu",
      key: "lms_module_sidebar",
      title: "Learning",
      submenu: true,
      subMenuInfo: [
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ? [{ key: "lms_courses_sidebar", path: `/${companySlug}/lms/courses`, title: "Course Management", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_assessments_sidebar", path: `/${companySlug}/lms/assessments`, title: "Assessments", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ? [{ key: "lms_enrollments_sidebar", path: `/${companySlug}/lms/enrollments`, title: "Enrollments", submenu: false, permission: [config.RESOURCES["LMS_ENROLLMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ? [{ key: "lms_my_learning_sidebar", path: `/${companySlug}/lms/my-learning`, title: "My Learning", submenu: false, permission: [config.RESOURCES["LMS_MY_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ? [{ key: "lms_certificates_sidebar", path: `/${companySlug}/lms/certificates`, title: "Certificates", submenu: false, permission: [config.RESOURCES["LMS_CERTIFICATES"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ? [{ key: "lms_learning_paths_sidebar", path: `/${companySlug}/lms/learning-paths`, title: "Learning Paths", submenu: false, permission: [config.RESOURCES["LMS_LEARNING_PATH"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ? [{ key: "lms_auto_assignment_sidebar", path: `/${companySlug}/lms/auto-assignment`, title: "Auto-Assignment", submenu: false, permission: [config.RESOURCES["LMS_AUTO_ASSIGNMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ? [{ key: "lms_team_learning_sidebar", path: `/${companySlug}/lms/team-learning`, title: "Team Learning", submenu: false, permission: [config.RESOURCES["LMS_TEAM_LEARNING"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_reports_sidebar", path: `/${companySlug}/lms/reports`, title: "LMS Reports", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ? [{ key: "lms_grading_sidebar", path: `/${companySlug}/lms/grading`, title: "Manual Grading", submenu: false, permission: [config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) ? [{ key: "lms_data_retention_sidebar", path: `/${companySlug}/lms/settings/data-retention`, title: "Data Retention", submenu: false, permission: [config.RESOURCES["LMS_REPORTS"]] }] : []),
        ...(getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["update"]) ? [{ key: "lms_cert_template_sidebar", path: `/${companySlug}/lms/settings/certificate-template`, title: "Certificate Template", submenu: false, permission: [config.RESOURCES["LMS_COURSE_MANAGEMENT"]] }] : []),
      ],
    }] : []),
    ...(getResource(config.RESOURCES.SKILL, ["read"]) ||
      getResource(config.RESOURCES.SKILLS_MATRIX, ["read"]) ||
      getResource(config.RESOURCES.SKILL_INVENTORY, ["read"]) ||
      getResource(config.RESOURCES.CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_PATH, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_CAREER_VIEW, ["read"]) ||
      getResource(config.RESOURCES.CAREER_READINESS, ["read"]) ||
      getResource(config.RESOURCES.EMPLOYEE_COMPETENCY, ["read"]) ||
      getResource(config.RESOURCES.COURSE_SKILL_MAPPING, ["read"]) ? [{
      icon: <i className="fas fa-user-graduate" />,
      key: "talent_development_sidebar",
      title: "Talent Development",
      path: `/${companySlug}/talent-development`,
      submenu: false,
      permission: [config.RESOURCES.SKILL, config.RESOURCES.CAREER_PATH],
    }] : []),


    // ✅ "Onboarding" submenu - Same structure as Admin


  ];

  // Analytics sidebar entry - a normal menu item (same shape as every other module), prepended
  // to each role's menu array so the existing map + hasSidebarItemAccess renders and RBAC-gates it
  // exactly like the rest. Visibility = the ANALYTICS resource's read permission (not plan-gated).
  const analyticsEntry = {
    key: "Analytics Studio",
    path: `/${companySlug}/analytics-studio`,
    icon: <AnalyticsIcon />,
    title: "Analytics",
    submenu: false,
    permission: [config.RESOURCES.ANALYTICS],
  };

  return (
    <>
      <Sider
        className="Sidebar"
        width={120}
        style={{ minWidth: "120px", maxWidth: "120px" }}
      >
        <SidebarLogo />

        {sidebarLoading ? (
          <SidebarMenuSkeleton />
        ) : getRoles(["Admin", "Sub-Admin"]) ? (
          <div className="gx-sidebar-content sidebar-menu">
            <CustomScrollbars className="gx-layout-sider-scrollbar">
              <Menu
                mode="vertical"
                openKeys={openKeys}
                selectedKeys={[selectedKeys]}
                theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
                triggerSubMenuAction="hover"
                onMouseEnter={(e) => {
                  const submenuKey = e.key;
                  if (submenuKey && !openKeys.includes(submenuKey)) {
                    setOpenKeys([...openKeys, submenuKey]);
                  }
                }}
                onOpenChange={(keys) =>
                  setOpenKeys(keys.length ? [keys[keys.length - 1]] : [])
                } // Ensure only one submenu is open
              >

                <Menu.Item
                  title={
                    <span>
                      <IntlMessages id="Dashboard" />
                    </span>
                  }
                  key="Admin Dashboard"
                  className={
                    matchesSubmenuPath(location.pathname, `/${companySlug}/dashboard`)
                      ? "ant-menu-item-active"
                      : ""
                  }
                >
                  <NavLink to={`/${companySlug}/dashboard`}>
                    <AiOutlineDashboard />
                    <span>
                      <IntlMessages id="sidebar.Admin_Dashboard" />
                    </span>
                  </NavLink>
                </Menu.Item>

                {hasPlanModuleAccess(analyticsEntry) && subAdminSeesAny([analyticsEntry]) && (
                  <Menu.Item
                    title={<span>{analyticsEntry.title}</span>}
                    key={analyticsEntry.key}
                    className={
                      matchesSubmenuPath(location.pathname, analyticsEntry.path)
                        ? "ant-menu-item-active"
                        : ""
                    }
                  >
                    <NavLink to={analyticsEntry.path}>
                      {analyticsEntry.icon}
                      <span>{analyticsEntry.title}</span>
                    </NavLink>
                  </Menu.Item>
                )}


                {hasPlanModuleAccess({ title: "Me" }) && subAdminSeesAny([
                  { path: `/${companySlug}/my-in-out`, permission: [config.RESOURCES.EMPLOYEE_IN_OUT] },
                  { path: `/${companySlug}/leave-application`, permission: [config.RESOURCES.LEAVE] },
                  { path: `/${companySlug}/shift-change-requests`, permission: [config.RESOURCES.SHIFT_SWAP_APPLICATION] },
                  { path: `/${companySlug}/individual-rank`, permission: [config.RESOURCES.RANKING] },
                  { path: `/${companySlug}/bill-reimbursement`, permission: [config.RESOURCES.BILLREIMBURSEMENT] },
                ]) && (
                  <SubMenu
                    popupClassName="right-submenu"
                    onMouseEnter={() => handleSubmenuEnter("sub15")}
                    onMouseLeave={() => handleSubmenuLeave()}
                    className={
                      [
                        "/my-in-out",
                        "/leave-application",
                        "/shift-change-requests",
                        "/individual-rank",
                        "/bill-reimbursement"
                      ].some((path) => matchesSubmenuPath(location.pathname, path))
                        ? "main-submenu full-width-hover ant-menu-item-active"
                        : "main-submenu full-width-hover"
                    }
                    key="sub15"
                    title="Me"
                    icon={<i className="fas fa-user" />}

                    onTitleClick={() =>
                      setOpenKeys(openKeys.includes("sub15") ? [] : ["sub15"])
                    } // Only one submenu at a time
                  >
                    {hasSidebarItemAccess({ path: `/${companySlug}/my-in-out`, permission: [config.RESOURCES.EMPLOYEE_IN_OUT] }) && (
                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="My-In-Out" />
                          </span>
                        }
                        key="My-In-Out"
                        className={
                          matchesSubmenuPath(location.pathname, "/my-in-out")
                            ? "ant-menu-item-active "
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/my-in-out`}>
                          <span>
                            <IntlMessages id="sidebar.My-In-Out" />
                          </span>
                        </NavLink>
                      </Menu.Item>
                    )}
                    {hasSidebarItemAccess({ path: `/${companySlug}/leave-application`, permission: [config.RESOURCES.LEAVE] }) && (
                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.leave-application" />
                          </span>
                        }
                        key="manage-leave-application"
                        className={
                          matchesSubmenuPath(location.pathname, "/leave-application")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/leave-application`}>
                          <span>
                            <IntlMessages id="sidebar.leave-application" />
                          </span>
                        </NavLink>
                      </Menu.Item>
                    )}
                    {hasSidebarItemAccess({ path: `/${companySlug}/shift-change-requests`, permission: [config.RESOURCES.SHIFT_SWAP_APPLICATION] }) && (
                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.shift-change-requests" />
                          </span>
                        }
                        key="shift-change-requests"
                        className={
                          matchesSubmenuPath(location.pathname, "/shift-change-requests")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/shift-change-requests`}>
                          <span>
                            <IntlMessages id="sidebar.shift-change-requests" />
                          </span>
                        </NavLink>
                      </Menu.Item>
                    )}
                    {hasSidebarItemAccess({ path: `/${companySlug}/individual-rank`, permission: [config.RESOURCES.RANKING] }) && (
                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.individual-rank" />
                          </span>
                        }
                        key="individual-ranking"
                        className={
                          matchesSubmenuPath(location.pathname, "/individual-rank")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/individual-rank`}>
                          <span>
                            <IntlMessages id="sidebar.individual-rank" />
                          </span>
                        </NavLink>
                      </Menu.Item>
                    )}
                    {hasSidebarItemAccess({ path: `/${companySlug}/bill-reimbursement`, permission: [config.RESOURCES.BILLREIMBURSEMENT] }) && (
                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.bill-reimbursement" />
                          </span>
                        }
                        key="bill-reimbursement"
                        className={
                          matchesSubmenuPath(location.pathname, "/bill-reimbursement")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/bill-reimbursement`}>
                          <span>
                            <IntlMessages id="sidebar.bill-reimbursement" />
                          </span>
                        </NavLink>
                      </Menu.Item>
                    )}

                  </SubMenu>
                )}

                {hasPlanModuleAccess({ title: "Team" }) && subAdminSeesAny([
                  { path: `/${companySlug}/employee-master`, permission: [config.RESOURCES.EMPLOYEE_MASTER] },
                  { path: `/${companySlug}/employee-in-out`, permission: [config.RESOURCES.EMPLOYEE_IN_OUT] },
                  { path: `/${companySlug}/resignation-employee-list`, permission: [config.RESOURCES.SEPARATION_MANAGEMENT] },
                  { path: `/${companySlug}/admin-reports`, permission: [config.RESOURCES.EMPLOYEE_REPORT] },
                  { path: `/${companySlug}/employee-tickets`, permission: [config.RESOURCES.EMPLOYEE_TICKET] },
                  { path: `/${companySlug}/admin-leave-approval`, permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT] },
                  { path: `/${companySlug}/admin-regularizatrion`, permission: [config.RESOURCES.ADMIN_REGULARIZATION] },
                ]) && (
                  <SubMenu
                    popupClassName="right-submenu"
                    onMouseEnter={() => handleSubmenuEnter("sub16")}
                    onMouseLeave={() => handleSubmenuLeave()}
                    className={
                      [
                        "/employee-master",
                        "/employee-in-out",
                        "/resignation-employee-list",
                        "/admin-reports",
                        "/employee-reports",
                        "/leave-reports",
                        "/attendence-reports",
                        "/letter",
                        "/employee-tickets",
                        "/admin-leave-approval",
                        "/admin-regularizatrion",
                        "/kpis", "/view-kpi"
                      ].some((path) => location.pathname?.toLowerCase()?.includes(path))
                        ? "main-submenu full-width-hover ant-menu-item-active"
                        : "main-submenu full-width-hover"
                    }
                    key="sub16"
                    title="Team"
                    icon={<TeamInfoIcon />}
                    onTitleClick={() =>
                      setOpenKeys(openKeys.includes("sub16") ? [] : ["sub16"])
                    }
                  >

                    {hasSidebarItemAccess({ path: `/${companySlug}/employee-master`, permission: [config.RESOURCES.EMPLOYEE_MASTER] }) && (

                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.employee-master" />
                          </span>
                        }
                        key="employee-master"
                        className={
                          matchesSubmenuPath(location.pathname, "/employee-master")
                            ? "ant-menu-item-active "
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/employee-master`}>
                          <span>
                            <IntlMessages id="sidebar.employee-master" />
                          </span>
                        </NavLink>
                      </Menu.Item>)}
                    {hasSidebarItemAccess({ path: `/${companySlug}/employee-in-out`, permission: [config.RESOURCES.EMPLOYEE_IN_OUT] }) && (

                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.Employee-In-Out" />
                          </span>
                        }
                        key="employee-in-out"
                        className={
                          matchesSubmenuPath(location.pathname, "/employee-in-out")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/employee-in-out`}>
                          <span>
                            <IntlMessages id="sidebar.Employee-In-Out" />
                          </span>
                        </NavLink>
                      </Menu.Item>
                    )}

                    {hasSidebarItemAccess({ path: `/${companySlug}/resignation-employee-list`, permission: [config.RESOURCES.SEPARATION_MANAGEMENT] }) && (

                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.resignation-employee-list" />
                          </span>
                        }
                        key="resignation-employee-list"
                        className={
                          matchesSubmenuPath(location.pathname, "/resignation-employee-list")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/resignation-employee-list`}>
                          <span>
                            <IntlMessages id="sidebar.resignation-employee-list" />
                          </span>
                        </NavLink>
                      </Menu.Item>)}


                    {hasSidebarItemAccess({ path: `/${companySlug}/admin-reports`, permission: [config.RESOURCES.EMPLOYEE_REPORT] }) && (

                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.admin-reports" />
                          </span>
                        }
                        key="admin-reports"
                        className={
                          matchesSubmenuPath(location.pathname, "/admin-reports") ||
                            matchesSubmenuPath(location.pathname, "/employee-reports") ||
                            matchesSubmenuPath(location.pathname, "/leave-reports") ||
                            matchesSubmenuPath(location.pathname, "/attendence-reports") ||
                            matchesSubmenuPath(location.pathname, "/letter")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/admin-reports`}>
                          <span>
                            <IntlMessages id="sidebar.admin-reports" />
                          </span>
                        </NavLink>
                      </Menu.Item>)}

                    {hasSidebarItemAccess({ path: `/${companySlug}/employee-tickets`, permission: [config.RESOURCES.EMPLOYEE_TICKET] }) && (

                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.tickets" />
                          </span>
                        }
                        key="tickets"
                        className={
                          matchesSubmenuPath(location.pathname, "/employee-tickets") ||
                            matchesSubmenuPath(location.pathname, "/employee-tickets")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/employee-tickets`}>
                          <span>
                            <IntlMessages id="sidebar.tickets" />
                          </span>
                        </NavLink>
                      </Menu.Item>)}

                    {hasSidebarItemAccess({ path: `/${companySlug}/admin-leave-approval`, permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT] }) && (

                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.admin-leave-management" />
                          </span>
                        }
                        key="admin-leave-approval"
                        className={
                          matchesSubmenuPath(location.pathname, "/admin-leave-approval")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/admin-leave-approval`}>
                          <span>
                            <IntlMessages id="sidebar.admin-leave-management" />
                          </span>
                        </NavLink>
                      </Menu.Item>)}

                    {hasSidebarItemAccess({ path: `/${companySlug}/admin-regularizatrion`, permission: [config.RESOURCES.ADMIN_REGULARIZATION] }) && (

                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.admin-regularizatrion" />
                          </span>
                        }
                        key="admin-regularizatrion"
                        className={
                          matchesSubmenuPath(location.pathname, "/admin-regularizatrion")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/admin-regularizatrion`}>
                          <span>
                            <IntlMessages id="sidebar.admin-regularizatrion" />
                          </span>
                        </NavLink>
                      </Menu.Item>)}
                    {/* {getResource(config.RESOURCES.KPI, ["read"]) && (

                    <Menu.Item
                      key="Kpis"
                      // className={
                      //   matchesSubmenuPath(location.pathname, "/kpis") ||
                      //     matchesSubmenuPath(location.pathname, "/kpis") ||
                      //     matchesSubmenuPath(location.pathname, "/view-kpi")
                      //     ? "ant-menu-item-active "
                      //     : ""
                      // }
                      className={
                        location.pathname?.toLowerCase().includes("/kpis") ||
                          location.pathname?.toLowerCase().includes("/view-kpi")
                          ? "ant-menu-item-active"
                          : ""
                      }

                    >

                      <NavLink to={`/${companySlug}/Kpis`}>
                        <span>
                          <IntlMessages id="sidebar.manage-kpis" />
                        </span>
                      </NavLink>
                    </Menu.Item>
                  )} */}
                  </SubMenu>
                )}

                {performanceMenu.subMenuInfo.length > 0 && <SubMenu
                  popupClassName="right-submenu"
                  onMouseEnter={() => handleSubmenuEnter("sub_performance")}
                  onMouseLeave={() => handleSubmenuLeave()}
                  className={
                    performanceMenu.subMenuInfo.some((sub) => matchesSubmenuPath(location.pathname, sub.path))
                      ? "main-submenu full-width-hover ant-menu-item-active"
                      : "main-submenu full-width-hover"
                  }
                  key="sub_performance"
                  title={<IntlMessages id="sidebar.performance_review" />}
                  icon={<FaChartLine />}
                  onTitleClick={() =>
                    setOpenKeys(openKeys.includes("sub_performance") ? [] : ["sub_performance"])
                  }
                >
                  {performanceMenu.subMenuInfo.map((subItem) => (
                    <Menu.Item
                      key={subItem.key}
                      className={location.pathname === subItem.path ? "ant-menu-item-active" : ""}
                    >
                      <NavLink to={subItem.path}>
                        <span>
                          <IntlMessages id={subItem.title} />
                        </span>
                      </NavLink>
                    </Menu.Item>
                  ))}
                </SubMenu>}

                {/* Expense Management - gated per item like every other module (hasSidebarItemAccess) */}
                {[
                  { path: `/${companySlug}/expense/dashboard`, permission: [config.RESOURCES.EXPENSE_DASHBOARD] },
                  { path: `/${companySlug}/expense/advances`, permission: [config.RESOURCES.EXPENSE_ADVANCE] },
                  { path: `/${companySlug}/expense/submit`, permission: [config.RESOURCES.EXPENSE_SUBMISSION] },
                  { path: `/${companySlug}/expense/approvals`, permission: [config.RESOURCES.EXPENSE_APPROVAL] },
                  { path: `/${companySlug}/expense/reports`, permission: [config.RESOURCES.EXPENSE_REPORTS] },
                  { path: `/${companySlug}/expense/config`, permission: [config.RESOURCES.EXPENSE_CONFIG] },
                ].some((it) => hasSidebarItemAccess(it)) && (
                    <SubMenu
                      popupClassName="right-submenu"
                      onMouseEnter={() => handleSubmenuEnter("sub_expense")}
                      onMouseLeave={() => handleSubmenuLeave()}
                      className={
                        [
                          "/expense/dashboard",
                          "/expense/advances",
                          "/expense/submit",
                          "/expense/approvals",
                          "/expense/reports",
                          "/expense/config",
                        ].some((path) => matchesSubmenuPath(location.pathname, path))
                          ? "main-submenu full-width-hover ant-menu-item-active"
                          : "main-submenu full-width-hover"
                      }
                      key="sub_expense"
                      title="Expense Management"
                      icon={<ExpenseIcon />}
                      onTitleClick={() =>
                        setOpenKeys(openKeys.includes("sub_expense") ? [] : ["sub_expense"])
                      }
                    >
                      {hasSidebarItemAccess({ path: `/${companySlug}/expense/dashboard`, permission: [config.RESOURCES.EXPENSE_DASHBOARD] }) && (
                        <Menu.Item key="expense-dashboard" className={matchesSubmenuPath(location.pathname, "/expense/dashboard") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/expense/dashboard`}><span>Expense Dashboard</span></NavLink>
                        </Menu.Item>
                      )}
                      {hasSidebarItemAccess({ path: `/${companySlug}/expense/advances`, permission: [config.RESOURCES.EXPENSE_ADVANCE] }) && (
                        <Menu.Item key="expense-advances" className={matchesSubmenuPath(location.pathname, "/expense/advances") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/expense/advances`}><span>Advance Requests</span></NavLink>
                        </Menu.Item>
                      )}
                      {hasSidebarItemAccess({ path: `/${companySlug}/expense/submit`, permission: [config.RESOURCES.EXPENSE_SUBMISSION] }) && (
                        <Menu.Item key="expense-submit" className={matchesSubmenuPath(location.pathname, "/expense/submit") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/expense/submit`}><span>Submit Expense</span></NavLink>
                        </Menu.Item>
                      )}
                      {hasSidebarItemAccess({ path: `/${companySlug}/expense/approvals`, permission: [config.RESOURCES.EXPENSE_APPROVAL] }) && (
                        <Menu.Item key="expense-approvals" className={matchesSubmenuPath(location.pathname, "/expense/approvals") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/expense/approvals`}><span>Approval Queue</span></NavLink>
                        </Menu.Item>
                      )}
                      {hasSidebarItemAccess({ path: `/${companySlug}/expense/reports`, permission: [config.RESOURCES.EXPENSE_REPORTS] }) && (
                        <Menu.Item key="expense-reports" className={matchesSubmenuPath(location.pathname, "/expense/reports") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/expense/reports`}><span>Expense Reports</span></NavLink>
                        </Menu.Item>
                      )}
                      {hasSidebarItemAccess({ path: `/${companySlug}/expense/config`, permission: [config.RESOURCES.EXPENSE_CONFIG] }) && (
                        <Menu.Item key="expense-config" className={matchesSubmenuPath(location.pathname, "/expense/config") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/expense/config`}><span>Expense Configuration</span></NavLink>
                        </Menu.Item>
                      )}
                    </SubMenu>
                  )}

                {hasPlanModuleAccess({ title: "Onboarding" }) && subAdminSeesAny([
                  { path: `/${companySlug}/company-doc-master`, permission: [config.RESOURCES.ON_BOARDING_INITIATION] },
                  { path: `/${companySlug}/candidate-list`, permission: [config.RESOURCES.CANDIDATE_APPROVAL] },
                  { path: `/${companySlug}/doc-approve-list`, permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST] },
                  { path: `/${companySlug}/new-joinees-list`, permission: [config.RESOURCES.NEW_JOINEES] },
                ]) && (
                  <SubMenu
                    popupClassName="right-submenu"
                    onMouseEnter={() => handleSubmenuEnter("sub19")}
                    onMouseLeave={() => handleSubmenuLeave()}
                    className={
                      [
                        "/candidate-approval",
                        "/candidate-list",
                        "/onboarding-initiation",
                        "/doc-approve-list",
                        "/new-joinees-list",
                        "/company-doc-master",
                      ].some((path) => matchesSubmenuPath(location.pathname, path))
                        ? "main-submenu full-width-hover ant-menu-item-active"
                        : "main-submenu full-width-hover"
                    }
                    key="sub19"
                    title="Onboarding"
                    icon={<OnboardingIcon />}
                    onTitleClick={() =>
                      setOpenKeys(openKeys.includes("sub19") ? [] : ["sub19"])
                    } // Only one submenu at a time
                  >
                    {hasSidebarItemAccess({ path: `/${companySlug}/company-doc-master`, permission: [config.RESOURCES.ON_BOARDING_INITIATION] }) && (
                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.docMaster" />
                          </span>
                        }
                        key="company-doc-master"
                        className={
                          matchesSubmenuPath(location.pathname, "/company-doc-master")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/company-doc-master`}>
                          <span>
                            <IntlMessages id="sidebar.docMaster" />
                          </span>
                        </NavLink>
                      </Menu.Item>
                    )}
                    {hasSidebarItemAccess({ path: `/${companySlug}/candidate-approval`, permission: [config.RESOURCES.ON_BOARDING_INITIATION] }) && (
                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.candidate-approvel" />
                          </span>
                        }
                        key="candidate-approvel"
                        className={
                          matchesSubmenuPath(location.pathname, "/candidate-approval")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/candidate-approval`}>
                          <span>
                            <IntlMessages id="sidebar.candidate-approvel" />
                          </span>
                        </NavLink>
                      </Menu.Item>)}
                    {hasSidebarItemAccess({ path: `/${companySlug}/candidate-list`, permission: [config.RESOURCES.CANDIDATE_APPROVAL] }) && (
                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.candidate-approvel-list" />
                          </span>
                        }
                        key="candidate-list"
                        className={
                          matchesSubmenuPath(location.pathname, "/candidate-list")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/candidate-list`}>
                          <span>
                            <IntlMessages id="sidebar.candidate-approvel-list" />
                          </span>
                        </NavLink>
                      </Menu.Item>
                    )}

                    {hasSidebarItemAccess({ path: `/${companySlug}/onboarding-initiation`, permission: [config.RESOURCES.ON_BOARDING_INITIATION] }) && (
                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.onboarding-initiation" />
                          </span>
                        }
                        key="initiation"
                        className={
                          matchesSubmenuPath(location.pathname, "/onboarding-initiation")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/onboarding-initiation`}>
                          <span>
                            <IntlMessages id="sidebar.onboarding-initiation" />
                          </span>
                        </NavLink>
                      </Menu.Item>)}

                    {hasSidebarItemAccess({ path: `/${companySlug}/doc-approve-list`, permission: [config.RESOURCES.ON_BOARDING_DOC_APPROVAL_LIST] }) && (
                      <Menu.Item
                        key="doc-approve-list"
                        title={
                          <span>
                            <IntlMessages id="sidebar.doc-approve-list" />
                          </span>
                        }
                        className={
                          matchesSubmenuPath(location.pathname, "/doc-approve-list")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/doc-approve-list`}>
                          <span>
                            <IntlMessages id="sidebar.doc-approve-list" />
                          </span>
                        </NavLink>
                      </Menu.Item>)}

                    {hasSidebarItemAccess({ path: `/${companySlug}/new-joinees-list`, permission: [config.RESOURCES.NEW_JOINEES] }) && (
                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.new-joinees-list" />
                          </span>
                        }
                        key="new-joinees-list"
                        className={
                          matchesSubmenuPath(location.pathname, "/new-joinees-list")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/new-joinees-list`}>
                          <span>
                            <IntlMessages id="sidebar.new-joinees-list" />
                          </span>
                        </NavLink>
                      </Menu.Item>)}
                  </SubMenu>
                )}

                {/* <SubMenu
                  popupClassName="right-submenu"
                  onMouseEnter={() => handleSubmenuEnter("sub20")}
                  onMouseLeave={() => handleSubmenuLeave()}
                  className={
                    [
                      "/auditmaster",
                      "/audits",
                      "/auditperformance",
                      "/auditHead",
                    ].some((path) => matchesSubmenuPath(location.pathname, path))
                      ? "main-submenu full-width-hover ant-menu-item-active"
                      : "main-submenu full-width-hover"
                  }
                  icon={<AuditIcon />}
                  key="sub20"
                  title="Audit"
                  onTitleClick={() =>
                    setOpenKeys(openKeys.includes("sub20") ? [] : ["sub20"])
                  } // Only one submenu at a time
                >
                  {(getResource(config.RESOURCES.AUDIT_MASTER, ["read"])) && (
                    <Menu.Item
                      title={
                        <span>
                          <IntlMessages id="Audits Master" />
                        </span>
                      }
                      key="Audit Master"
                      className={
                        matchesSubmenuPath(location.pathname, "/auditmaster")
                          ? "ant-menu-item-active "
                          : ""
                      }
                    >
                      <NavLink to={`/${companySlug}/auditmaster`}>
                        <span>
                          <IntlMessages id="Audits Master" />
                        </span>
                      </NavLink>
                    </Menu.Item>
                  )}
                  {(getResource(config.RESOURCES.AUDITS, ["read"])) && (
                    <Menu.Item
                      title={
                        <span>
                          <IntlMessages id="Audits" />
                        </span>
                      }
                      key="Audits"
                      className={
                        matchesSubmenuPath(location.pathname, "/audits")
                          ? "ant-menu-item-active "
                          : ""
                      }
                    >
                      <NavLink to={`/${companySlug}/audits`}>
                        <span>
                          <IntlMessages id="Audits" />
                        </span>
                      </NavLink>
                    </Menu.Item>
                  )}
                  {(getResource(config.RESOURCES.AUDITS_PERFORMANCE, ["read"])) && (
                    <Menu.Item
                      title={
                        <span>
                          <IntlMessages id="Audit Performance" />
                        </span>
                      }
                      key="auditperformance"
                      className={
                        matchesSubmenuPath(location.pathname, "/auditperformance")
                          ? "ant-menu-item-active"
                          : ""
                      }
                    >
                      <NavLink to={`/${companySlug}/auditperformance`}>
                        <span>
                          <IntlMessages id="Audit Performance" />
                        </span>
                      </NavLink>
                    </Menu.Item>
                  )}
                  {(getResource(config.RESOURCES.AUDIT_HEAD, ["read"])) && (
                    <Menu.Item
                      title={
                        <span>
                          <IntlMessages id="Audit Head" />
                        </span>
                      }
                      key="auditHead"
                      className={
                        matchesSubmenuPath(location.pathname, "/auditHead")
                          ? "ant-menu-item-active"
                          : ""
                      }
                    >
                      <NavLink to={`/${companySlug}/auditHead`}>
                        <span>
                          <IntlMessages id="Audit Head" />
                        </span>
                      </NavLink>
                    </Menu.Item>
                  )}
                </SubMenu> */}

                {(hasSidebarItemAccess({ path: `/${companySlug}/360-survey`, permission: [config.RESOURCES["SURVEY"]] }) ||
                  hasSidebarItemAccess({ path: `/${companySlug}/360-questionnaire`, permission: [config.RESOURCES["360QUE"]] }) ||
                  hasSidebarItemAccess({ path: `/${companySlug}/360-reviews`, permission: [config.RESOURCES["360REVW"]] })) &&
                  <SubMenu
                    popupClassName="right-submenu"
                    onMouseEnter={() => handleSubmenuEnter("sub22")}
                    onMouseLeave={() => handleSubmenuLeave()} //"/goals" "/inventory"
                    className={
                      ["/360-survey", "/360-questionnaire", "/360-reviews"].some((path) =>
                        matchesSubmenuPath(location.pathname, path)
                      )
                        ? "main-submenu full-width-hover ant-menu-item-active"
                        : "main-submenu full-width-hover"
                    }
                    key="sub22"
                    title="Survey"
                    icon={<i className="fas fa-poll" />}
                    onTitleClick={() => setOpenKeys(openKeys.includes("sub22") ? [] : ["sub22"])}
                  >
                    {hasSidebarItemAccess({ path: `/${companySlug}/360-survey`, permission: [config.RESOURCES["SURVEY"]] }) &&
                      <Menu.Item
                        title={<span><IntlMessages id="360_survey" /></span>}
                        key="360_survey"
                        className={
                          location.pathname?.includes("360-survey")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/360-survey`}>
                          <span>
                            <IntlMessages id="sidebar.360_survey" />
                          </span>
                        </NavLink>
                      </Menu.Item>}
                    {hasSidebarItemAccess({ path: `/${companySlug}/360-questionnaire`, permission: [config.RESOURCES["360QUE"]] }) &&

                      <Menu.Item
                        title={<span><IntlMessages id="360_questionnaire" /></span>}
                        key="questionnaire"
                        className={
                          location.pathname?.includes("360-questionnaire")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/360-questionnaire`}>
                          <span>
                            <IntlMessages id="sidebar.360_questionnaire" />
                          </span>
                        </NavLink>
                      </Menu.Item>}
                    {hasSidebarItemAccess({ path: `/${companySlug}/360-reviews`, permission: [config.RESOURCES["360REVW"]] }) &&
                      <Menu.Item
                        title={<span><IntlMessages id="360_reviews" /></span>}
                        key="360 Reviews"
                        className={
                          matchesSubmenuPath(location.pathname, "/360-reviews")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/360-reviews`}>
                          <span>
                            <IntlMessages id="sidebar.360_reviews" />
                          </span>
                        </NavLink>
                      </Menu.Item>}
                  </SubMenu>
                }

                {/* ─── LMS (Learning Management) – Admin: gated by LMS permissions ──────────── */}
                {/* Sub-Admin: only its non-settings LMS screens count (settings are hidden). */}
                {(!isSubAdmin || [
                  "LMS_ENROLLMENT", "LMS_MY_LEARNING", "LMS_CERTIFICATES", "LMS_TEAM_LEARNING", "LMS_REPORTS", "LMS_ASSESSMENT_MANAGEMENT",
                ].some((resource) => getResource(config.RESOURCES[resource], ["read"]))) && (
                  getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) ||
                  getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) ||
                  getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) ||
                  getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) ||
                  getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) ||
                  getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) ||
                  getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) ||
                  getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) ||
                  getResource(config.RESOURCES["LMS_REPORTS"], ["read"])
                ) && (
                    <SubMenu
                      popupClassName="right-submenu"
                      onMouseEnter={() => handleSubmenuEnter("lms_module_admin")}
                      onMouseLeave={() => handleSubmenuLeave()}
                      className={
                        [
                          "/lms/courses", "/lms/assessments", "/lms/enrollments",
                          "/lms/my-learning", "/lms/certificates", "/lms/learning-paths",
                          "/lms/auto-assignment", "/lms/team-learning", "/lms/reports",
                          "/lms/grading", "/lms/settings/data-retention",
                          "/lms/settings/certificate-template",
                        ].some((p) => matchesSubmenuPath(location.pathname, p))
                          ? "main-submenu full-width-hover ant-menu-item-active"
                          : "main-submenu full-width-hover"
                      }
                      key="lms_module_admin"
                      title="Learning"
                      icon={<i className="fas fa-graduation-cap" />}
                      onTitleClick={() => setOpenKeys(openKeys.includes("lms_module_admin") ? [] : ["lms_module_admin"])}
                    >
                      {!isHiddenForSubAdmin(`/${companySlug}/lms/courses`) && getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["read"]) && hasPlanModuleAccess({ path: `/${companySlug}/lms/courses` }) && (
                        <Menu.Item key="lms-courses" className={matchesSubmenuPath(location.pathname, "/lms/courses") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/lms/courses`}><span>Course Management</span></NavLink>
                        </Menu.Item>
                      )}
                      {!isHiddenForSubAdmin(`/${companySlug}/lms/assessments`) && getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) && hasPlanModuleAccess({ path: `/${companySlug}/lms/assessments` }) && (
                        <Menu.Item key="lms-assessments" className={matchesSubmenuPath(location.pathname, "/lms/assessments") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/lms/assessments`}><span>Assessments</span></NavLink>
                        </Menu.Item>
                      )}
                      {getResource(config.RESOURCES["LMS_ENROLLMENT"], ["read"]) && (
                        <Menu.Item key="lms-enrollments" className={matchesSubmenuPath(location.pathname, "/lms/enrollments") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/lms/enrollments`}><span>Enrollments</span></NavLink>
                        </Menu.Item>
                      )}
                      {getResource(config.RESOURCES["LMS_MY_LEARNING"], ["read"]) && (
                        <Menu.Item key="lms-my-learning" className={matchesSubmenuPath(location.pathname, "/lms/my-learning") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/lms/my-learning`}><span>My Learning</span></NavLink>
                        </Menu.Item>
                      )}
                      {getResource(config.RESOURCES["LMS_CERTIFICATES"], ["read"]) && (
                        <Menu.Item key="lms-certificates" className={matchesSubmenuPath(location.pathname, "/lms/certificates") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/lms/certificates`}><span>Certificates</span></NavLink>
                        </Menu.Item>
                      )}
                      {!isHiddenForSubAdmin(`/${companySlug}/lms/learning-paths`) && getResource(config.RESOURCES["LMS_LEARNING_PATH"], ["read"]) && (
                        <Menu.Item key="lms-learning-paths" className={matchesSubmenuPath(location.pathname, "/lms/learning-paths") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/lms/learning-paths`}><span>Learning Paths</span></NavLink>
                        </Menu.Item>
                      )}
                      {!isHiddenForSubAdmin(`/${companySlug}/lms/auto-assignment`) && getResource(config.RESOURCES["LMS_AUTO_ASSIGNMENT"], ["read"]) && (
                        <Menu.Item key="lms-auto-assignment" className={matchesSubmenuPath(location.pathname, "/lms/auto-assignment") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/lms/auto-assignment`}><span>Auto-Assignment</span></NavLink>
                        </Menu.Item>
                      )}
                      {getResource(config.RESOURCES["LMS_TEAM_LEARNING"], ["read"]) && (
                        <Menu.Item key="lms-team-learning" className={matchesSubmenuPath(location.pathname, "/lms/team-learning") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/lms/team-learning`}><span>Team Learning</span></NavLink>
                        </Menu.Item>
                      )}
                      {getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) && hasPlanModuleAccess({ path: `/${companySlug}/lms/reports` }) && (
                        <Menu.Item key="lms-reports" className={matchesSubmenuPath(location.pathname, "/lms/reports") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/lms/reports`}><span>LMS Reports</span></NavLink>
                        </Menu.Item>
                      )}
                      {getResource(config.RESOURCES["LMS_ASSESSMENT_MANAGEMENT"], ["read"]) && hasPlanModuleAccess({ path: `/${companySlug}/lms/grading` }) && (
                        <Menu.Item key="lms-grading" className={matchesSubmenuPath(location.pathname, "/lms/grading") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/lms/grading`}><span>Manual Grading</span></NavLink>
                        </Menu.Item>
                      )}
                      {!isHiddenForSubAdmin(`/${companySlug}/lms/settings/data-retention`) && getResource(config.RESOURCES["LMS_REPORTS"], ["read"]) && hasPlanModuleAccess({ path: `/${companySlug}/lms/settings/data-retention` }) && (
                        <Menu.Item key="lms-data-retention" className={matchesSubmenuPath(location.pathname, "/lms/settings/data-retention") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/lms/settings/data-retention`}><span>Data Retention</span></NavLink>
                        </Menu.Item>
                      )}
                      {!isHiddenForSubAdmin(`/${companySlug}/lms/settings/certificate-template`) && getResource(config.RESOURCES["LMS_COURSE_MANAGEMENT"], ["update"]) && hasPlanModuleAccess({ path: `/${companySlug}/lms/settings/certificate-template` }) && (
                        <Menu.Item key="lms-cert-template" className={matchesSubmenuPath(location.pathname, "/lms/settings/certificate-template") ? "ant-menu-item-active" : ""}>
                          <NavLink to={`/${companySlug}/lms/settings/certificate-template`}><span>Certificate Template</span></NavLink>
                        </Menu.Item>
                      )}
                    </SubMenu>
                  )}


                {/* ─── Talent Development (merged Skills + Career Growth Path) ─── */}
                {(getResource(config.RESOURCES["SKILL"], ["read"]) || getResource(config.RESOURCES["CAREER_PATH"], ["read"])) && (
                  <SubMenu
                    popupClassName="right-submenu"
                    onMouseEnter={() => handleSubmenuEnter("talent_development_sidebar")}
                    onMouseLeave={() => handleSubmenuLeave()}
                    className={
                      ["/talent-development", "/skills", "/skills-matrix", "/skill-inventory", "/role-skill-requirements", "/skill-gap-analysis", "/career-paths", "/employee-career-paths", "/employee-career-view", "/career-readiness", "/employee-competencies", "/course-skill-mapping"].some((p) => matchesSubmenuPath(location.pathname, p))
                        ? "main-submenu full-width-hover ant-menu-item-active"
                        : "main-submenu full-width-hover"
                    }
                    key="talent_development_sidebar"
                    title="Talent Development"
                    icon={<i className="fas fa-user-graduate" />}
                    onTitleClick={() => setOpenKeys(openKeys.includes("talent_development_sidebar") ? [] : ["talent_development_sidebar"])}
                  >
                    {getResource(config.RESOURCES["SKILL"], ["read"]) && (
                      <Menu.Item
                        key="talent-development-skill"
                        className={
                          matchesSubmenuPath(location.pathname, "/talent-development") && !location.search.includes("module=career")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/talent-development?module=skill`}><span>Skill</span></NavLink>
                      </Menu.Item>
                    )}
                    {getResource(config.RESOURCES["CAREER_PATH"], ["read"]) && (
                      <Menu.Item
                        key="talent-development-career"
                        className={
                          matchesSubmenuPath(location.pathname, "/talent-development") && location.search.includes("module=career")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/talent-development?module=career`}><span>Career Growth Plan</span></NavLink>
                      </Menu.Item>
                    )}
                  </SubMenu>
                )}

                {/* ─── Engage — Announcements moved out of "More" ─── */}
                {(() => {
                  const canSeeEngageAnnouncements = hasSidebarItemAccess({ path: `/${companySlug}/engage/announcements`, permission: [] });
                  // Celebrations is NOT plan-gated or permission-gated — no RBAC
                  // resource, always shows, by design (2026-09-15). Not derived from
                  // hasSidebarItemAccess (which would still run the plan check via
                  // pathToSidebarModuleSlug) so it also keeps the parent "Engage"
                  // SubMenu open when Celebrations is the only thing enabled.
                  const canSeeEngageCelebrations = true;
                  const canSeeEngageWall = hasSidebarItemAccess({ path: `/${companySlug}/engage/wall`, permission: [config.RESOURCES.ENGAGE_WALL] });
                  // Awards/Badges/Moderation/Settings are the "Engage Panel" admin
                  // surfaces — gated separately from Wall itself (engage-wall-rbac-split).
                  // path must be one pathToSidebarModuleSlug actually resolves to the
                  // "wall" plan-module slug (/engage/awards, /engage/badges,
                  // /engage/moderation, /engage/settings, /engage/wall — see its
                  // /engage/* cases above) — /engage/overview was NOT in that list, so
                  // Step 1's plan/module pre-check silently failed before Step 2's real
                  // ENGAGE_PANEL RBAC check ever ran, hiding Awards/Badges/Moderation/
                  // Settings from this flyout for every user whose company has any plan
                  // module config, even when their role genuinely has ENGAGE_PANEL
                  // access (confirmed live: all 4 pages load real data when navigated to
                  // directly, and the top tab bar — SubmenuTabs.js, which correctly uses
                  // each item's own real path — shows all of them).
                  const canSeeEngagePanel = hasSidebarItemAccess({ path: `/${companySlug}/engage/awards`, permission: [config.RESOURCES.ENGAGE_PANEL] });
                  return (
                    (canSeeEngageAnnouncements || canSeeEngageCelebrations || canSeeEngageWall || canSeeEngagePanel) && (
                      <SubMenu
                        popupClassName="right-submenu"
                        onMouseEnter={() => handleSubmenuEnter("engage")}
                        onMouseLeave={() => handleSubmenuLeave()}
                        className={
                          location.pathname.toLowerCase().includes("/engage/")
                            ? "main-submenu full-width-hover ant-menu-item-active"
                            : "main-submenu full-width-hover"
                        }
                        key="engage"
                        title="Engage"
                        icon={<i className="fas fa-bullhorn" />}
                        onTitleClick={() =>
                          setOpenKeys(openKeys.includes("engage") ? [] : ["engage"])
                        }
                      >
                        {/* Overview is the Wall/Awards/Badges dashboard — it belongs to the
                      Wall & Recognition cluster (canSeeEngagePanel), not to Announcements
                      or Celebrations alone. Enabling only Announcements must not surface it. */}
                        {canSeeEngagePanel && (
                          <Menu.Item
                            title={
                              <span>
                                <IntlMessages id="sidebar.engageOverview" />
                              </span>
                            }
                            key="engage-overview"
                            className={
                              matchesSubmenuPath(location.pathname, "/engage/overview")
                                ? "ant-menu-item-active"
                                : ""
                            }
                          >
                            <NavLink to={`/${companySlug}/engage/overview`}>
                              <span>
                                <IntlMessages id="sidebar.engageOverview" />
                              </span>
                            </NavLink>
                          </Menu.Item>
                        )}
                        {canSeeEngageAnnouncements && (
                          <Menu.Item
                            title={
                              <span>
                                <IntlMessages id="sidebar.announcement" />
                              </span>
                            }
                            key="engage-announcements"
                            className={
                              matchesSubmenuPath(location.pathname, "/engage/announcements")
                                ? "ant-menu-item-active"
                                : ""
                            }
                          >
                            <NavLink to={`/${companySlug}/engage/announcements`}>
                              <span>
                                <IntlMessages id="sidebar.announcement" />
                              </span>
                            </NavLink>
                          </Menu.Item>
                        )}
                        {/* Celebrations is NOT plan-gated or permission-gated — no RBAC
                      resource, always shows, by design (2026-09-15). Intentionally
                      not conditioned on canSeeEngageCelebrations (which still runs
                      the plan check via pathToSidebarModuleSlug). */}
                        <Menu.Item
                          title={
                            <span>
                              <IntlMessages id="sidebar.celebrations" />
                            </span>
                          }
                          key="engage-celebrations"
                          className={
                            matchesSubmenuPath(location.pathname, "/engage/celebrations")
                              ? "ant-menu-item-active"
                              : ""
                          }
                        >
                          <NavLink to={`/${companySlug}/engage/celebrations`}>
                            <span>
                              <IntlMessages id="sidebar.celebrations" />
                            </span>
                          </NavLink>
                        </Menu.Item>
                        {canSeeEngagePanel && !isHiddenForSubAdmin(`/${companySlug}/engage/awards`) && (
                          <Menu.Item
                            title={
                              <span>
                                <IntlMessages id="sidebar.awards" />
                              </span>
                            }
                            key="engage-awards"
                            className={
                              matchesSubmenuPath(location.pathname, "/engage/awards")
                                ? "ant-menu-item-active"
                                : ""
                            }
                          >
                            <NavLink to={`/${companySlug}/engage/awards`}>
                              <span>
                                <IntlMessages id="sidebar.awards" />
                              </span>
                            </NavLink>
                          </Menu.Item>
                        )}
                        {canSeeEngagePanel && !isHiddenForSubAdmin(`/${companySlug}/engage/badges`) && (
                          <Menu.Item
                            title={
                              <span>
                                <IntlMessages id="sidebar.badges" />
                              </span>
                            }
                            key="engage-badges"
                            className={
                              matchesSubmenuPath(location.pathname, "/engage/badges")
                                ? "ant-menu-item-active"
                                : ""
                            }
                          >
                            <NavLink to={`/${companySlug}/engage/badges`}>
                              <span>
                                <IntlMessages id="sidebar.badges" />
                              </span>
                            </NavLink>
                          </Menu.Item>
                        )}
                        {canSeeEngageWall && (
                          <Menu.Item
                            title={
                              <span>
                                <IntlMessages id="sidebar.engageWall" />
                              </span>
                            }
                            key="engage-wall"
                            className={
                              matchesSubmenuPath(location.pathname, "/engage/wall")
                                ? "ant-menu-item-active"
                                : ""
                            }
                          >
                            <NavLink to={`/${companySlug}/engage/wall`}>
                              <span>
                                <IntlMessages id="sidebar.engageWall" />
                              </span>
                            </NavLink>
                          </Menu.Item>
                        )}
                        {canSeeEngagePanel && !isHiddenForSubAdmin(`/${companySlug}/engage/moderation`) && (
                          <Menu.Item
                            title={
                              <span>
                                <IntlMessages id="sidebar.engageModeration" />
                              </span>
                            }
                            key="engage-moderation"
                            className={
                              matchesSubmenuPath(location.pathname, "/engage/moderation")
                                ? "ant-menu-item-active"
                                : ""
                            }
                          >
                            <NavLink to={`/${companySlug}/engage/moderation`}>
                              <span>
                                <IntlMessages id="sidebar.engageModeration" />
                              </span>
                            </NavLink>
                          </Menu.Item>
                        )}
                        {canSeeEngagePanel && !isHiddenForSubAdmin(`/${companySlug}/engage/settings`) && (
                          <Menu.Item
                            title={
                              <span>
                                <IntlMessages id="sidebar.engageSettings" />
                              </span>
                            }
                            key="engage-settings"
                            className={
                              matchesSubmenuPath(location.pathname, "/engage/settings")
                                ? "ant-menu-item-active"
                                : ""
                            }
                          >
                            <NavLink to={`/${companySlug}/engage/settings`}>
                              <span>
                                <IntlMessages id="sidebar.engageSettings" />
                              </span>
                            </NavLink>
                          </Menu.Item>
                        )}
                      </SubMenu>
                    )
                  );
                })()}

                {/* ─── More (kept last, per sidebar ordering) ─── */}
                {hasPlanModuleAccess({ title: "More" }) && subAdminSeesAny([
                  { path: `/${companySlug}/penalty-revoke`, permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT] },
                  { path: `/${companySlug}/mood-list`, permission: [config.RESOURCES.HOLIDAYS] },
                  { path: `/${companySlug}/jobs/openings`, permission: [config.RESOURCES.JOB_OPENING] },
                  { path: `/${companySlug}/inventory`, permission: [config.RESOURCES.INVENTORY] },
                  { path: `/${companySlug}/admin-bill-reimbursement/submit`, permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL] },
                ]) && (
                  <SubMenu
                    popupClassName="right-submenu"
                    onMouseEnter={() => handleSubmenuEnter("sub18")}
                    onMouseLeave={() => handleSubmenuLeave()}
                    className={
                      [
                        // "/holidays", // Moved to Settings Dropdown
                        // "/policies", // Moved to Settings Dropdown
                        "/goals",
                        "/jobs/openings",
                        "/inventory",
                        "/employee-leave-balance",
                        "/admin-bill-reimbursement/submit",
                        "/penalty-revoke",
                        "/mood-list"
                      ].some((path) => matchesSubmenuPath(location.pathname, path))
                        ? "main-submenu full-width-hover ant-menu-item-active"
                        : "main-submenu full-width-hover"
                    }
                    key="sub18"
                    title="More"
                    icon={<i className="fas fa-ellipsis-h" />}
                    onTitleClick={() =>
                      setOpenKeys(openKeys.includes("sub18") ? [] : ["sub18"])
                    }
                  >

                    {hasSidebarItemAccess({ path: `/${companySlug}/penalty-revoke`, permission: [config.RESOURCES.ADMIN_LEAVE_MANAGEMENT] }) && (
                      <Menu.Item
                        key="penalty-revoke"
                        title={<span><IntlMessages id="sidebar.penalty-revoke" /></span>}
                        className={matchesSubmenuPath(location.pathname, "/penalty-revoke") ? "ant-menu-item-active" : ""}
                      >
                        <NavLink to={`/${companySlug}/penalty-revoke`}>
                          <span><IntlMessages id="sidebar.penalty-revoke" /></span>
                        </NavLink>
                      </Menu.Item>
                    )}

                    {hasSidebarItemAccess({ path: `/${companySlug}/mood-list`, permission: [config.RESOURCES.HOLIDAYS] }) && (
                      <>
                        <Menu.Item
                          key="mood-analytics"
                          title={<span><IntlMessages id="mood-analytics" /></span>}
                          className={matchesSubmenuPath(location.pathname, "/mood-list") ? "ant-menu-item-active" : ""}
                        >
                          <NavLink to={`/${companySlug}/mood-list`}>
                            <span><IntlMessages id="Mood Analytics" /></span>
                          </NavLink>
                        </Menu.Item>
                      </>
                    )}

                    {hasSidebarItemAccess({ path: `/${companySlug}/jobs/openings`, permission: [config.RESOURCES.JOB_OPENING] }) && (

                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.admin-jobopening" />
                          </span>
                        }
                        key="Job Opening"
                        className={
                          matchesSubmenuPath(location.pathname, "/jobs/openings")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/jobs/openings`}>
                          <span>
                            <IntlMessages id="sidebar.admin-jobopening" />
                          </span>
                        </NavLink>
                      </Menu.Item>)}

                    {hasSidebarItemAccess({ path: `/${companySlug}/inventory`, permission: [config.RESOURCES.INVENTORY] }) && (

                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.user-assetstabpane" />
                          </span>
                        }
                        key="inventory"
                        className={
                          matchesSubmenuPath(location.pathname, "/inventory")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/inventory`}>
                          <span>
                            <IntlMessages id="sidebar.user-assetstabpane" />
                          </span>
                        </NavLink>
                      </Menu.Item>)}

                    {hasSidebarItemAccess({ path: `/${companySlug}/admin-bill-reimbursement/submit`, permission: [config.RESOURCES.BILLREIMBURSEMENT_APPROVAL] }) && (
                      <Menu.Item
                        title={
                          <span>
                            <IntlMessages id="sidebar.admin-bill-reimbursement" />
                          </span>
                        }
                        key="admin-bill-reimbursement"
                        className={
                          location.pathname?.includes("/admin-bill-reimbursement/submit")
                            ? "ant-menu-item-active"
                            : ""
                        }
                      >
                        <NavLink to={`/${companySlug}/admin-bill-reimbursement/submit`}>
                          <span>
                            <IntlMessages id="sidebar.admin-bill-reimbursement" />
                          </span>
                        </NavLink>
                      </Menu.Item>
                    )}

                  </SubMenu>
                )}

                {/* Organization Tree moved to the header, next to the Settings icon - see Topbar/index.js */}


              </Menu>
            </CustomScrollbars>
          </div>
        ) : getRoles(["Reporting Manager"]) ? (
          <div className="gx-sidebar-content sidebar-menu">
            <CustomScrollbars className="gx-layout-sider-scrollbar">
              <Menu
                openKeys={openKeys}
                selectedKeys={[selectedKeys]}
                theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
                mode="vertical"
                triggerSubMenuAction="hover"
                onOpenChange={(keys) =>
                  setOpenKeys(keys.length ? [keys[keys.length - 1]] : [])
                } // Ensure only one submenu is open
                onMouseEnter={(e) => {
                  const submenuKey = e.key;
                  if (submenuKey && !openKeys.includes(submenuKey)) {
                    setOpenKeys([...openKeys, submenuKey]);
                  }
                }}
              >
                {[...(Reporting_Manager || []).slice(0, 1), analyticsEntry, ...(Reporting_Manager || []).slice(1)].map((item) => {
                  if (item.submenu) {
                    const permission =
                      item?.permission?.length > 0
                        ? item.permission.some((resourceName) => getResource(resourceName, ["read"]))
                        : hasVisibleSubmenuItems(item);
                    return (
                      permission && (
                        <SubMenu
                          popupClassName="right-submenu"
                          onMouseEnter={() => handleSubmenuEnter(item.key)}
                          onMouseLeave={() => handleSubmenuLeave()}
                          className={
                            item.subMenuInfo?.some((sub) =>
                              matchesSubmenuPath(location.pathname, sub.path)
                            )
                              ? `${item.className || ""
                              } full-width-hover ant-menu-item-active`
                              : `${item.className || ""} full-width-hover`
                          }
                          key={item.key}
                          icon={item.icon}
                          title={item.title}
                          onTitleClick={() =>
                            setOpenKeys(
                              openKeys.includes(item.key) ? [] : [item.key]
                            )
                          }
                        >
                          {item.subMenuInfo.map((subItem) => {
                            const submenupermission = hasSidebarItemAccess(subItem);
                            return (
                              submenupermission && (
                                <Menu.Item
                                  title={
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  }
                                  key={subItem.key}
                                  className={
                                    location.pathname == subItem.path
                                      ? "ant-menu-item-active"
                                      : ""
                                  }
                                >
                                  <NavLink
                                    to={{
                                      pathname: subItem.path,
                                      state: { parentMenuKey: item.key }
                                    }}
                                  >
                                    {subItem.icon}
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  </NavLink>
                                </Menu.Item>
                              )
                            );
                          })}
                        </SubMenu>
                      )
                    );
                  } else {
                    const permission = hasSidebarItemAccess(item);
                    return (
                      permission && (
                        <Menu.Item
                          title={
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          }
                          key={item.key}
                          className={
                            matchesSubmenuPath(location.pathname, item.path)
                              ? "ant-menu-item-active"
                              : ""
                          }
                        >
                          <NavLink to={item.path}>
                            {item.icon}
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          </NavLink>
                        </Menu.Item>
                      )
                    );
                  }
                })}
              </Menu>
            </CustomScrollbars>
          </div>
        ) : getRoles(["Assistance Reporting Manager"]) ? (
          <div className="gx-sidebar-content sidebar-menu">
            <CustomScrollbars className="gx-layout-sider-scrollbar">
              <Menu
                mode="vertical"
                openKeys={openKeys}
                selectedKeys={[selectedKeys]}
                theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
                triggerSubMenuAction="hover"
                onMouseEnter={(e) => {
                  const submenuKey = e.key;
                  if (submenuKey && !openKeys.includes(submenuKey)) {
                    setOpenKeys([...openKeys, submenuKey]);
                  }
                }}
                onOpenChange={(keys) =>
                  setOpenKeys(keys.length ? [keys[keys.length - 1]] : [])
                }
              >
                {[...(Assistant_Reporting_Manager || []).slice(0, 1), analyticsEntry, ...(Assistant_Reporting_Manager || []).slice(1)].map((item) => {
                  const permission = item?.submenu
                    ? hasVisibleSubmenuItems(item)
                    : hasSidebarItemAccess(item);
                  console.log(item, 'itemitemitem');

                  if (item.submenu) {
                    return (
                      permission && (
                        <SubMenu
                          popupClassName="right-submenu"
                          key={item.key}
                          title={item.title}
                          icon={item.icon}
                          onMouseEnter={() => handleSubmenuEnter(item.key)}
                          onMouseLeave={() => handleSubmenuLeave()}
                          className={
                            item.subMenuInfo?.some((sub) =>
                              matchesSubmenuPath(location.pathname, sub.path)
                            )
                              ? `${item.className} full-width-hover ant-menu-item-active`
                              : `${item.className} full-width-hover`
                          }
                          onTitleClick={() =>
                            setOpenKeys(
                              openKeys.includes(item.key) ? [] : [item.key]
                            )
                          } // Only one submenu at a time
                        >
                          {item.subMenuInfo.map((subItem) => {
                            const submenupermission = hasSidebarItemAccess(subItem);
                            return (
                              submenupermission && (
                                <Menu.Item
                                  title={
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  }
                                  key={subItem.key}
                                  className={
                                    location.pathname == subItem.path
                                      ? "ant-menu-item-active"
                                      : ""
                                  }
                                >
                                  <NavLink
                                    to={{
                                      pathname: subItem.path,
                                      state: { parentMenuKey: item.key }
                                    }}
                                  >
                                    {subItem.icon}
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  </NavLink>
                                </Menu.Item>
                              )
                            );
                          })}
                        </SubMenu>
                      )
                    );
                  } else {
                    const permission = hasSidebarItemAccess(item);
                    return (
                      permission && (
                        <Menu.Item
                          title={
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          }
                          key={item.key}
                          className={
                            matchesSubmenuPath(location.pathname, item.path)
                              ? "ant-menu-item-active"
                              : ""
                          }
                        >
                          <NavLink to={item.path}>
                            {item.icon}
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          </NavLink>
                        </Menu.Item>
                      )
                    );
                  }
                })}
              </Menu>
            </CustomScrollbars>
          </div>
        ) : getRoles(["Network Team"]) ? (
          <div className="gx-sidebar-content sidebar-menu">
            <CustomScrollbars className="gx-layout-sider-scrollbar">
              <Menu
                openKeys={openKeys}
                selectedKeys={[selectedKeys]}
                theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
                mode="vertical"
                triggerSubMenuAction="hover"
                onMouseEnter={(e) => {
                  const submenuKey = e.key;
                  if (submenuKey && !openKeys.includes(submenuKey)) {
                    setOpenKeys([...openKeys, submenuKey]);
                  }
                }}
                onOpenChange={(keys) =>
                  setOpenKeys(keys.length ? [keys[keys.length - 1]] : [])
                }
              >
                {[...(Network_Team || []).slice(0, 1), analyticsEntry, ...(Network_Team || []).slice(1)].map((item) => {
                  if (item.submenu) {
                    const permission =
                      item?.permission?.length > 0
                        ? item.permission.some((resourceName) => getResource(resourceName, ["read"]))
                        : hasVisibleSubmenuItems(item);
                    return (
                      permission && (
                        <SubMenu
                          icon={item.icon}
                          key={item.key}
                          title={item.title}
                          expandIcon={item.expandIcon}
                          onMouseEnter={() => handleSubmenuEnter(item.key)}
                          onMouseLeave={() => handleSubmenuLeave()}
                          className={
                            item.subMenuInfo?.some((sub) =>
                              matchesSubmenuPath(location.pathname, sub.path)
                            )
                              ? `${item.className} full-width-hover ant-menu-item-active`
                              : `${item.className} full-width-hover`
                          }
                          onTitleClick={() =>
                            setOpenKeys(
                              openKeys.includes(item.key) ? [] : [item.key]
                            )
                          }
                        >
                          {item.subMenuInfo.map((subItem) => {
                            const submenupermission = hasSidebarItemAccess(subItem);
                            return (
                              submenupermission && (
                                <Menu.Item
                                  title={
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  }
                                  key={subItem.key}
                                  className={
                                    location.pathname == subItem.path
                                      ? "ant-menu-item-active"
                                      : ""
                                  }
                                >
                                  <NavLink
                                    to={{
                                      pathname: subItem.path,
                                      state: { parentMenuKey: item.key }
                                    }}
                                  >
                                    {subItem.icon}
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  </NavLink>
                                </Menu.Item>
                              )
                            );
                          })}
                        </SubMenu>
                      )
                    );
                  } else {
                    const permission = hasSidebarItemAccess(item);
                    return (
                      permission && (
                        <Menu.Item
                          title={
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          }
                          key={item.key}
                          className={
                            matchesSubmenuPath(location.pathname, item.path)
                              ? "ant-menu-item-active"
                              : ""
                          }
                        >
                          <NavLink to={item.path}>
                            {item.icon}
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          </NavLink>
                        </Menu.Item>
                      )
                    );
                  }
                })}
              </Menu>
            </CustomScrollbars>
          </div>
        ) : getRoles(["User"]) ? (
          <div className="gx-sidebar-content sidebar-menu">
            <CustomScrollbars className="gx-layout-sider-scrollbar">
              <Menu
                openKeys={openKeys}
                selectedKeys={[selectedKeys]}
                theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
                mode="vertical"
                triggerSubMenuAction="hover"
                onMouseEnter={(e) => {
                  const submenuKey = e.key;
                  if (submenuKey && !openKeys.includes(submenuKey)) {
                    setOpenKeys([...openKeys, submenuKey]);
                  }
                }}
                onOpenChange={(keys) =>
                  setOpenKeys(keys.length ? [keys[keys.length - 1]] : [])
                }
              >
                {[...(User || []).slice(0, 1), analyticsEntry, ...(User || []).slice(1)].map((item) => {
                  let billKey = item.key === "Bill Reimbursement";
                  let billrembmt = false;
                  const permission = item?.submenu
                    ? hasVisibleSubmenuItems(item)
                    : hasSidebarItemAccess(item);
                  if (billKey) {
                    billrembmt = true;
                  } else {
                    billrembmt = false;
                  }

                  if (item.submenu) {
                    // Use location.state to determine which menu should be active
                    // This ensures that when clicking from a specific menu, only that menu shows as active
                    const parentMenuKey = location.state?.parentMenuKey;

                    let shouldBeActive;

                    if (parentMenuKey) {
                      // If we have a tracked parent menu key, only check this specific menu
                      // This ensures clicking from "Me" menu only shows "Me" as active, not "Audit"
                      if (item.key === parentMenuKey) {
                        // Check if pathname matches this submenu's items
                        shouldBeActive = item.subMenuInfo?.some((sub) =>
                          matchesSubmenuPath(location.pathname, sub.path)
                        );
                      } else {
                        // If this is not the tracked parent menu, don't show as active
                        shouldBeActive = false;
                      }
                    } else {
                      // Fallback: Check if pathname matches this submenu's items
                      shouldBeActive = item.subMenuInfo?.some((sub) =>
                        matchesSubmenuPath(location.pathname, sub.path)
                      );

                      // Fallback: Use priority logic when no parent menu key is tracked
                      // Special handling for /goals: prioritize "Me" submenu over "More" submenu
                      if (matchesSubmenuPath(location.pathname, '/my-goals')) {
                        if (item.key === "sub18") {
                          // If this is "More" submenu and /goals exists in "Me" submenu, don't show "More" as active
                          const meSubmenu = User?.find(menuItem =>
                            menuItem.submenu &&
                            (menuItem.key === "Me" || menuItem.key === "sub15") &&
                            menuItem.subMenuInfo?.some((sub) =>
                              sub.path && matchesSubmenuPath(location.pathname, sub.path)
                            )
                          );
                          if (meSubmenu) {
                            shouldBeActive = false;
                          }
                        }
                        // If this is "Me" submenu and path matches, it should be active (priority over "More")
                        // This is already handled by the initial shouldBeActive check above
                      }

                      // Special handling: For "Audit" submenu (sub20), don't show as active if /my-audit-history is in "Me" submenu
                      if (item.key === "sub20" && matchesSubmenuPath(location.pathname, '/my-audit-history')) {
                        const meSubmenu = User?.find(menuItem =>
                          menuItem.submenu &&
                          (menuItem.key === "Me" || menuItem.key === "sub15") &&
                          menuItem.subMenuInfo?.some((sub) =>
                            sub.path && matchesSubmenuPath(location.pathname, sub.path)
                          )
                        );
                        // If "Me" submenu has /my-audit-history, don't show "Audit" as active
                        if (meSubmenu) {
                          shouldBeActive = false;
                        }
                      }
                    }

                    return (
                      permission && (
                        <SubMenu
                          icon={item.icon}
                          key={item.key}
                          title={item.title}
                          expandIcon={item.expandIcon}
                          onMouseEnter={() => handleSubmenuEnter(item.key)}
                          onMouseLeave={() => handleSubmenuLeave()}
                          className={
                            shouldBeActive
                              ? `${item.className} full-width-hover ant-menu-item-active`
                              : `${item.className} full-width-hover`
                          }
                          onTitleClick={() =>
                            setOpenKeys(
                              openKeys.includes(item.key) ? [] : [item.key]
                            )
                          }
                        >
                          {item.subMenuInfo.map((subItem) => {
                            const submenupermission = hasSidebarItemAccess(subItem);
                            return (
                              submenupermission && (
                                <Menu.Item
                                  title={
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  }
                                  key={subItem.key}
                                  className={
                                    location.pathname == subItem.path
                                      ? "ant-menu-item-active"
                                      : ""
                                  }
                                >
                                  <NavLink
                                    to={{
                                      pathname: subItem.path,
                                      state: { parentMenuKey: item.key }
                                    }}
                                  >
                                    {subItem.icon}
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  </NavLink>
                                </Menu.Item>
                              )
                            );
                          })}
                        </SubMenu>
                      )
                    );
                  } else {
                    return (
                      (permission || billrembmt) && (
                        <Menu.Item
                          title={
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          }
                          key={item.key}
                          className={
                            matchesSubmenuPath(location.pathname, item.path)
                              ? "ant-menu-item-active"
                              : ""
                          }
                        >
                          <NavLink to={item.path}>
                            {item.icon}
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          </NavLink>
                        </Menu.Item>
                      )
                    );
                  }
                })}
              </Menu>
            </CustomScrollbars>
          </div>
        ) : getRoles(["Auditor"]) ? (
          <div className="gx-sidebar-content sidebar-menu">
            <CustomScrollbars className="gx-layout-sider-scrollbar">
              <Menu
                openKeys={openKeys}
                selectedKeys={[selectedKeys]}
                theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
                mode="vertical"
                triggerSubMenuAction="hover"
                onMouseEnter={(e) => {
                  const submenuKey = e.key;
                  if (submenuKey && !openKeys.includes(submenuKey)) {
                    setOpenKeys([...openKeys, submenuKey]);
                  }
                }}
                onOpenChange={(keys) =>
                  setOpenKeys(keys.length ? [keys[keys.length - 1]] : [])
                }
              >
                {[...(Auditor || []).slice(0, 1), analyticsEntry, ...(Auditor || []).slice(1)].map((item) => {
                  const permission = item?.submenu
                    ? hasVisibleSubmenuItems(item)
                    : hasSidebarItemAccess(item);
                  if (item.submenu) {
                    return (
                      permission && (
                        <SubMenu
                          icon={item.icon}
                          key={item.key}
                          title={item.title}
                          expandIcon={item.expandIcon}
                          onMouseEnter={() => handleSubmenuEnter(item.key)}
                          onMouseLeave={() => handleSubmenuLeave()}
                          className={
                            item.subMenuInfo?.some((sub) =>
                              matchesSubmenuPath(location.pathname, sub.path)
                            )
                              ? `${item.className} full-width-hover ant-menu-item-active`
                              : `${item.className} full-width-hover`
                          }
                          onTitleClick={() =>
                            setOpenKeys(
                              openKeys.includes(item.key) ? [] : [item.key]
                            )
                          }
                        >
                          {item.subMenuInfo.map((subItem) => {
                            const submenupermission = hasSidebarItemAccess(subItem);
                            return (
                              submenupermission && (
                                <Menu.Item
                                  title={
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  }
                                  key={subItem.key}
                                  className={
                                    location.pathname == subItem.path
                                      ? "ant-menu-item-active"
                                      : ""
                                  }
                                >
                                  <NavLink
                                    to={{
                                      pathname: subItem.path,
                                      state: { parentMenuKey: item.key }
                                    }}
                                  >
                                    {subItem.icon}
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  </NavLink>
                                </Menu.Item>
                              )
                            );
                          })}
                        </SubMenu>
                      )
                    );
                  } else {
                    const permission = hasSidebarItemAccess(item);
                    return (
                      permission && (
                        <Menu.Item
                          title={
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          }
                          key={item.key}
                          className={
                            matchesSubmenuPath(location.pathname, item.path)
                              ? "ant-menu-item-active"
                              : ""
                          }
                        >
                          <NavLink to={item.path}>
                            {item.icon}
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          </NavLink>
                        </Menu.Item>
                      )
                    );
                  }
                })}
              </Menu>
            </CustomScrollbars>
          </div>
        ) : getRoles(["HR"]) ? (
          <div className="gx-sidebar-content sidebar-menu">
            <CustomScrollbars className="gx-layout-sider-scrollbar">
              <Menu
                openKeys={openKeys}
                selectedKeys={[selectedKeys]}
                theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
                mode="vertical"
                triggerSubMenuAction="hover"
                onMouseEnter={(e) => {
                  const submenuKey = e.key;
                  if (submenuKey && !openKeys.includes(submenuKey)) {
                    setOpenKeys([...openKeys, submenuKey]);
                  }
                }}
                onOpenChange={(keys) =>
                  setOpenKeys(keys.length ? [keys[keys.length - 1]] : [])
                } // Ensure only one submenu is open
              >
                {[...(HR || []).slice(0, 1), analyticsEntry, ...(HR || []).slice(1)].map((item) => {
                  const permission = item?.submenu
                    ? hasVisibleSubmenuItems(item)
                    : hasSidebarItemAccess(item);
                  if (item.submenu) {
                    // Use location.state to determine which menu should be active
                    // This ensures that when clicking from a specific menu, only that menu shows as active
                    const parentMenuKey = location.state?.parentMenuKey;

                    let shouldBeActive;

                    if (parentMenuKey) {
                      // If we have a tracked parent menu key, only check this specific menu
                      // This ensures clicking from "Me" menu only shows "Me" as active, not "Audit" or "More"
                      if (item.key === parentMenuKey) {
                        // Check if pathname matches this submenu's items
                        shouldBeActive = item.subMenuInfo?.some((sub) =>
                          matchesSubmenuPath(location.pathname, sub.path)
                        );
                      } else {
                        // If this is not the tracked parent menu, don't show as active
                        shouldBeActive = false;
                      }
                    } else {
                      // Fallback: Check if pathname matches this submenu's items
                      shouldBeActive = item.subMenuInfo?.some((sub) =>
                        matchesSubmenuPath(location.pathname, sub.path)
                      );

                      // Fallback: Use priority logic when no parent menu key is tracked
                      // Special handling for /audits: prioritize "Audit" submenu over "Me" submenu
                      // if (matchesSubmenuPath(location.pathname, '/audits') && !matchesSubmenuPath(location.pathname, '/my-audit-history')) {
                      //   if ((item.key === "Me" || item.key === "sub15")) {
                      //     // If this is "Me" submenu and /audits exists in "Audit" submenu, don't show "Me" as active
                      //     const auditSubmenu = HR?.find(menuItem =>
                      //       menuItem.submenu &&
                      //       menuItem.key === "sub20" &&
                      //       menuItem.subMenuInfo?.some((sub) =>
                      //         sub.path && matchesSubmenuPath(location.pathname, sub.path)
                      //       )
                      //     );
                      //     // If "Audit" submenu has /audits, don't show "Me" as active (prioritize "Audit" - more specific)
                      //     if (auditSubmenu) {
                      //       shouldBeActive = false;
                      //     }
                      //   }
                      //   // If this is "Audit" submenu and path matches, it should be active (priority over "Me")
                      //   // This is already handled by the initial shouldBeActive check above
                      // }

                      // Special handling for /goals: prioritize "Me" submenu over "More" submenu
                      if (matchesSubmenuPath(location.pathname, '/my-goals')) {
                        if (item.key === "sub18") {
                          // If this is "More" submenu and /goals exists in "Me" submenu, don't show "More" as active
                          const meSubmenu = HR?.find(menuItem =>
                            menuItem.submenu &&
                            (menuItem.key === "Me" || menuItem.key === "sub15") &&
                            menuItem.subMenuInfo?.some((sub) =>
                              sub.path && matchesSubmenuPath(location.pathname, sub.path)
                            )
                          );
                          // If "Me" submenu has /goals, don't show "More" as active
                          if (meSubmenu) {
                            shouldBeActive = false;
                          }
                        }
                        // If this is "Me" submenu and path matches, it should be active (priority over "More")
                        // This is already handled by the initial shouldBeActive check above
                      }
                    }

                    return (
                      permission && (
                        <SubMenu
                          key={item.key}
                          icon={item.icon}
                          title={item.title}
                          onMouseEnter={() => handleSubmenuEnter(item.key)}
                          onMouseLeave={() => handleSubmenuLeave()}
                          className={
                            shouldBeActive
                              ? `${item.className} full-width-hover ant-menu-item-active`
                              : `${item.className} full-width-hover`
                          }
                          expandIcon={item.expandIcon}
                          onTitleClick={() =>
                            setOpenKeys(
                              openKeys.includes(item.key) ? [] : [item.key]
                            )
                          }
                        >
                          {item.subMenuInfo.map((subItem) => {
                            const submenupermission = hasSidebarItemAccess(subItem);
                            return (
                              submenupermission && (
                                <Menu.Item
                                  title={
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  }
                                  key={subItem.key}
                                  className={
                                    location.pathname == subItem.path
                                      ? "ant-menu-item-active"
                                      : ""
                                  }
                                >
                                  <NavLink
                                    to={{
                                      pathname: subItem.path,
                                      state: { parentMenuKey: item.key }
                                    }}
                                  >
                                    {subItem.icon}
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  </NavLink>
                                </Menu.Item>
                              )
                            );
                          })}
                        </SubMenu>
                      )
                    );
                  } else {
                    return (
                      permission && (
                        <Menu.Item
                          title={
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          }
                          key={item.key}
                          className={
                            matchesSubmenuPath(location.pathname, item.path)
                              ? "ant-menu-item-active"
                              : ""
                          }
                        >
                          <NavLink to={item.path}>
                            {item.icon}
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          </NavLink>
                        </Menu.Item>
                      )
                    );
                  }
                })}
              </Menu>
            </CustomScrollbars>
          </div>
        ) : getRoles(["Intern"]) ? (
          <div className="gx-sidebar-content sidebar-menu">
            <CustomScrollbars className="gx-layout-sider-scrollbar">
              <Menu
                openKeys={openKeys}
                selectedKeys={[selectedKeys]}
                theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
                mode="vertical"
                triggerSubMenuAction="hover"
                onMouseEnter={(e) => {
                  const submenuKey = e.key;
                  if (submenuKey && !openKeys.includes(submenuKey)) {
                    setOpenKeys([...openKeys, submenuKey]);
                  }
                }}
                onOpenChange={(keys) =>
                  setOpenKeys(keys.length ? [keys[keys.length - 1]] : [])
                }
              >
                {[...(Intern || []).slice(0, 1), analyticsEntry, ...(Intern || []).slice(1)].map((item) => {
                  const permission = item?.submenu
                    ? hasVisibleSubmenuItems(item)
                    : hasSidebarItemAccess(item);

                  if (item.submenu) {
                    return (
                      permission && (
                        <SubMenu
                          icon={item.icon}
                          key={item.key}
                          title={item.title}
                          expandIcon={item.expandIcon}
                          onMouseEnter={() => handleSubmenuEnter(item.key)}
                          onMouseLeave={() => handleSubmenuLeave()}
                          className={
                            item.subMenuInfo?.some((sub) =>
                              matchesSubmenuPath(location.pathname, sub.path)
                            )
                              ? `${item.className} full-width-hover ant-menu-item-active`
                              : `${item.className} full-width-hover`
                          }
                          onTitleClick={() =>
                            setOpenKeys(
                              openKeys.includes(item.key) ? [] : [item.key]
                            )
                          }
                        >
                          {item.subMenuInfo.map((subItem) => {
                            const submenupermission = hasSidebarItemAccess(subItem);
                            return (
                              submenupermission && (
                                <Menu.Item
                                  title={
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  }
                                  key={subItem.key}
                                  className={
                                    location.pathname == subItem.path
                                      ? "ant-menu-item-active"
                                      : ""
                                  }
                                >
                                  {subItem.path && <NavLink
                                    to={{
                                      pathname: subItem.path,
                                      state: { parentMenuKey: item.key }
                                    }}
                                  >
                                    {subItem.icon}
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  </NavLink>}
                                </Menu.Item>
                              )
                            );
                          })}
                        </SubMenu>
                      )
                    );
                  } else {
                    return (
                      permission && (
                        <Menu.Item
                          title={
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          }
                          key={item.key}
                          className={
                            matchesSubmenuPath(location.pathname, item.path)
                              ? "ant-menu-item-active"
                              : ""
                          }
                        >
                          <NavLink to={item.path}>
                            {item.icon}
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          </NavLink>
                        </Menu.Item>
                      )
                    );
                  }
                })}
              </Menu>
            </CustomScrollbars>
          </div>
        ) : getRoles(["Account Team"]) ? (
          <div className="gx-sidebar-content sidebar-menu">
            <CustomScrollbars className="gx-layout-sider-scrollbar">
              <Menu
                openKeys={openKeys}
                selectedKeys={[selectedKeys]}
                theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
                mode="vertical"
                triggerSubMenuAction="hover"
                onMouseEnter={(e) => {
                  const submenuKey = e.key;
                  if (submenuKey && !openKeys.includes(submenuKey)) {
                    setOpenKeys([...openKeys, submenuKey]);
                  }
                }}
                onOpenChange={(keys) =>
                  setOpenKeys(keys.length ? [keys[keys.length - 1]] : [])
                }
              >
                {[...(Account || []).slice(0, 1), analyticsEntry, ...(Account || []).slice(1)].map((item) => {
                  const permission = item?.submenu
                    ? hasVisibleSubmenuItems(item)
                    : hasSidebarItemAccess(item);

                  if (item.submenu) {
                    return (
                      permission && (
                        <SubMenu
                          icon={item.icon}
                          key={item.key}
                          title={item.title}
                          expandIcon={item.expandIcon}
                          onMouseEnter={() => handleSubmenuEnter(item.key)}
                          onMouseLeave={() => handleSubmenuLeave()}
                          className={
                            item.subMenuInfo?.some((sub) =>
                              matchesSubmenuPath(location.pathname, sub.path)
                            )
                              ? `${item.className} full-width-hover ant-menu-item-active`
                              : `${item.className} full-width-hover`
                          }
                          onTitleClick={() =>
                            setOpenKeys(
                              openKeys.includes(item.key) ? [] : [item.key]
                            )
                          }
                        >
                          {item.subMenuInfo.map((subItem) => {
                            const submenupermission = hasSidebarItemAccess(subItem);
                            return (
                              submenupermission && (
                                <Menu.Item
                                  title={
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  }
                                  key={subItem.key}
                                  className={
                                    location.pathname == subItem.path
                                      ? "ant-menu-item-active"
                                      : ""
                                  }
                                >
                                  {subItem.path && <NavLink
                                    to={{
                                      pathname: subItem.path,
                                      state: { parentMenuKey: item.key }
                                    }}
                                  >
                                    {subItem.icon}
                                    <span>
                                      <IntlMessages id={subItem.title} />
                                    </span>
                                  </NavLink>}
                                </Menu.Item>
                              )
                            );
                          })}
                        </SubMenu>
                      )
                    );
                  } else {
                    return (
                      permission && (
                        <Menu.Item
                          title={
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          }
                          key={item.key}
                          className={
                            matchesSubmenuPath(location.pathname, item.path)
                              ? "ant-menu-item-active"
                              : ""
                          }
                        >
                          <NavLink to={item.path}>
                            {item.icon}
                            <span>
                              <IntlMessages id={item.title} />
                            </span>
                          </NavLink>
                        </Menu.Item>
                      )
                    );
                  }
                })}
              </Menu>
            </CustomScrollbars>
          </div>
        ) : getRoles(["Recruiter"]) ? (
          <div className="gx-sidebar-content sidebar-menu">
            <CustomScrollbars className="gx-layout-sider-scrollbar">
              <Menu
                openKeys={openKeys}
                selectedKeys={[selectedKeys]}
                theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
                mode="vertical"
                triggerSubMenuAction="hover"
                onMouseEnter={(e) => {
                  const submenuKey = e.key;
                  if (submenuKey && !openKeys.includes(submenuKey)) {
                    setOpenKeys([...openKeys, submenuKey]);
                  }
                }}
                onOpenChange={(keys) =>
                  setOpenKeys(keys.length ? [keys[keys.length - 1]] : [])
                }
              >
                {[...(Recruiter || []).slice(0, 1), analyticsEntry, ...(Recruiter || []).slice(1)].map((item) => {
                  const permission =
                    item?.permission?.length > 0
                      ? getResource(item?.permission[0], ["read"])
                      : item?.submenu ? hasVisibleSubmenuItems(item) : hasSidebarItemAccess(item);
                  if (item.submenu) {
                    return (
                      permission && (
                        <SubMenu
                          icon={item.icon}
                          key={item.key}
                          title={item.title}
                          expandIcon={item.expandIcon}
                          onMouseEnter={() => handleSubmenuEnter(item.key)}
                          onMouseLeave={() => handleSubmenuLeave()}
                          className={
                            item.subMenuInfo?.some((sub) =>
                              matchesSubmenuPath(location.pathname, sub.path)
                            )
                              ? `${item.className} full-width-hover ant-menu-item-active`
                              : `${item.className} full-width-hover`
                          }
                          onTitleClick={() =>
                            setOpenKeys(
                              openKeys.includes(item?.key) ? [] : [item?.key]
                            )
                          }
                        >
                          {item.subMenuInfo.map((subItem) => {
                            const submenupermission = hasSidebarItemAccess(subItem);
                            return (
                              submenupermission && (
                                <Menu.Item
                                  title={
                                    <span>
                                      <IntlMessages id={subItem?.title} />
                                    </span>
                                  }
                                  key={subItem?.key}
                                  className={
                                    location?.pathname == subItem?.path
                                      ? "ant-menu-item-active"
                                      : ""
                                  }
                                >
                                  <NavLink
                                    to={{
                                      pathname: subItem?.path,
                                      state: { parentMenuKey: item.key }
                                    }}
                                  >
                                    {subItem?.icon}
                                    <span>
                                      <IntlMessages id={subItem?.title} />
                                    </span>
                                  </NavLink>
                                </Menu.Item>
                              )
                            );
                          })}
                        </SubMenu>
                      )
                    );
                  } else {
                    const permission = hasSidebarItemAccess(item);
                    return (
                      permission && (
                        <Menu.Item
                          title={
                            <span>
                              <IntlMessages id={item?.title} />
                            </span>
                          }
                          key={item.key}
                          className={
                            location.pathname?.includes(item?.path)
                              ? "ant-menu-item-active"
                              : ""
                          }
                        >
                          <NavLink to={item?.path}>
                            {item?.icon}
                            <span>
                              <IntlMessages id={item?.title} />
                            </span>
                          </NavLink>
                        </Menu.Item>
                      )
                    );
                  }
                })}
              </Menu>
            </CustomScrollbars>
          </div>
        ) : (
          <></>
        )}
      </Sider>
    </>
  );
};

export default SidebarContent;
